// src/ClientDashboard.js

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { callSendManualMessage, callAnswerCustomerQuestion, fetchChatMedia, callOwnerOrderAction, callSendOrderNow } from './firebase';
import { signOut } from './lib/supabaseAuth';
import { isDafOneEmbedded, getDafOneBrandName } from './lib/embedMode';
import { classifyChatMedia, chatMediaFilename, splitMessageLinks } from './chatPresentation';
import {
    subscribeMyBrand,
    subscribeFeedbacks,
    fetchMoreFeedbacks,
    subscribeBostaRecords,
    fetchMoreBostaRecords,
    subscribeFinalOrders,
    // G11/G12.2 — keyset paging + a database-side count. fetchMoreFinalOrders
    // (offset-based) is no longer imported here: it gets slower the deeper you
    // page and can skip/repeat rows under the concurrent inserts this table
    // gets from n8n. It stays exported for any other caller.
    fetchOrdersPage,
    countOrders,
    ORDERS_PAGE_SIZE,
    subscribeBrandStats,
    subscribeOpenQuestions,
    deleteQuestion,
    subscribeKnowledgeBase,
    addKnowledgeItem,
    updateKnowledgeItem,
    deleteKnowledgeItem,
    subscribePendingOrders,
    subscribeNonAiChats,
    addToNonAiChat,
    removeFromNonAiChat,
    resetChatUnreadCount,
    subscribeChatHistories,
    fetchMoreChatHistories,
    subscribeChatThread,
    saveClientContactInfo,
    setOperationalSetupDone,
    setAutoCancel,
    setDuplicatedOrderNote,
    // ⚠️ الأسماء دي بالظبط زي أسماء الـlocal state setters الموجودة (useState)
    // في الكومبوننت ده (setCancelTimeoutHours/setFirstMessageDelayHours/
    // setDeliveryDateEnabled/setDeliveryAverageDays/setFeedbackEnabled/
    // setShippingCheckEnabled) — لازم alias عشان محدش يطغى على التاني.
    setCancelTimeoutHours as saveCancelTimeoutHours,
    setFirstMessageDelayHours as saveFirstMessageDelayHours,
    setDeliveryDateEnabled as saveDeliveryDateEnabled,
    setDeliveryAverageDays as saveDeliveryAverageDays,
    setFeedbackEnabled as saveFeedbackEnabled,
    setAiMode,
    setShippingCheckEnabled as saveShippingCheckEnabled,
    setBostaAwaitingActions,
} from './lib/confirmationData';
import './redesign.css';
import * as XLSX from 'xlsx';
import { CSVLink } from 'react-csv';
import { useNavigate, useLocation } from 'react-router-dom';
import AgentPage from './AgentPage';
import OrderActionControls from './OrderActionControls';
import WebhookSetupPage from './WebhookSetupPage';
import { useI18n } from './i18n';

// 🔥 Wave 3b: كل صفوف الجداول الجديدة (confirmationData.js) بترجع الصف
// الكامل (doc_id + الأعمدة الـtyped + عمود data jsonb) مش الشكل المسطّح
// اللي Firestore كان بيرجّعه. الهيلبر ده بيعيد بناء نفس شكل
// `{id: doc.id, ...doc.data()}` القديم بالظبط من `data` jsonb (اللي هو نسخة
// كاملة من الدوكيومنت الأصلي — الأعمدة الـtyped مجرد نسخ مفهرسة منه لأغراض
// الفلترة/الترتيب في Postgres).
function mapRowToLegacyShape(row) {
  return { id: row.doc_id, ...(row.data || {}) };
}

// 🔥 Post-migration fix: قبل النقل لـ Postgres، حقول التواريخ كانت Firestore
// `Timestamp` objects (`.toDate()`). بعد الهجرة، نفس الحقول بقت naked jsonb
// values — ISO-8601 strings في الغالبية العظمى (مؤكّد حيًّا:
// `confirmation_date`/`created_at`/`date_added`/`timestamp`/`addedAt`)، وبعض
// صفوف `chat_histories_api.data.last_updated_timestamp` القديمة (قبل هذا
// الكود) لسه محفوظة كـ raw epoch-ms **number** (مؤكّد حيًّا: نوعين مختلطين في
// نفس العمود). الهيلبر ده بيتعامل مع كل الأشكال المحتملة (Firestore Timestamp
// النادر المتبقي/`{_seconds}`/`{seconds}`/ISO string/epoch-ms كنص أو رقم) بدل
// ما يفترض شكل واحد بس ويرجّع `null` لأي حاجة تانية.
function parseFlexibleTimestamp(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'object') {
    if (typeof value.toDate === 'function') return value.toDate(); // Firestore Timestamp (نادر بعد الهجرة)
    if (value._seconds != null) return new Date(value._seconds * 1000);
    if (value.seconds != null) return new Date(value.seconds * 1000);
  }
  const numeric = typeof value === 'string' && /^\d+$/.test(value) ? Number(value) : value;
  const date = new Date(numeric);
  return isNaN(date.getTime()) ? null : date;
}

// --- الثوابت ---
const infoBoxH2Style = { margin: '0 0 10px 0', color: '#6c5ce7', fontSize: '13px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px' };
const statCardH4Style = { margin: '0 0 8px 0', fontSize: '12px', fontWeight: 600, color: '#6b7280' };

// الستايلات بتاعة الشات (light theme)
const chatStyles = {
  chatModalContent: {
    width: '90%',
    maxWidth: '500px',
    height: '80vh',
    padding: '0',
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'right',
    background: '#ffffff',
    borderRadius: '16px',
    overflow: 'hidden',
  },
  chatHeader: {
    padding: '18px 20px',
    borderBottom: '1px solid #eceef3',
    background: '#6c5ce7',
    position: 'relative',
  },
  closeButton: {
    position: 'absolute',
    top: '12px',
    right: '16px',
    background: 'rgba(255,255,255,0.2)',
    border: 'none',
    color: '#ffffff',
    fontSize: '22px',
    cursor: 'pointer',
    padding: '0 6px',
    lineHeight: '1',
    transition: 'background 0.2s ease',
    borderRadius: '6px',
  },
  chatWindow: {
    flexGrow: 1,
    overflowY: 'auto',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    scrollBehavior: 'smooth',
    background: '#f4f3fb',
  },
  messageBubble: {
    padding: '10px 15px',
    borderRadius: '18px',
    marginBottom: '0px',
    maxWidth: '80%',
    fontSize: '14px',
    lineHeight: '1.5',
    wordBreak: 'break-word',
  },
  userMessage: {
    backgroundColor: '#d4f1e3',
    color: '#1b1f2a',
    alignSelf: 'flex-start',
    textAlign: 'left',
    borderRadius: '18px 18px 18px 5px',
  },
  assistantMessage: {
    backgroundColor: '#ffffff',
    color: '#1b1f2a',
    alignSelf: 'flex-end',
    textAlign: 'right',
    borderRadius: '18px 18px 5px 18px',
    border: '1px solid #eceef3',
  },
  loadingText: {
    textAlign: 'center',
    color: '#6b7280',
    fontSize: '15px',
    padding: '20px',
  },
  clickableRow: {
    cursor: 'pointer',
    transition: 'background-color 0.12s ease',
  }
};


// 🔥 ==========================================================
// 🔥 مودال عرض الشات (مع تصليح الكراش)
// 🔥 ==========================================================
function MessageMedia({ media }) {
  const [objectUrl, setObjectUrl] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    let createdUrl = '';

    async function load() {
      if (!media?.id || media?.stored === false) {
        setError('الملف غير متاح');
        return;
      }
      try {
        const blob = await fetchChatMedia(media.id);
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setObjectUrl(createdUrl);
      } catch (err) {
        if (!cancelled) setError(err?.message || 'تعذر تحميل الملف');
      }
    }

    load();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [media?.id, media?.stored]);

  if (!media) return null;
  if (error) {
    return <div style={{ fontSize: '12px', color: '#b42318', marginBottom: '6px' }}>⚠️ {error}</div>;
  }
  if (!objectUrl) {
    return <div style={{ fontSize: '12px', opacity: 0.7, marginBottom: '6px' }}>جاري تحميل الملف...</div>;
  }
  const kind = classifyChatMedia(media);
  const filename = chatMediaFilename(media);
  if (kind === 'audio') {
    return (
      <audio
        controls
        preload="metadata"
        src={objectUrl}
        style={{ width: '100%', minWidth: '230px', marginBottom: '6px' }}
      >
        المتصفح لا يدعم تشغيل التسجيل الصوتي.
      </audio>
    );
  }
  if (kind === 'video') {
    return (
      <video
        controls
        preload="metadata"
        src={objectUrl}
        style={{ display: 'block', width: '100%', maxHeight: '420px', borderRadius: '10px', marginBottom: '6px' }}
      >
        المتصفح لا يدعم تشغيل الفيديو.
      </video>
    );
  }
  if (kind === 'document') {
    return (
      <a
        href={objectUrl}
        target="_blank"
        rel="noopener noreferrer"
        download={filename}
        style={{ display: 'inline-block', marginBottom: '6px', color: '#2563eb', fontWeight: 700 }}
      >
        📄 فتح أو تحميل {filename}
      </a>
    );
  }
  return (
    <a href={objectUrl} target="_blank" rel="noopener noreferrer" title="فتح الصورة بالحجم الكامل">
      <img
        src={objectUrl}
        alt={media.caption || 'صورة أرسلها العميل عبر واتساب'}
        loading="lazy"
        style={{ display: 'block', width: '100%', maxHeight: '360px', objectFit: 'contain', borderRadius: '10px', marginBottom: '6px' }}
      />
    </a>
  );
}

function MessageText({ content }) {
  return String(content || '').split('\n').map((line, lineIndex) => (
    <React.Fragment key={lineIndex}>
      {splitMessageLinks(line).map((part, partIndex) => part.type === 'link' ? (
        <a
          key={partIndex}
          href={part.href}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'inherit', textDecoration: 'underline', overflowWrap: 'anywhere' }}
        >
          {part.value}
        </a>
      ) : (
        <React.Fragment key={partIndex}>{part.value}</React.Fragment>
      ))}
      <br />
    </React.Fragment>
  ));
}

function ChatModeControl({ phoneNumber, platform, isManualMode, onSwitchToManual, isSwitchingMode, isModeReady, modeUnavailable }) {
  const { t } = useI18n();
  return (
    <div style={{ marginTop: '12px' }}>
      {isManualMode ? (
        <span role="status" style={{ display: 'inline-block', padding: '7px 11px', borderRadius: '8px', background: 'rgba(255,255,255,0.2)', color: '#fff', fontSize: '13px', fontWeight: 700 }}>
          {t('chat.modal.manualActive')}
        </span>
      ) : (
        <button
          type="button"
          onClick={(e) => onSwitchToManual(phoneNumber, platform, e, 'manual')}
          disabled={!isModeReady || isSwitchingMode}
          style={{ padding: '7px 11px', borderRadius: '8px', border: 'none', background: '#fff', color: '#45359b', fontSize: '13px', fontWeight: 700, fontFamily: 'inherit', cursor: !isModeReady || isSwitchingMode ? 'not-allowed' : 'pointer', opacity: !isModeReady || isSwitchingMode ? 0.7 : 1 }}
        >
          {modeUnavailable ? t('chat.modal.modeUnavailable') : !isModeReady ? t('chat.modal.modeLoading') : isSwitchingMode ? t('chat.modal.switchingToManual') : t('chat.modal.switchToManual')}
        </button>
      )}
    </div>
  );
}

function ChatHistoryModal({ order, history, loading, onClose, isManualMode, onSwitchToManual, isSwitchingMode, isModeReady, modeUnavailable, messageInput, setMessageInput, onSendMessage, isSending }) {
  const { t, lang } = useI18n();
  const chatWindowRef = useRef(null);

  // Auto-scroll للأسفل لما الرسائل تتحمل
  useEffect(() => {
    if (chatWindowRef.current && history.length > 0) {
      chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
    }
  }, [history]);

  // دالة لتنسيق اليوم كـ separator
  const formatDateSeparator = (timestamp) => {
    const date = parseFlexibleTimestamp(timestamp);
    if (!date) return null;
    return date.toLocaleDateString(lang === 'ar' ? 'ar-EG' : 'en-GB', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  // دالة لتنسيق الوقت (محسّنة)
  const formatMessageTime = (timestamp) => {
    const date = parseFlexibleTimestamp(timestamp);
    if (!date) return '';

    return date.toLocaleTimeString(lang === 'ar' ? 'ar-EG' : 'en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!order) return null;

  return (
    <div style={styles.modalBackdrop} onClick={onClose}>
      <div style={{...styles.modalContent, ...chatStyles.chatModalContent}} onClick={(e) => e.stopPropagation()}>
        
        <div style={chatStyles.chatHeader}>
          <h3 style={{color: '#ffffff', margin: 0, fontSize: '16px', fontWeight: 700}}>{t('chat.modal.title')}</h3>
          <p style={{color: 'rgba(255,255,255,0.85)', margin: '4px 0 0 0', fontSize: '13px'}}>
            {order.customer_name || t('chat.modal.customerFallback')} ({order.customer_phone})
          </p>
          <ChatModeControl
            phoneNumber={order.customer_phone}
            platform={order.platform_type || order.platform || 'shopify'}
            isManualMode={isManualMode}
            onSwitchToManual={onSwitchToManual}
            isSwitchingMode={isSwitchingMode}
            isModeReady={isModeReady}
            modeUnavailable={modeUnavailable}
          />
          <button onClick={onClose} style={chatStyles.closeButton}>×</button>
        </div>
        
        <div ref={chatWindowRef} style={chatStyles.chatWindow}>
          {loading ? (
            <div style={chatStyles.loadingText}>{t('chat.modal.loading')}</div>
          ) : history.length === 0 ? (
            <div style={chatStyles.loadingText}>{t('chat.modal.empty')}</div>
          ) : (
            history.map((msg, index) => {
              const dateLabel = formatDateSeparator(msg.timestamp);
              const prevDateLabel = index > 0 ? formatDateSeparator(history[index - 1].timestamp) : null;
              const showSeparator = dateLabel && dateLabel !== prevDateLabel;
              return (
                <React.Fragment key={index}>
                  {showSeparator && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      margin: '10px 0',
                    }}>
                      <div style={{ flex: 1, height: '1px', background: '#eceef3' }} />
                      <span style={{
                        fontSize: '11px',
                        color: '#6b7280',
                        background: '#eceef3',
                        padding: '2px 10px',
                        borderRadius: '10px',
                        whiteSpace: 'nowrap',
                      }}>{dateLabel}</span>
                      <div style={{ flex: 1, height: '1px', background: '#eceef3' }} />
                    </div>
                  )}
                  <div
                    style={{
                      ...chatStyles.messageBubble,
                      ...(msg.role === 'user' ? chatStyles.userMessage : chatStyles.assistantMessage)
                    }}
                  >
                    <MessageMedia media={msg.media} />
                    <MessageText content={msg.content} />
                    {msg.timestamp && (
                      <div style={{
                        fontSize: '11px',
                        opacity: 0.7,
                        marginTop: '5px',
                        textAlign: msg.role === 'user' ? 'left' : 'right'
                      }}>
                        {formatMessageTime(msg.timestamp)}
                      </div>
                    )}
                  </div>
                </React.Fragment>
              );
            })
          )}
        </div>

        {/* Message Input Box - يظهر فقط في الوضع اليدوي */}
        {isManualMode && (
          <div style={{
            padding: '12px 16px',
            borderTop: '1px solid #eceef3',
            backgroundColor: '#ffffff',
            display: 'flex',
            gap: '10px',
            alignItems: 'center'
          }}>
            <textarea
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  onSendMessage();
                }
              }}
              placeholder="اكتب رسالتك هنا..."
              disabled={isSending}
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: '8px',
                border: '1px solid #eceef3',
                backgroundColor: '#f4f3fb',
                color: '#1b1f2a',
                fontSize: '14px',
                resize: 'none',
                minHeight: '42px',
                maxHeight: '120px',
                fontFamily: 'inherit',
                direction: 'rtl',
                textAlign: 'right',
                outline: 'none',
              }}
              rows="2"
            />
            <button
              onClick={onSendMessage}
              disabled={isSending || !messageInput.trim()}
              style={{
                padding: '10px 18px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: isSending || !messageInput.trim() ? '#c8c4d7' : '#6c5ce7',
                color: '#ffffff',
                fontSize: '14px',
                fontWeight: '600',
                cursor: isSending || !messageInput.trim() ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap',
                fontFamily: 'inherit',
              }}
            >
              {isSending ? t('chat.modal.sending') : t('chat.modal.send')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


// ==========================================================
// مودال عرض Feedback (مع إمكانية الرد)
// ==========================================================
function FeedbackModal({ feedback, history, loading, onClose, messageInput, setMessageInput, onSendMessage, isSending, isManualMode, onSwitchToManual, isSwitchingMode, isModeReady, modeUnavailable }) {
  const chatWindowRef = useRef(null);

  // Auto-scroll للأسفل
  useEffect(() => {
    if (chatWindowRef.current && history.length > 0) {
      chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
    }
  }, [history]);

  // دالة لتنسيق الوقت
  const formatMessageTime = (timestamp) => {
    const date = parseFlexibleTimestamp(timestamp);
    if (!date) return '';

    return date.toLocaleTimeString('ar-EG', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!feedback) return null;

  return (
    <div style={styles.modalBackdrop} onClick={onClose}>
      <div style={{...styles.modalContent, ...chatStyles.chatModalContent}} onClick={(e) => e.stopPropagation()}>
        
        <div style={{...chatStyles.chatHeader, background: '#6c5ce7'}}>
          <h3 style={{color: '#ffffff', margin: 0, fontSize: '16px', fontWeight: 700}}>التقييم - {feedback.status}</h3>
          <p style={{color: 'rgba(255,255,255,0.85)', margin: '4px 0 0 0', fontSize: '13px', fontWeight: 600}}>
            {feedback.customer_phone}
          </p>
          {feedback.comment && (
            <p style={{color: 'rgba(255,255,255,0.9)', margin: '8px 0 0 0', fontSize: '13px', background: 'rgba(255,255,255,0.15)', padding: '7px 10px', borderRadius: '7px'}}>
              {feedback.comment}
            </p>
          )}
          <ChatModeControl
            phoneNumber={feedback.customer_phone}
            platform={feedback.platform_type || feedback.platform || 'shopify'}
            isManualMode={isManualMode}
            onSwitchToManual={onSwitchToManual}
            isSwitchingMode={isSwitchingMode}
            isModeReady={isModeReady}
            modeUnavailable={modeUnavailable}
          />
          <button onClick={onClose} style={chatStyles.closeButton}>×</button>
        </div>
        
        <div ref={chatWindowRef} style={chatStyles.chatWindow}>
          {loading ? (
            <div style={chatStyles.loadingText}>جاري تحميل الرسائل...</div>
          ) : history.length === 0 ? (
            <div style={chatStyles.loadingText}>لا يوجد رسائل.</div>
          ) : (
            history.map((msg, index) => (
              <div 
                key={index} 
                style={{
                  ...chatStyles.messageBubble,
                  ...(msg.role === 'user' ? chatStyles.userMessage : chatStyles.assistantMessage)
                }}
              >
                <MessageMedia media={msg.media} />
                <MessageText content={msg.content} />
                
                {msg.timestamp && (
                  <div style={{
                    fontSize: '11px',
                    opacity: 0.7,
                    marginTop: '5px',
                    textAlign: msg.role === 'user' ? 'left' : 'right'
                  }}>
                    {formatMessageTime(msg.timestamp)}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Message Input Box - يظهر فقط في الوضع اليدوي */}
        {isManualMode && (
          <div style={{
            padding: '12px 16px',
            borderTop: '1px solid #eceef3',
            backgroundColor: '#ffffff',
            display: 'flex',
            gap: '10px',
            alignItems: 'center'
          }}>
            <textarea
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  onSendMessage();
                }
              }}
              placeholder="اكتب ردك هنا..."
              disabled={isSending}
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: '8px',
                border: '1px solid #eceef3',
                backgroundColor: '#f4f3fb',
                color: '#1b1f2a',
                fontSize: '14px',
                resize: 'none',
                minHeight: '42px',
                maxHeight: '120px',
                fontFamily: 'inherit',
                direction: 'rtl',
                textAlign: 'right',
                outline: 'none',
              }}
              rows="2"
            />
            <button
              onClick={onSendMessage}
              disabled={isSending || !messageInput.trim()}
              style={{
                padding: '10px 18px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: isSending || !messageInput.trim() ? '#c8c4d7' : '#6c5ce7',
                color: '#ffffff',
                fontSize: '14px',
                fontWeight: '600',
                cursor: isSending || !messageInput.trim() ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap',
                fontFamily: 'inherit',
              }}
            >
              {isSending ? '...' : 'إرسال'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


// ==========================================================
// الداش بورد الحقيقية
// ==========================================================
// تعريف nav items للـ sidebar
const NAV_ITEMS = [
  { key: 'overview',      labelAr: 'نظرة عامة',       labelEn: 'Overview',       icon: '📊' },
  { key: 'orders',        labelAr: 'الأوردرات',        labelEn: 'Orders',         icon: '🛒' },
  { key: 'pending',       labelAr: 'الأوردرات المعلقة', labelEn: 'Pending',       icon: '⏳' },
  { key: 'conversations', labelAr: 'المحادثات',         labelEn: 'Conversations', icon: '💬' },
  { key: 'questions',     labelAr: 'أسئلة العملاء',    labelEn: 'Questions',      icon: '❓' },
  { key: 'products', labelAr: 'المنتجات', labelEn: 'Products', icon: '🛍️' },
  { key: 'offers', labelAr: 'العروض', labelEn: 'Offers', icon: '🏷️' },
  { key: 'feedback',      labelAr: 'التقييمات',         labelEn: 'Feedback',      icon: '⭐' },
  { key: 'knowledge',     labelAr: 'قاعدة المعرفة',    labelEn: 'Knowledge',      icon: '📚' },
  { key: 'settings',      labelAr: 'الإعدادات',         labelEn: 'Settings',      icon: '⚙️' },
  { key: 'billing',       labelAr: 'الرصيد والباقات',  labelEn: 'Billing',        icon: '💳' },
];

function RealDashboard({ user, clientInfo, handleLogout }) {
  const { t, lang, toggleLang } = useI18n();
  const navigate = useNavigate();
  // 🔒 Wave 3b: مفيش location.search بيتسحب هنا تاني — كان بيحمل
  // ?uid=/?admin= (uid-in-URL) عشان يفضل يتمرر مع أي navigate('/billing').
  // الدخول للصفحات كلها بقى عبر الجلسة بس، مفيش باراميتر هوية في التنقل.

  const location = useLocation();
  const deepLinkChat = useMemo(() => {
    const raw = new URLSearchParams(location.search).get('chat') || '';
    const digits = raw.replace(/\D/g, '');
    return digits.length >= 6 && digits.length <= 20 ? digits : '';
  }, [location.search]);
  const deepLinkOpenedRef = useRef(false);
  useEffect(() => { deepLinkOpenedRef.current = false; }, [deepLinkChat]);

  // Navigation view state — Telegram links can land directly on conversations.
  const viewParams = new URLSearchParams(location.search);
  const requestedView = viewParams.get('view');
  const activeView = NAV_ITEMS.some(item => item.key === requestedView) ? requestedView : 'overview';
  const activeSection = viewParams.get('section') || '';
  const setActiveView = (view, section) => navigate(`/dashboard?view=${view}${section ? `&section=${section}` : ''}`);
  const SectionTabs = ({items}) => <nav className="rd-page-sections" aria-label={lang==='ar'?'أقسام الصفحة':'Page sections'}>{items.map(item => <button key={item.key} className={`rd-btn ${activeSection===item.key?'rd-btn-primary':'rd-btn-ghost'}`} aria-current={activeSection===item.key?'page':undefined} onClick={()=>setActiveView(activeView,item.key)}>{lang==='ar'?item.ar:item.en}</button>)}</nav>;

  // Mobile sidebar drawer state
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [clientOrders, setClientOrders] = useState([]);
  const [orderActionBusy, setOrderActionBusy] = useState({});
  const [orderActionNotice, setOrderActionNotice] = useState({});
  const [lastOrderActionNotice, setLastOrderActionNotice] = useState(null);
  const [orderNotifyCustomer, setOrderNotifyCustomer] = useState({});
  const orderActionLocks = useRef(new Set());

  const [ordersCache, setOrdersCache] = useState([]); // 🔥 كل الأوردرات للبحث عن الأرقام
  const [loadingOrders, setLoadingOrders] = useState(true); 
  const [filterType, setFilterType] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [activeStatusFilter, setActiveStatusFilter] = useState('ALL');
  const [searchPhoneNumber, setSearchPhoneNumber] = useState('');
  const [questions, setQuestions] = useState([]); 
  const [loadingQuestions, setLoadingQuestions] = useState(true); 
  const [selectedQuestion, setSelectedQuestion] = useState(null); 
  const [answerText, setAnswerText] = useState(''); 
  const [saveToKnowledgeBase, setSaveToKnowledgeBase] = useState(false); 
  const [isSubmitting, setIsSubmitting] = useState(false); 
  const [knowledgeBase, setKnowledgeBase] = useState([]);
  const [loadingKnowledgeBase, setLoadingKnowledgeBase] = useState(true);
  const [editingItem, setEditingItem] = useState(null); 
  const [editedAnswer, setEditedAnswer] = useState(''); 
  const [editedQuestion, setEditedQuestion] = useState('');
  const [editedProduct, setEditedProduct] = useState('');
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
  // State للإضافة
  const [showAddKnowledgeModal, setShowAddKnowledgeModal] = useState(false);
  const [newQuestion, setNewQuestion] = useState('');
  const [newAnswer, setNewAnswer] = useState('');
  const [newProduct, setNewProduct] = useState('');
  const [isSubmittingNew, setIsSubmittingNew] = useState(false); 
  const [selectedOrderForChat, setSelectedOrderForChat] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [loadingChat, setLoadingChat] = useState(false);
  
  // 🔥 --- State للإلغاء التلقائي ---
  const [autoCancelEnabled, setAutoCancelEnabled] = useState(clientInfo?.auto_cancel || false);
  const [isUpdatingAutoCancel, setIsUpdatingAutoCancel] = useState(false);

  // 🔥 --- State لـ Cancel Timeout Hours ---
  const [cancelTimeoutHours, setCancelTimeoutHours] = useState(clientInfo?.cancel_timeout_hours || 16);
  const [isUpdatingCancelTimeout, setIsUpdatingCancelTimeout] = useState(false);

  // ⏰ --- State لـ First Message Delay (توقيت إرسال أول رسالة) ---
  const [firstMessageDelayHours, setFirstMessageDelayHours] = useState(clientInfo?.first_message_delay_hours ?? 1);
  const [isUpdatingFirstMsgDelay, setIsUpdatingFirstMsgDelay] = useState(false);
  const [showFirstMsgDelayPicker, setShowFirstMsgDelayPicker] = useState(false);

  // --- State لـ موعد التسليم المتوقع (Expected Delivery Date) ---
  const [deliveryDateEnabled, setDeliveryDateEnabled] = useState(clientInfo?.delivery_date_enabled === true);
  const [isUpdatingDeliveryDate, setIsUpdatingDeliveryDate] = useState(false);
  const [deliveryAverageDays, setDeliveryAverageDays] = useState(clientInfo?.delivery_average_days ?? 3);
  const [isUpdatingDeliveryDays, setIsUpdatingDeliveryDays] = useState(false);

  // 🔥 --- State للـ Feedback ---
  const [feedbackEnabled, setFeedbackEnabled] = useState(clientInfo?.feedback || false);
  const [isUpdatingFeedback, setIsUpdatingFeedback] = useState(false);

  // 🔥 --- State لـ Bosta Actions ---
  const [bostaActionsEnabled, setBostaActionsEnabled] = useState(clientInfo?.bosta_awaiting_actions || false);
  const [isUpdatingBostaActions, setIsUpdatingBostaActions] = useState(false);

  // 🔥 --- State لـ AI Mode ---
  const [aiModeEnabled, setAiModeEnabled] = useState(clientInfo?.ai_mode === true); // default false
  const [isUpdatingAiMode, setIsUpdatingAiMode] = useState(false);
  const [showAiModeTooltip, setShowAiModeTooltip] = useState(false);

  // 🔥 --- State لـ Shipping Check ---
  const [shippingCheckEnabled, setShippingCheckEnabled] = useState(clientInfo?.shipping_check === true); // default false
  const [isUpdatingShippingCheck, setIsUpdatingShippingCheck] = useState(false);

  // 🔥 --- State لـ Duplicated Order Note (default true لو مش موجود) ---
  const [duplicatedOrderNoteEnabled, setDuplicatedOrderNoteEnabled] = useState(
    clientInfo?.dublicated_order_note !== false
  );
  const [isUpdatingDuplicatedOrderNote, setIsUpdatingDuplicatedOrderNote] = useState(false);
  const [showDupNoteTooltip, setShowDupNoteTooltip] = useState(false);

  const [searchChatPhone, setSearchChatPhone] = useState('');
  // 🔥 جديد: البحث برقم الأوردر في المحادثات
  const [searchLeadsOrderNumber, setSearchLeadsOrderNumber] = useState('');
  const [searchPendingPhone, setSearchPendingPhone] = useState('');
  const [searchPendingOrderNumber, setSearchPendingOrderNumber] = useState('');
  const [searchOrderNumber, setSearchOrderNumber] = useState('');
  // G11.2.b — البحث بالاسم. جو طلبه صراحة ("بار زي بوسطة: اسم + تليفون + رقم
  // أوردر") والداشبورد مكانش فيها بحث بالاسم خالص. الاسم جوّه `data` من غير
  // عمود، فـmigration 001 بيولّد عمود lower-cased عليه فهرس.
  const [searchCustomerName, setSearchCustomerName] = useState('');
  const [searchFeedbackPhone, setSearchFeedbackPhone] = useState('');
  const [searchNonAiPhone, setSearchNonAiPhone] = useState('');

  // 🔥 --- State لإجمالي الأوردرات (كل السجلات) ---
  const [totalOrdersStats, setTotalOrdersStats] = useState({
    confirmed: 0,
    aiCreated: 0,
    cancelled: 0,
    unclear: 0,
    total: 0
  });

  // 🔥 إضافات الحالة للداتا والتحميل
  const [pendingOrders, setPendingOrders] = useState([]);
  const [allChats, setAllChats] = useState([]);
  const [loadingChats, setLoadingChats] = useState(true);
  const [nonAiChats, setNonAiChats] = useState([]);
  const [nonAiChatsLoaded, setNonAiChatsLoaded] = useState(false);
  const [nonAiChatsError, setNonAiChatsError] = useState(false);
  const [togglingChat, setTogglingChat] = useState({});

  // ⚠️ `lastVisibleX` كانت Firestore document snapshot (cursor لـ startAfter).
  // بقت دلوقتي **رقم offset** (عدد العناصر المحمّلة حاليًا) — Postgres/
  // supabase-js's `.range()` بيستخدم offset مش cursor. شوف confirmationData.js's
  // "fetchMoreX" لتفاصيل المقايضة (مش equivalent 100% تحت inserts متزامنة،
  // موثّق هناك). الاسم اتساب زي ما هو تقليل diff، بس المعنى اتغيّر.
  const [lastVisibleChat, setLastVisibleChat] = useState(0);
  const [hasMoreChats, setHasMoreChats] = useState(true);
  const [loadingMoreChats, setLoadingMoreChats] = useState(false);
  const chatsSentinelRef = useRef(null);
  const chatsScrollRef = useRef(null);

  const [lastVisibleOrder, setLastVisibleOrder] = useState(0);
  const [hasMoreOrders, setHasMoreOrders] = useState(true);
  const [loadingMoreOrders, setLoadingMoreOrders] = useState(false);

  const [feedbacks, setFeedbacks] = useState([]);
  const [loadingFeedbacks, setLoadingFeedbacks] = useState(true);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [feedbackChatHistory, setFeedbackChatHistory] = useState([]);
  const [loadingFeedbackChat, setLoadingFeedbackChat] = useState(false);
  const [lastVisibleFeedback, setLastVisibleFeedback] = useState(0);
  const [hasMoreFeedbacks, setHasMoreFeedbacks] = useState(true);
  const [loadingMoreFeedbacks, setLoadingMoreFeedbacks] = useState(false);
  const feedbacksSentinelRef = useRef(null);
  const feedbacksScrollRef = useRef(null);

  // 🔥 --- State للـ Bosta Table ---
  const [bostaRecords, setBostaRecords] = useState([]);
  const [loadingBosta, setLoadingBosta] = useState(true);
  const [selectedBosta, setSelectedBosta] = useState(null);
  const [bostaChatHistory, setBostaChatHistory] = useState([]);
  const [loadingBostaChat, setLoadingBostaChat] = useState(false);
  const [lastVisibleBosta, setLastVisibleBosta] = useState(0);
  const [hasMoreBosta, setHasMoreBosta] = useState(true);
  const [loadingMoreBosta, setLoadingMoreBosta] = useState(false);
  const [searchBostaPhone, setSearchBostaPhone] = useState('');

  const [messageInput, setMessageInput] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);

  // 🔥 حالة مودال بيانات العميل
  const [showClientInfoModal, setShowClientInfoModal] = useState(false);
  const [editingTelegram, setEditingTelegram] = useState('');
  const [telegramLinkUrl, setTelegramLinkUrl] = useState(null);
  const [isLoadingTelegramLink, setIsLoadingTelegramLink] = useState(false);
  const [editingFacebook, setEditingFacebook] = useState('');
  const [editingInstagram, setEditingInstagram] = useState('');
  const [editingMessageLanguage, setEditingMessageLanguage] = useState('ar');
  const [isSavingInfo, setIsSavingInfo] = useState(false);

  // 🏗️ "Finish setting up" modal — for merchants auto-provisioned via the
  // Daf One bridge (confirmation-provision.js) who skipped this dashboard's
  // own onboarding form entirely and therefore never set a Telegram admin
  // id (or Facebook/Instagram/message language). Deliberately a SEPARATE
  // modal + its own small state, not reusing `showClientInfoModal` above —
  // that one is the general-purpose "edit my info" settings modal, while
  // this one is a focused one-time "finish your setup" nudge shown right
  // after a Daf One auto-provisioned login (Telegram required, FB/IG/
  // message-language all optional — 2026-07-07: extended to collect all
  // four fields in one go, per owner request, so the merchant does this
  // once and is fully done instead of having to separately visit Settings
  // afterwards just to pick a message language).
  const [showOperationalSetupModal, setShowOperationalSetupModal] = useState(false);
  const [opSetupTelegram, setOpSetupTelegram] = useState('');
  const [opSetupFacebook, setOpSetupFacebook] = useState('');
  const [opSetupInstagram, setOpSetupInstagram] = useState('');
  const [opSetupMessageLanguage, setOpSetupMessageLanguage] = useState('ar');
  const [isSavingOpSetup, setIsSavingOpSetup] = useState(false);
  const [opSetupError, setOpSetupError] = useState('');

  // Triple guard (per task brief) — only ever true for a Daf-One-provisioned
  // brand that has NOT completed operational setup AND still has no
  // Telegram id on file. `operational_setup_done` is already a DERIVED value
  // server-side (see get_my_brand_settings() in
  // daf_confirmation_operational_setup.sql) that flips to `true`
  // automatically the moment a Telegram id exists — so the third condition
  // here is technically redundant with the second, kept anyway as explicit
  // defense-in-depth so independent/pre-existing merchants (all of whom
  // already have a Telegram id) can never see this banner under any
  // circumstance, even a future regression in the derivation logic.
  const needsOperationalSetup =
    (clientInfo?.provisioned_via === 'daf_one' || clientInfo?.billing_managed_by === 'daf_one') &&
    !clientInfo?.operational_setup_done &&
    !clientInfo?.brand_owner_telegram_ID;

  const handleSaveOperationalSetup = async () => {
    setOpSetupError('');
    if (!String(opSetupTelegram || '').trim()) {
      setOpSetupError(t('operationalSetup.modal.telegramError'));
      return;
    }
    setIsSavingOpSetup(true);
    try {
      const { error } = await saveClientContactInfo({
        telegram: opSetupTelegram,
        facebook: opSetupFacebook,
        instagram: opSetupInstagram,
        messageLanguage: opSetupMessageLanguage,
      });
      if (error) throw error;

      const { error: flagError } = await setOperationalSetupDone();
      if (flagError) throw flagError;

      setShowOperationalSetupModal(false);
    } catch (err) {
      console.error('خطأ في حفظ إعداد الحساب:', err);
      setOpSetupError(t('operationalSetup.modal.saveError'));
    } finally {
      setIsSavingOpSetup(false);
    }
  };

  // 🔥 مرجع للاشتراكات الحية
  const chatListenerUnsubscribe = useRef(null);

  // 🔥 دالة حفظ بيانات العميل — عبر updateMyBrandSettings RPC (whitelist)
  // مش update مباشر على clients_api.
  const handleSaveInfo = async () => {
    setIsSavingInfo(true);
    try {
      // نفس سلوك الكود القديم بالظبط: بنكتب الأربع حقول دايمًا (حتى لو
      // فاضية، عشان التاجر يقدر يمسح قيمة قديمة) — saveClientContactInfo
      // بتعمل نفس الحاجة (RPC بتاعها jsonb merge، مش replace، فمفيش خطر
      // مسح حقول تانية).
      const { error } = await saveClientContactInfo({
        telegram: editingTelegram,
        facebook: editingFacebook,
        instagram: editingInstagram,
        messageLanguage: editingMessageLanguage,
      });
      if (error) throw error;

      alert(t('alerts.saveInfo.success'));
      setShowClientInfoModal(false); // لو الـ modal مفتوح ناغلقه، لو مش مفتوح مش بيعمل حاجة
    } catch (error) {
      console.error('خطأ في تحديث البيانات:', error);
      alert(t('alerts.saveInfo.error'));
    } finally {
      setIsSavingInfo(false);
    }
  };

  // Effect لمزامنة إعداد الإلغاء التلقائي
  useEffect(() => {
    if (clientInfo?.auto_cancel !== undefined) {
      setAutoCancelEnabled(clientInfo.auto_cancel);
    }
  }, [clientInfo?.auto_cancel]);

  // Effect لمزامنة cancel_timeout_hours
  useEffect(() => {
    if (clientInfo?.cancel_timeout_hours !== undefined) {
      setCancelTimeoutHours(clientInfo.cancel_timeout_hours);
    }
  }, [clientInfo?.cancel_timeout_hours]);

  // ⏰ Effect لمزامنة first_message_delay_hours
  useEffect(() => {
    if (clientInfo?.first_message_delay_hours !== undefined) {
      setFirstMessageDelayHours(clientInfo.first_message_delay_hours);
    }
  }, [clientInfo?.first_message_delay_hours]);

  // Effect لمزامنة delivery_date_enabled
  useEffect(() => {
    if (clientInfo?.delivery_date_enabled !== undefined) {
      setDeliveryDateEnabled(clientInfo.delivery_date_enabled);
    }
  }, [clientInfo?.delivery_date_enabled]);

  // Effect لمزامنة delivery_average_days
  useEffect(() => {
    if (clientInfo?.delivery_average_days !== undefined) {
      setDeliveryAverageDays(clientInfo.delivery_average_days);
    }
  }, [clientInfo?.delivery_average_days]);

  // Effect لمزامنة إعداد الـ Feedback
  useEffect(() => {
    if (clientInfo?.feedback !== undefined) {
      setFeedbackEnabled(clientInfo.feedback);
    }
  }, [clientInfo?.feedback]);

  // Effect لمزامنة إعداد Bosta Actions
  useEffect(() => {
    if (clientInfo?.bosta_awaiting_actions !== undefined) {
      setBostaActionsEnabled(clientInfo.bosta_awaiting_actions);
    }
  }, [clientInfo?.bosta_awaiting_actions]);

  // Effect لمزامنة AI Mode
  useEffect(() => {
    if (clientInfo?.ai_mode !== undefined) {
      setAiModeEnabled(clientInfo.ai_mode);
    }
  }, [clientInfo?.ai_mode]);

  // Effect لمزامنة Shipping Check
  useEffect(() => {
    if (clientInfo?.shipping_check !== undefined) {
      setShippingCheckEnabled(clientInfo.shipping_check);
    }
  }, [clientInfo?.shipping_check]);

  // Effect لمزامنة Duplicated Order Note (default true لو الحقل مش موجود)
  useEffect(() => {
    setDuplicatedOrderNoteEnabled(clientInfo?.dublicated_order_note !== false);
  }, [clientInfo?.dublicated_order_note]);

  // Effect لمزامنة حقول تعديل بيانات العميل (يتشغّل لما clientInfo يتغيّر أو لما نفتح الـ Settings)
  useEffect(() => {
    if (clientInfo) {
      setEditingTelegram(String(clientInfo.brand_owner_telegram_ID || ''));
      setEditingFacebook(String(clientInfo.facebook_url || ''));
      setEditingInstagram(String(clientInfo.instegram_url || ''));
      setEditingMessageLanguage(clientInfo.message_language || 'ar');
    }
  }, [clientInfo?.brand_owner_telegram_ID, clientInfo?.facebook_url, clientInfo?.instegram_url, clientInfo?.message_language]);

  // 🔥 --- Load More Functions للـ Pagination ---
  
  // Load More للمحادثات
  // ⚠️ offset-based (.range()) بدل Firestore document cursor (startAfter) —
  // الحارس بقى `hasMoreChats` (مش truthiness بتاعة الـoffset، لأن offset=0
  // قيمة أولية صحيحة مش "مفيش صفحة تانية"). شوف design note في
  // confirmationData.js's fetchMoreChatHistories.
  const handleLoadMoreChats = async () => {
    if (!hasMoreChats || !clientInfo?.brand_name || loadingMoreChats) return;

    // 🔥 حفظ موضع الـ scroll الحالي
    const currentScrollY = window.scrollY;

    setLoadingMoreChats(true);
    try {
      const { rows, error } = await fetchMoreChatHistories(clientInfo.brand_name, lastVisibleChat, ORDERS_PAGE_SIZE);
      if (error) throw error;

      const newChats = (rows || []).map((row) => {
        const legacy = mapRowToLegacyShape(row);
        return {
          ...legacy,
          customer_phone: legacy.customer_phone,
          customer_name: legacy.messages && legacy.messages.length > 0 ? t('chat.list.conversation') : t('chat.list.emptyConversation')
        };
      });

      // إضافة المحادثات الجديدة للقائمة الموجودة
      setAllChats(prev => [...prev, ...newChats]);

      // تحديث الـ offset
      setLastVisibleChat(prev => prev + newChats.length);
      setHasMoreChats(newChats.length === ORDERS_PAGE_SIZE);

      // 🔥 نحمل الأوردرات كمان (في نفس الوقت)
      if (hasMoreOrders) {
        handleLoadMoreOrders();
      }

      // 🔥 إرجاع الـ scroll لنفس الموضع بعد التحميل
      setTimeout(() => {
        window.scrollTo(0, currentScrollY);
      }, 0);
    } catch (error) {
      console.error('Error loading more chats:', error);
    } finally {
      setLoadingMoreChats(false);
    }
  };

  // ══ G11.3.b + G12.2 — الصفحة التالية بـkeyset، مش offset ══
  //
  // الـoffset بيبطّأ كل ما تنزل (بوستجرس بيمشي على كل صف بيتخطّاه ويرميه)،
  // وأخطر من كده: الجدول ده بيتكتب فيه باستمرار من n8n، والـoffset تحت الكتابة
  // المتزامنة ممكن يكرّر صف أو يقفّشه على حدود الصفحة. الـkeyset بيقفز على
  // المؤشر مباشرة وثابت السرعة.
  //
  // ⚠️ confirmation_date بيبقى NULL في صفوف (٤ صفوف حيّة دلوقتي)، فالمؤشر شايل
  // doc_id كـtiebreak وذيل الـNULL بيتقسّم بالـdoc_id لوحده — من غير كده حدود
  // الصفحة بتقفّش صفوف بالصمت.
  const handleLoadMoreOrders = async () => {
    if (!hasMoreOrders || !clientInfo?.brand_name || loadingMoreOrders) return;
    const cursor = ordersCursorRef.current;
    if (!cursor) return;

    setLoadingMoreOrders(true);
    try {
      const { rows, error, nextCursor } = await fetchOrdersPage(clientInfo.brand_name, {
        filters: debouncedFilters,
        cursor,
      });
      if (error) throw error;

      const newOrders = (rows || []).map(mapRowToLegacyShape);
      setClientOrders(prev => [...prev, ...newOrders]);
      setOrdersCache(prev => [...prev, ...newOrders]);

      ordersCursorRef.current = nextCursor;
      setLastVisibleOrder(prev => prev + newOrders.length);
      setHasMoreOrders(!!nextCursor);
      // مفيش window.scrollTo هنا: الصفوف بتتضاف تحت اللي ظاهر، فمكان الـscroll
      // مابيتغيّرش أصلاً. الـscrollTo القديم كان لازم مع زرار بيعمل re-render
      // من فوق؛ مع الإضافة بقى بيسبّب رجّة.
    } catch (error) {
      console.error('Error loading more orders:', error);
    } finally {
      setLoadingMoreOrders(false);
    }
  };

  // Load More للـ Feedbacks
  const handleLoadMoreFeedbacks = async () => {
    if (!hasMoreFeedbacks || !clientInfo?.brand_name || loadingMoreFeedbacks) return;

    // 🔥 حفظ موضع الـ scroll الحالي
    const currentScrollY = window.scrollY;

    setLoadingMoreFeedbacks(true);
    try {
      const { rows, error } = await fetchMoreFeedbacks(clientInfo.brand_name, lastVisibleFeedback, ORDERS_PAGE_SIZE);
      if (error) throw error;

      const newFeedbacks = (rows || []).map(mapRowToLegacyShape);

      setFeedbacks(prev => [...prev, ...newFeedbacks]);

      setLastVisibleFeedback(prev => prev + newFeedbacks.length);
      setHasMoreFeedbacks(newFeedbacks.length === ORDERS_PAGE_SIZE);

      // 🔥 إرجاع الـ scroll لنفس الموضع بعد التحميل
      setTimeout(() => {
        window.scrollTo(0, currentScrollY);
      }, 0);
    } catch (error) {
      console.error('Error loading more feedbacks:', error);
    } finally {
      setLoadingMoreFeedbacks(false);
    }
  };

  // Load More للـ Bosta
  const handleLoadMoreBosta = async () => {
    if (!hasMoreBosta || !clientInfo?.brand_name || loadingMoreBosta) return;

    const currentScrollY = window.scrollY;

    setLoadingMoreBosta(true);
    try {
      const { rows, error } = await fetchMoreBostaRecords(clientInfo.brand_name, lastVisibleBosta, ORDERS_PAGE_SIZE);
      if (error) throw error;

      const newRecords = (rows || []).map(mapRowToLegacyShape);

      setBostaRecords(prev => [...prev, ...newRecords]);

      setLastVisibleBosta(prev => prev + newRecords.length);
      setHasMoreBosta(newRecords.length === ORDERS_PAGE_SIZE);

      setTimeout(() => {
        window.scrollTo(0, currentScrollY);
      }, 0);
    } catch (error) {
      console.error('Error loading more bosta records:', error);
    } finally {
      setLoadingMoreBosta(false);
    }
  };
  // 🔥 --- End Load More Functions ---

  // 🔥 --- Search Functions (البحث المباشر في Firebase) ---
  // تم تعطيل البحث التلقائي لأن Firebase يحتاج exact match فقط
  // البحث الآن محلي (client-side) - استخدم "تحميل المزيد" لجلب سجلات قديمة

  // Effect الـ Feedbacks
  useEffect(() => {
    if (!clientInfo?.brand_name) {
      setFeedbacks([]);
      setLoadingFeedbacks(false);
      return undefined;
    }

    setLoadingFeedbacks(true);

    // 🔥 إذا كان هناك بحث، نجلب كل البيانات المطابقة
    const isSearching = !!searchFeedbackPhone.trim();

    const unsubscribe = subscribeFeedbacks(clientInfo.brand_name, { search: isSearching, phoneSearch: searchFeedbackPhone.trim() }, ({ rows, error }) => {
      if (error) {
        console.error('Error fetching feedbacks:', error);
        setLoadingFeedbacks(false);
        return;
      }
      const fetchedFeedbacks = (rows || []).map(mapRowToLegacyShape);

      if (isSearching) {
        setHasMoreFeedbacks(false); // لا يوجد المزيد لأننا جلبنا كل شيء
      } else {
        setLastVisibleFeedback(fetchedFeedbacks.length);
        setHasMoreFeedbacks(fetchedFeedbacks.length === ORDERS_PAGE_SIZE);
      }

      setFeedbacks(fetchedFeedbacks);
      setLoadingFeedbacks(false);
    });

    return () => unsubscribe();
  }, [clientInfo, searchFeedbackPhone]);

  // Effect الـ Bosta
  useEffect(() => {
    if (!clientInfo?.brand_name) {
      setBostaRecords([]);
      setLoadingBosta(false);
      return undefined;
    }

    setLoadingBosta(true);

    const isSearching = !!searchBostaPhone.trim();

    const unsubscribe = subscribeBostaRecords(clientInfo.brand_name, { search: isSearching, phoneSearch: searchBostaPhone.trim() }, ({ rows, error }) => {
      if (error) {
        console.error('Error fetching bosta records:', error);
        setLoadingBosta(false);
        return;
      }
      const fetched = (rows || []).map(mapRowToLegacyShape);

      if (isSearching) {
        setHasMoreBosta(false);
      } else {
        setLastVisibleBosta(fetched.length);
        setHasMoreBosta(fetched.length === ORDERS_PAGE_SIZE);
      }

      setBostaRecords(fetched);
      setLoadingBosta(false);
    });

    return () => unsubscribe();
  }, [clientInfo, searchBostaPhone]);

  // ══ G11 — البحث بقى في الداتابيز، مش في المتصفح ══
  //
  // كان: أول ما التاجر يكتب حرف، الـ limit بيتشال ويتسحب كل تاريخ البراند
  // (15,074 صف على 33 براند) ويتبعت للمتصفح ويتفلتر هناك بـ String.includes.
  // وده كان بيتكرر مع كل حدث realtime طول ما خانة البحث مفتوحة.
  //
  // بقى: كل فلتر شرط WHERE، والـ limit مابيتشالش أبدًا، والتليفون بيتطبّع
  // للتسعة أرقام الأخيرة على الجانبين (اللي مخزّن "201060871051" واللي
  // التاجر بيكتبه "01060871051") — وده اللي كان مخلّي البحث بالتليفون
  // مايشتغلش أصلاً.
  //
  // debounce 300ms عشان كل ضغطة زرار ماتولّعش استعلام.
  const [debouncedFilters, setDebouncedFilters] = useState({ phone: '', orderNumber: '', name: '' });
  /** G11.3.b — where the next keyset page starts; null = we're at the end. */
  const ordersCursorRef = useRef(null);
  /** G12.2.b — the element whose visibility triggers the next page. */
  const ordersSentinelRef = useRef(null);
  /** G12.2.c — the table's own scroll box (max-height + overflow-y), not the page. */
  const ordersScrollRef = useRef(null);
  useEffect(() => {
    const id = setTimeout(
      () =>
        setDebouncedFilters({
          phone: searchPhoneNumber.trim(),
          orderNumber: searchOrderNumber.trim(),
          name: searchCustomerName.trim(),
        }),
      300
    );
    return () => clearTimeout(id);
  }, [searchPhoneNumber, searchOrderNumber, searchCustomerName]);

  // Effect الأوردرات
  useEffect(() => {
    if (!clientInfo?.brand_name) return undefined;
    setLoadingOrders(true);

    // Reset the visible page immediately when the brand/search changes. This
    // prevents the old result and a zero/empty counter from flashing while
    // the new server-side page is being fetched.
    setClientOrders([]);
    setOrdersCache([]);
    setHasMoreOrders(false);
    setOrdersTotal(null);
    ordersCursorRef.current = null;
    let initialLoadTimedOut = false;
    const initialLoadTimeout = setTimeout(() => {
      initialLoadTimedOut = true;
      setLoadingOrders(false);
    }, 15000);

    const filters = debouncedFilters;
    const unsubscribe = subscribeFinalOrders(clientInfo.brand_name, { filters }, ({ rows, error }) => {
      clearTimeout(initialLoadTimeout);
      if (initialLoadTimedOut) return;
      if (error) {
        console.error("Error fetching orders:", error);
        setLoadingOrders(false);
        return;
      }
      const orders = (rows || []).map(mapRowToLegacyShape);
      setClientOrders(orders);
      setOrdersCache(orders);
      // ⚠️ الصفحة بقى ليها حدّ دايمًا — حتى وإحنا بنبحث. والمؤشر بيتظبط من آخر
      // صف ظاهر عشان الصفحة اللي بعدها تكمّل من عنده بالظبط.
      const lastRow = rows && rows.length ? rows[rows.length - 1] : null;
      ordersCursorRef.current =
        lastRow && orders.length >= ORDERS_PAGE_SIZE
          ? { date: lastRow.confirmation_date || null, id: lastRow.doc_id }
          : null;
      setLastVisibleOrder(orders.length);
      setHasMoreOrders(orders.length >= ORDERS_PAGE_SIZE);
      setLoadingOrders(false);
    });

    return () => {
      clearTimeout(initialLoadTimeout);
      unsubscribe();
    };
  }, [clientInfo, debouncedFilters]);

  // G11.3.d — العدّاد من الداتابيز (count exact, head:true → بيعدّ من غير ما
  // يرجّع ولا صف). قبل كده العدد كان "اللي اتحمّل"، وده بقى غلط أول ما بقى فيه
  // ترقيم أصلاً.
  const [ordersTotal, setOrdersTotal] = useState(null);
  useEffect(() => {
    if (!clientInfo?.brand_name) return undefined;
    let cancelled = false;
    countOrders(clientInfo.brand_name, { filters: debouncedFilters })
      .then(({ count }) => { if (!cancelled) setOrdersTotal(count); })
      .catch(() => { if (!cancelled) setOrdersTotal(null); });
    return () => { cancelled = true; };
  }, [clientInfo, debouncedFilters]);

  // G12.2.c — يتحمّل من جوّه الجدول نفسه (rd-table-scroll) مش من صفحة كبيرة.
  // root: الصندوق اللي فيه max-height+overflow-y، مش الـviewport. من غير سقف
  // صفحات: بيفضل كده لغاية آخر الليستة.
  //
  // 🔥 (2026-08-20) لكن "اليوم/إمبارح/أول إمبارح" فلترة محلية (client-side)
  // بعد التحميل، والترتيب من السيرفر تنازلي بالتاريخ (الأحدث أولاً). أول
  // تصليح كان بيوقف التحميل التلقائي تمامًا لما أي فلتر تاريخي يكون شغّال —
  // ده منع اللوب اللانهائي، بس كسر حاجتين: (أ) لو "اليوم" فيها أكتر من 50
  // أوردر، بيوقف عند أول 50 وميكملش، (ب) "أول إمبارح" (يومين قبل النهاردة)
  // ممكن أصلاً ما يكونش موجود في أول 50 صف خالص، فكان بيرجع صفر نتايج دايمًا.
  //
  // الحل الصح: يفضل يحمّل صفحات لغاية ما "أقدم صف اتحمّل" يعدّي بداية اليوم
  // المطلوب فعليًا (بعدها أي صفحة زيادة مضمون ما تفدش، زي المنطق الأصلي، بس
  // من غير ما يوقف بدري قبل ما يوصل هناك). أقدم صف = آخر عنصر في clientOrders
  // (بيتحمّل بالترتيب من السيرفر، فيفضل تنازلي).
  const ordersDatePresetLowerBound = useMemo(() => {
    if (filterType !== 'today' && filterType !== 'yesterday' && filterType !== 'dayBefore') return null;
    const daysBack = filterType === 'today' ? 0 : filterType === 'yesterday' ? 1 : 2;
    const bound = new Date(); bound.setHours(0, 0, 0, 0); bound.setDate(bound.getDate() - daysBack);
    return bound;
  }, [filterType]);
  const ordersOldestLoadedDate = useMemo(() => {
    if (!clientOrders.length) return null;
    return parseFlexibleTimestamp(clientOrders[clientOrders.length - 1]?.confirmation_date);
  }, [clientOrders]);
  const ordersDateBoundaryReached =
    !!ordersDatePresetLowerBound &&
    !!ordersOldestLoadedDate &&
    ordersOldestLoadedDate.getTime() < ordersDatePresetLowerBound.getTime();
  useEffect(() => {
    const el = ordersSentinelRef.current;
    if (!el || !hasMoreOrders || loadingOrders || ordersDateBoundaryReached) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && !loadingMoreOrders) {
          handleLoadMoreOrders();
        }
      },
      { root: ordersScrollRef.current, rootMargin: '150px' }
    );
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView, hasMoreOrders, loadingOrders, loadingMoreOrders, clientOrders.length, ordersDateBoundaryReached]);

  // G12.2.a/c — chats and feedback: نفس الفكرة، بتتحمّل من جوّه صندوق الجدول
  // بتاعها من غير سقف صفحات.
  useEffect(() => {
    const el = chatsSentinelRef.current;
    if (activeView !== 'conversations' || !el || !hasMoreChats || loadingChats) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting) && !loadingMoreChats) {
          handleLoadMoreChats();
        }
      },
      { root: chatsScrollRef.current, rootMargin: '150px' }
    );
    io.observe(el);
    return () => io.disconnect();
    // handleLoadMoreChats intentionally reads the current guarded state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView, hasMoreChats, loadingChats, loadingMoreChats, allChats.length]);

  useEffect(() => {
    const el = feedbacksSentinelRef.current;
    if (activeView !== 'feedback' || !el || !hasMoreFeedbacks || loadingFeedbacks) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting) && !loadingMoreFeedbacks) {
          handleLoadMoreFeedbacks();
        }
      },
      { root: feedbacksScrollRef.current, rootMargin: '150px' }
    );
    io.observe(el);
    return () => io.disconnect();
    // handleLoadMoreFeedbacks intentionally reads the current guarded state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView, hasMoreFeedbacks, loadingFeedbacks, loadingMoreFeedbacks, feedbacks.length]);

  // 🔥 --- Effect لقراءة إحصائيات الأوردرات من brand_stats_api ---
  useEffect(() => {
    if (!clientInfo?.brand_name) return undefined;

    const unsubscribe = subscribeBrandStats(clientInfo.brand_name, ({ stats, error }) => {
      if (error) {
        console.error('Error fetching brand stats:', error);
        // في حالة الخطأ، نحسب من clientOrders
        if (clientOrders && clientOrders.length > 0) {
          const confirmed = clientOrders.filter((o) => o.final_status === 'CONFIRM').length;
          const aiCreated = clientOrders.filter((o) => o.final_status === 'AI_CREATED').length;
          const cancelled = clientOrders.filter((o) => o.final_status === 'CANCELED').length;
          const unclear = clientOrders.filter((o) => o.final_status === 'UNCLEAR').length;
          setTotalOrdersStats({
            confirmed,
            aiCreated,
            cancelled,
            unclear,
            total: clientOrders.length
          });
        }
        return;
      }

      if (stats) {
        // ✅ الإحصائيات موجودة - نستخدمها
        setTotalOrdersStats({
          confirmed: stats.confirmed || 0,
          aiCreated: stats.aiCreated || 0,
          cancelled: stats.cancelled || 0,
          unclear: stats.unclear || 0,
          total: stats.total || 0
        });
      } else {
        // ⚠️ الإحصائيات مش موجودة - نحسبها من الأوردرات المحملة
        console.warn('⚠️ brand_stats_api غير موجود، الحساب من clientOrders');
        if (clientOrders && clientOrders.length > 0) {
          const confirmed = clientOrders.filter((o) => o.final_status === 'CONFIRM').length;
          const aiCreated = clientOrders.filter((o) => o.final_status === 'AI_CREATED').length;
          const cancelled = clientOrders.filter((o) => o.final_status === 'CANCELED').length;
          const unclear = clientOrders.filter((o) => o.final_status === 'UNCLEAR').length;
          setTotalOrdersStats({
            confirmed,
            aiCreated,
            cancelled,
            unclear,
            total: clientOrders.length
          });
        } else {
          setTotalOrdersStats({
            confirmed: 0,
            aiCreated: 0,
            cancelled: 0,
            unclear: 0,
            total: 0
          });
        }
      }
    });

    return () => unsubscribe();
  }, [clientInfo, clientOrders]);

  // Effect الأسئلة
  useEffect(() => {
    if (!clientInfo?.brand_name) return undefined;
    setLoadingQuestions(true);
    const unsubscribe = subscribeOpenQuestions(clientInfo.brand_name, ({ rows, error }) => {
      if (error) {
        console.error("Error fetching questions:", error);
        setLoadingQuestions(false);
        return;
      }
      setQuestions((rows || []).map(mapRowToLegacyShape));
      setLoadingQuestions(false);
    });
    return () => unsubscribe();
  }, [clientInfo]);

  // Effect قاعدة المعرفة
  useEffect(() => {
    if (!clientInfo?.brand_name) return undefined;
    setLoadingKnowledgeBase(true);
    const unsubscribe = subscribeKnowledgeBase(clientInfo.brand_name, ({ rows, error }) => {
      if (error) {
        console.error("Error fetching knowledge base:", error);
        setLoadingKnowledgeBase(false);
        return;
      }
      setKnowledgeBase((rows || []).map(mapRowToLegacyShape));
      setLoadingKnowledgeBase(false);
    });
    return () => unsubscribe();
  }, [clientInfo]);

  // 🔥 --- Effect للـ Pending Orders ---
  useEffect(() => {
    if (!clientInfo?.brand_name) return undefined;

    const unsubscribe = subscribePendingOrders(
      clientInfo.brand_name,
      { phoneSearch: searchPendingPhone.trim(), orderSearch: searchPendingOrderNumber.trim() },
      ({ rows, error }) => {
        if (error) {
          console.error("Error fetching pending orders:", error);
          return;
        }
        setPendingOrders((rows || []).map(mapRowToLegacyShape));
      }
    );

    return () => unsubscribe();
  }, [clientInfo, searchPendingPhone, searchPendingOrderNumber]);

  // 🔥 --- Effect لجلب non-AI chats ---
  useEffect(() => {
    if (!clientInfo?.brand_name) return undefined;

    setNonAiChats([]);
    setNonAiChatsLoaded(false);
    setNonAiChatsError(false);
    console.log('🔍 جاري جلب المحادثات اليدوية لـ:', clientInfo.brand_name);

    const unsubscribe = subscribeNonAiChats(clientInfo.brand_name, ({ rows, error }) => {
      if (error) {
        console.error('Error fetching non-AI chats:', error);
        setNonAiChatsError(true);
        setNonAiChatsLoaded(true);
        return;
      }
      setNonAiChatsError(false);
      setNonAiChatsLoaded(true);
      const chats = (rows || []).map(mapRowToLegacyShape);
      console.log('📊 عدد المحادثات اليدوية:', chats.length);
      chats.forEach(chat => {
        console.log('  📄', chat.id, '- Unread:', chat.unreadCount || 0);
      });
      setNonAiChats(chats);
    });

    return () => unsubscribe();
  }, [clientInfo]);

  // 🔥 --- (جديد) Effect المحادثات ---
  useEffect(() => {
    if (!clientInfo?.brand_name) return undefined;
    setLoadingChats(true);

    // السيرش بيحصل server-side في Supabase — مش client-side
    const phoneSearch = searchChatPhone.trim();
    const isSearching = !!(phoneSearch || searchLeadsOrderNumber.trim());

    const unsubscribe = subscribeChatHistories(
      clientInfo.brand_name,
      { search: isSearching, phoneSearch },
      ({ rows, error }) => {
        if (error) {
          console.error("Error fetching all chats:", error);
          setLoadingChats(false);
          return;
        }

        const chats = (rows || []).map((row) => {
          const legacy = mapRowToLegacyShape(row);
          return {
            ...legacy,
            customer_phone: legacy.customer_phone,
            customer_name: legacy.messages && legacy.messages.length > 0 ? t('chat.list.conversation') : t('chat.list.emptyConversation')
          };
        });

        if (isSearching) {
          setHasMoreChats(false);
        } else {
          setLastVisibleChat(chats.length);
          setHasMoreChats(chats.length === ORDERS_PAGE_SIZE);
        }

        setAllChats(chats);
        setLoadingChats(false);
      }
    );

    return () => unsubscribe();
  }, [clientInfo, searchChatPhone, searchLeadsOrderNumber, t]);
  // 🔥 --- (انتهى الجديد) ---


  // الدوال
  const handleOpenReplyModal = (question) => {
    setSelectedQuestion(question);
    setAnswerText(''); 
    setSaveToKnowledgeBase(false); 
  };

  const handleCloseReplyModal = useCallback(() => {
    if (isSubmitting) return; 
    setSelectedQuestion(null);
  }, [isSubmitting]);

  const handleSubmitAnswer = useCallback(async () => {
    if (!selectedQuestion || !answerText.trim() || isSubmitting) return;
    setIsSubmitting(true);
    
    try {
      console.log(`📤 Answering question ${selectedQuestion.id} via Cloud Function`);
      console.log('📋 Data being sent:', {
        questionId: selectedQuestion.id,
        answerText: answerText,
        addToKnowledge: saveToKnowledgeBase
      });
      
      const result = await callAnswerCustomerQuestion({
        questionId: selectedQuestion.id,
        answerText: answerText,
        addToKnowledge: saveToKnowledgeBase
      });
      
      console.log('✅ Answer sent successfully:', result);
      
      if (result.status === 'success') {
        handleCloseReplyModal();
        
        // رسالة بسيطة وواضحة
        let successMessage = t('alerts.answer.success');
        if (result.added_to_knowledge) {
          successMessage += `\n${t('alerts.answer.savedToKb')}`;
        }

        alert(successMessage);
      } else {
        throw new Error(t('alerts.answer.error'));
      }
      
    } catch (error) {
      console.error("❌ Error submitting answer:", error);
      console.error("❌ Full error:", JSON.stringify(error, null, 2));
      alert(`${t('alerts.answer.error')}\n${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedQuestion, answerText, saveToKnowledgeBase, isSubmitting, handleCloseReplyModal, t]); 

  // ⚠️ محظور حاليًا تحت الـgrants الحية — مفيش GRANT DELETE لـ authenticated
  // على brand_questions_api بعد (MIGRATION_MAP.md "الغموض #2"). الاستدعاء
  // جاهز لحظة ما الـGRANT يتوسّع أو راوت سيرفر يتبنى.
  const handleDeleteQuestion = useCallback(async (questionId) => {
    if (!window.confirm(t('alerts.question.deleteConfirm'))) {
      return;
    }
    console.log(`Attempting to delete question: ${questionId}`);
    try {
      const { error } = await deleteQuestion(questionId);
      if (error) throw error;
      console.log("Question deleted successfully!");
    } catch (error) {
      console.error("Error deleting question: ", error);
      alert(t('alerts.question.deleteError'));
    }
  }, [t]);

  const handleOpenEditModal = (item) => {
    setEditingItem(item);
    setEditedQuestion(item.question_text);
    setEditedAnswer(item.answer_text);
    setEditedProduct(item.product_context || '');
  };

  const handleCloseEditModal = useCallback(() => {
    if (isSubmittingEdit) return;
    setEditingItem(null);
    setEditedQuestion('');
    setEditedAnswer('');
    setEditedProduct('');
  }, [isSubmittingEdit]);

  const handleSubmitEdit = useCallback(async () => {
    if (!editingItem || !editedQuestion.trim() || !editedAnswer.trim() || isSubmittingEdit) return;
    setIsSubmittingEdit(true);
    try {
      const { error } = await updateKnowledgeItem(editingItem.id, {
        question: editedQuestion,
        answer: editedAnswer,
        productContext: editedProduct,
      });
      if (error) throw error;
      handleCloseEditModal();
      alert(t('alerts.kb.saveSuccess'));
    } catch (error) {
      console.error("Error updating knowledge base item: ", error);
      alert(t('alerts.kb.saveError'));
    } finally {
      setIsSubmittingEdit(false);
    }
  }, [editingItem, editedQuestion, editedAnswer, editedProduct, isSubmittingEdit, handleCloseEditModal, t]);

  // ⚠️ محظور حاليًا تحت الـgrants الحية — نفس سبب handleDeleteQuestion فوق
  // (مفيش GRANT DELETE لـ authenticated على knowledge_base_api بعد).
  const handleDeleteKnowledgeItem = useCallback(async (itemId) => {
    if (!window.confirm(t('alerts.kb.deleteConfirm'))) {
      return;
    }
    console.log(`Attempting to delete knowledge item: ${itemId}`);
    try {
      const { error } = await deleteKnowledgeItem(itemId);
      if (error) throw error;
      console.log("Knowledge item deleted successfully!");
    } catch (error) {
      console.error("Error deleting knowledge item: ", error);
      alert(t('alerts.kb.deleteError'));
    }
  }, [t]);

  // إضافة سؤال جديد
  const handleOpenAddModal = () => {
    setNewQuestion('');
    setNewAnswer('');
    setNewProduct('');
    setShowAddKnowledgeModal(true);
  };

  const handleCloseAddModal = useCallback(() => {
    if (isSubmittingNew) return;
    setShowAddKnowledgeModal(false);
    setNewQuestion('');
    setNewAnswer('');
    setNewProduct('');
  }, [isSubmittingNew]);

  const handleSubmitNew = useCallback(async () => {
    if (!newQuestion.trim() || !newAnswer.trim() || isSubmittingNew) {
      alert('يرجى ملء السؤال والإجابة');
      return;
    }
    setIsSubmittingNew(true);
    try {
      const { error } = await addKnowledgeItem(clientInfo.brand_name, {
        question: newQuestion,
        answer: newAnswer,
        productContext: newProduct,
      });
      if (error) throw error;
      handleCloseAddModal();
      alert('✅ تم إضافة السؤال والإجابة بنجاح!');
    } catch (error) {
      console.error("Error adding knowledge item: ", error);
      alert('حدث خطأ في الإضافة. حاول مرة أخرى.');
    } finally {
      setIsSubmittingNew(false);
    }
  }, [newQuestion, newAnswer, newProduct, isSubmittingNew, clientInfo, handleCloseAddModal]);

  // 🔥 تصدير الأوردرات المعلقة إلى Excel
  const exportPendingOrdersToExcel = useCallback(() => {
    try {
      if (pendingOrders.length === 0) {
        alert(t('alerts.pending.none'));
        return;
      }

      const exportData = pendingOrders.map(order => ({
        'رقم الأوردر': order.order_id || order.order_number || 'N/A',
        'اسم العميل': order.customer_name || 'N/A',
        'رقم الهاتف': order.customer_phone || 'N/A',
        'المنتجات': order.items?.map(item => 
          `${item.title || item.product_name || item.name} (${item.quantity}×)`
        ).join(', ') || 'N/A',
        'الإجمالي': `${order.total_price || order.grand_total || 0} ${order.currency || 'EGP'}`,
        'العنوان': order.shipping_address || order.address || 'N/A',
        'المحافظة': order.province || order.governorate || 'N/A',
        'المدينة': order.city || 'N/A',
        'ملاحظات': order.notes || order.note || '-',
        'تاريخ الإنشاء': order.created_at || order.createdAt || 'N/A'
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      ws['!cols'] = [
        { wch: 15 }, { wch: 20 }, { wch: 15 }, { wch: 40 }, { wch: 15 },
        { wch: 35 }, { wch: 15 }, { wch: 15 }, { wch: 30 }, { wch: 20 }
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'الأوردرات المعلقة');

      const fileName = `أوردرات_معلقة_${clientInfo?.brand_name || 'client'}_${new Date().toLocaleDateString('ar-EG').replace(/\//g, '-')}.xlsx`;
      XLSX.writeFile(wb, fileName);
      
      console.log('✅ تم تصدير الأوردرات المعلقة بنجاح');
    } catch (error) {
      console.error('❌ خطأ في تصدير الأوردرات:', error);
      alert(t('alerts.export.error'));
    }
  }, [pendingOrders, clientInfo, t]);

  // 🔥 دالة تحديث إعداد الإلغاء التلقائي — عبر setAutoCancel() (RPC
  // updateMyBrandSettings whitelist)، مش query+updateDoc مباشر.
  // ⚠️ الحارس بقى `!clientInfo?.brand_name` بدل `!clientInfo?.auth_uid` —
  // get_my_brand_settings() ماترجعش auth_uid أبدًا (حقل هوية، مش قابل
  // للعرض بالتصميم، شوف daf_confirmation_rls.sql)؛ brand_name موجود
  // دايمًا في القاعدة (`doc_id, brand_name, webhook_path_id, ...`).
  const handleToggleAutoCancel = useCallback(async () => {
    if (isUpdatingAutoCancel || !clientInfo?.brand_name) return;

    setIsUpdatingAutoCancel(true);
    const newValue = !autoCancelEnabled;

    try {
      const { error } = await setAutoCancel(newValue);
      if (error) throw error;

      setAutoCancelEnabled(newValue);
      alert(newValue
        ? t('alerts.autoCancel.enabled').replace('16', cancelTimeoutHours)
        : t('alerts.autoCancel.disabled')
      );
    } catch (error) {
      console.error('Error updating auto_cancel:', error);
      alert(t('alerts.settingError'));
    } finally {
      setIsUpdatingAutoCancel(false);
    }
  }, [autoCancelEnabled, isUpdatingAutoCancel, clientInfo, t]);

  // 🔥 دالة تحديث إعداد Duplicated Order Note
  const handleToggleDuplicatedOrderNote = useCallback(async () => {
    if (isUpdatingDuplicatedOrderNote || !clientInfo?.brand_name) return;

    setIsUpdatingDuplicatedOrderNote(true);
    const newValue = !duplicatedOrderNoteEnabled;

    try {
      const { error } = await setDuplicatedOrderNote(newValue);
      if (error) throw error;

      setDuplicatedOrderNoteEnabled(newValue);
      alert(newValue
        ? t('alerts.duplicatedOrderNote.enabled')
        : t('alerts.duplicatedOrderNote.disabled')
      );
    } catch (error) {
      console.error('Error updating dublicated_order_note:', error);
      alert(t('alerts.settingError'));
    } finally {
      setIsUpdatingDuplicatedOrderNote(false);
    }
  }, [duplicatedOrderNoteEnabled, isUpdatingDuplicatedOrderNote, clientInfo, t]);

  // 🔥 دالة تحديث cancel_timeout_hours
  const handleCancelTimeoutChange = useCallback(async (hours) => {
    if (isUpdatingCancelTimeout || !clientInfo?.brand_name) return;

    setIsUpdatingCancelTimeout(true);
    try {
      const { error } = await saveCancelTimeoutHours(hours);
      if (error) throw error;
      setCancelTimeoutHours(hours);
    } catch (error) {
      console.error('Error updating cancel_timeout_hours:', error);
      alert(t('alerts.settingError'));
    } finally {
      setIsUpdatingCancelTimeout(false);
    }
  }, [isUpdatingCancelTimeout, clientInfo, t]);

  // ⏰ خيارات توقيت أول رسالة (بالساعات): من ربع ساعة لحد 3 أيام
  const FIRST_MSG_DELAY_OPTIONS = [0.25, 0.5, 0.75, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 48, 72];

  // ⏰ تحويل قيمة الساعات لنص مقروء حسب اللغة
  const formatDelayLabel = useCallback((value) => {
    const h = parseFloat(value);
    if (Number.isNaN(h)) return '';
    if (h < 1) return t('dashboard.firstMsgDelay.unit.min').replace('{n}', String(Math.round(h * 60)));
    if (h === 1) return t('dashboard.firstMsgDelay.unit.hour').replace('{n}', '1');
    if (h < 24) return t('dashboard.firstMsgDelay.unit.hours').replace('{n}', String(h));
    if (h === 24) return t('dashboard.firstMsgDelay.unit.day');
    return t('dashboard.firstMsgDelay.unit.days').replace('{n}', String(h / 24));
  }, [t]);

  // ⏰ دالة تحديث first_message_delay_hours (نفس نمط cancel_timeout_hours)
  const handleFirstMsgDelayChange = useCallback(async (hours) => {
    if (isUpdatingFirstMsgDelay || !clientInfo?.brand_name) return;

    setIsUpdatingFirstMsgDelay(true);
    try {
      const { error } = await saveFirstMessageDelayHours(hours);
      if (error) throw error;
      setFirstMessageDelayHours(hours);
      alert(t('alerts.firstMsgDelay.saved').replace('{label}', formatDelayLabel(hours)));
    } catch (error) {
      console.error('Error updating first_message_delay_hours:', error);
      alert(t('alerts.settingError'));
    } finally {
      setIsUpdatingFirstMsgDelay(false);
    }
  }, [isUpdatingFirstMsgDelay, clientInfo, t, formatDelayLabel]);

  // --- handler Toggle لـ delivery_date_enabled (نمط handleToggleFeedback) ---
  const handleToggleDeliveryDate = useCallback(async () => {
    if (isUpdatingDeliveryDate || !clientInfo?.brand_name) return;

    setIsUpdatingDeliveryDate(true);
    const newValue = !deliveryDateEnabled;

    try {
      const { error } = await saveDeliveryDateEnabled(newValue);
      if (error) throw error;

      setDeliveryDateEnabled(newValue);
      alert(newValue
        ? t('alerts.deliveryDate.enabled')
        : t('alerts.deliveryDate.disabled')
      );
    } catch (error) {
      console.error('Error updating delivery_date_enabled:', error);
      alert(t('alerts.settingError'));
    } finally {
      setIsUpdatingDeliveryDate(false);
    }
  }, [deliveryDateEnabled, isUpdatingDeliveryDate, clientInfo, t]);

  // --- handler input رقمي لـ delivery_average_days (نمط handleCancelTimeoutChange) ---
  const handleDeliveryDaysChange = useCallback(async (days) => {
    if (isUpdatingDeliveryDays || !clientInfo?.brand_name) return;

    setIsUpdatingDeliveryDays(true);
    try {
      const { error } = await saveDeliveryAverageDays(days);
      if (error) throw error;
      setDeliveryAverageDays(days);
      alert(t('alerts.deliveryDate.daysSaved').replace('{n}', days));
    } catch (error) {
      console.error('Error updating delivery_average_days:', error);
      alert(t('alerts.settingError'));
    } finally {
      setIsUpdatingDeliveryDays(false);
    }
  }, [isUpdatingDeliveryDays, clientInfo, t]);

  // 🔥 دالة تحديث إعداد الـ Feedback
  const handleGetTelegramLink = useCallback(async () => {
    if (!clientInfo?.brand_name) return;
    setIsLoadingTelegramLink(true);
    try {
      const API_BASE = process.env.REACT_APP_CONFIRMATION_API_BASE;
      const res = await fetch(`${API_BASE}/api/telegram-bot/link-url?brand=${encodeURIComponent(clientInfo.brand_name)}`, {
        headers: { 'authorization': `Bearer ${(await import('./lib/supabaseAuth')).getAccessToken?.() || ''}` }
      });
      const data = await res.json();
      if (data.url) {
        // Magic link — يفتح البوت مباشرة (user يضغط زرار ✅ جوّه تليجرام)
        setTelegramLinkUrl(data.url);
        window.open(data.url, '_blank');
      }
    } catch (err) {
      console.error('telegram link error:', err);
    } finally {
      setIsLoadingTelegramLink(false);
    }
  }, [clientInfo?.brand_name]);

  const handleToggleFeedback = useCallback(async () => {
    if (isUpdatingFeedback || !clientInfo?.brand_name) return;

    setIsUpdatingFeedback(true);
    const newValue = !feedbackEnabled;

    try {
      const { error } = await saveFeedbackEnabled(newValue);
      if (error) throw error;

      setFeedbackEnabled(newValue);
      alert(newValue
        ? t('alerts.feedbackToggle.enabled')
        : t('alerts.feedbackToggle.disabled')
      );
    } catch (error) {
      console.error('Error updating feedback:', error);
      alert(t('alerts.settingError'));
    } finally {
      setIsUpdatingFeedback(false);
    }
  }, [feedbackEnabled, isUpdatingFeedback, clientInfo, t]);

  // 🔥 دالة Toggle لـ AI Mode
  const handleToggleAiMode = useCallback(async () => {
    if (isUpdatingAiMode || !clientInfo?.brand_name) return;

    // ✅ التحقق إن على الأقل واحد من Facebook URL أو Instagram URL موجود
    const hasFacebook = String(clientInfo?.facebook_url || '').trim() !== '';
    const hasInstagram = String(clientInfo?.instegram_url || '').trim() !== '';

    if (!hasFacebook && !hasInstagram) {
      alert(lang === 'ar'
        ? '⚠️ لازم تحط على الأقل Facebook URL أو Instagram URL الأول!\nروح حطهم في صفحة الإعدادات.'
        : '⚠️ You must add at least a Facebook URL or Instagram URL first!\nGo add them in the settings page.'
      );
      // فتح مودال الإعدادات عشان يحط الـ URLs
      setEditingTelegram(String(clientInfo?.brand_owner_telegram_ID || ''));
      setEditingFacebook(String(clientInfo?.facebook_url || ''));
      setEditingInstagram(String(clientInfo?.instegram_url || ''));
      setEditingMessageLanguage(clientInfo?.message_language || 'ar');
      setShowClientInfoModal(true);
      return;
    }

    setIsUpdatingAiMode(true);
    const newValue = !aiModeEnabled;

    try {
      // ⚠️ فجوة order_cost (MIGRATION_MAP.md §5 #7 + updateMyBrandSettings's
      // doc comment في supabaseAuth.js): الكود القديم كان بيكتب ai_mode
      // و order_cost سوا. order_cost عمود typed حساس بالفوترة، مُستبعد
      // عمدًا من whitelist الـRPC (قرار أمان متعمّد — التاجر مش قادر يتلاعب
      // برقم فوترته من المتصفح). setAiMode() الجديدة بتكتب ai_mode
      // (+shipping_check:false لو بيتقفل) بس — order_cost **مش هيتغيّر**
      // في الداتابيز حتى لو الرسالة تحت لسه بتعرض الرقم "المفروض". قرار
      // مطلوب من المالك (توسيع الـRPC أو دالة SQL جديدة تحسبه من جوّه
      // Postgres) — موثّق، مش تم الالتفاف عليه هنا.
      const { error } = await setAiMode(newValue);
      if (error) throw error;

      const newOrderCost = newValue ? (clientInfo.order_cost_ai || 0.06) : (clientInfo.order_cost_no_ai || 0.02);
      // ⛔ لو AI اتقفل → نقفل الـ shipping_check كمان (setAiMode() بتكتبها
      // في نفس الـRPC، هنا بس بنحدّث الـlocal state).
      if (!newValue) {
        setShippingCheckEnabled(false);
      }
      // تحديث clientInfo محلياً (mutation مباشرة زي الكود القديم بالظبط —
      // مش setState، فمش بيعمل re-render فوري؛ سايبناها زي ما هي لتقليل
      // الانحراف، بس القيمة دي دلوقتي عرض تقديري بس مش انعكاس لحاجة
      // اتكتبت فعليًا في الداتابيز، شوف الفجوة فوق).
      clientInfo.order_cost = newOrderCost;
      setAiModeEnabled(newValue);
      alert(newValue
        ? t('alerts.aiMode.enabled') + ` - $${newOrderCost.toFixed(2)}`
        : t('alerts.aiMode.disabled') + ` - $${newOrderCost.toFixed(2)}`
      );
    } catch (error) {
      console.error('Error updating ai_mode:', error);
      alert(t('alerts.settingError'));
    } finally {
      setIsUpdatingAiMode(false);
    }
  }, [aiModeEnabled, isUpdatingAiMode, clientInfo, t, lang]);

  // 🔥 دالة Toggle لـ Shipping Check
  const handleToggleShippingCheck = useCallback(async () => {
    if (isUpdatingShippingCheck || !clientInfo?.brand_name) return;

    // ⛔ مينفعش تشغل Shipping Check لو AI واقف
    if (!aiModeEnabled) return;

    setIsUpdatingShippingCheck(true);
    const newValue = !shippingCheckEnabled;

    try {
      const { error } = await saveShippingCheckEnabled(newValue);
      if (error) throw error;
      setShippingCheckEnabled(newValue);
      alert(newValue
        ? t('alerts.shippingCheck.enabled')
        : t('alerts.shippingCheck.disabled')
      );
    } catch (error) {
      console.error('Error updating shipping_check:', error);
      alert(t('alerts.settingError'));
    } finally {
      setIsUpdatingShippingCheck(false);
    }
  }, [shippingCheckEnabled, isUpdatingShippingCheck, aiModeEnabled, clientInfo, t]);

  // 🔥 دالة Toggle لـ Bosta Awaiting Actions
  const handleToggleBostaActions = useCallback(async () => {
    if (isUpdatingBostaActions || !clientInfo?.brand_name) return;

    setIsUpdatingBostaActions(true);
    const newValue = !bostaActionsEnabled;

    try {
      const { error } = await setBostaAwaitingActions(newValue);
      if (error) throw error;

      setBostaActionsEnabled(newValue);
      alert(newValue
        ? t('bosta.toggle.enabled')
        : t('bosta.toggle.disabled')
      );
    } catch (error) {
      console.error('Error updating bosta_awaiting_actions:', error);
      alert(t('alerts.settingError'));
    } finally {
      setIsUpdatingBostaActions(false);
    }
  }, [bostaActionsEnabled, isUpdatingBostaActions, clientInfo, t]);

  // 🔥 دالة فتح مودال الـ Feedback
  const handleOpenFeedbackModal = useCallback(async (feedback) => {
    if (!feedback || !clientInfo?.brand_name || !feedback.customer_phone) {
      console.error("Data is incomplete for feedback.", feedback);
      alert(t('alerts.feedback.incomplete'));
      return;
    }

    // إلغاء أي listener سابق
    if (chatListenerUnsubscribe.current) {
      chatListenerUnsubscribe.current();
    }

    setLoadingFeedbackChat(true);
    setSelectedFeedback(feedback);
    setFeedbackChatHistory([]);
    setMessageInput('');

    // 🔥 إعادة تعيين عداد الرسائل غير المقروءة (best-effort، زي القديم بالظبط).
    // ⚠️ resetChatUnreadCount بتعمل REPLACE لعمود data كله مش merge (bug
    // موثّق صراحة في confirmationData.js "Ambiguity #8") — ممكن تمسح
    // phoneNumber/brand/platform/addedAt من نفس الصف لو الرقم ده مضاف
    // فعلاً لـnon_ai_chat_api. الإصلاح الصحيح يحتاج RPC جديدة أو
    // read-modify-write (برّه نطاق هذه المهمة — صفر SQL جديد)، اتسجّل في
    // تقرير المهمة كأعلى انحراف خطورة في هذا الملف.
    try {
      await resetChatUnreadCount(clientInfo.brand_name, feedback.customer_phone);
      console.log('✅ Reset unread count to 0');
    } catch (error) {
      console.error('Error resetting unread count:', error);
    }

    try {
      // 🔥 استخدام subscribeChatThread للـ Real-time updates (بديل onSnapshot)
      const unsubscribe = subscribeChatThread(clientInfo.brand_name, feedback.customer_phone, ({ messages, error }) => {
        if (error) {
          console.error("Error listening to feedback chat:", error);
          alert(t('alerts.chat.followError'));
          setFeedbackChatHistory([]);
          setLoadingFeedbackChat(false);
          return;
        }
        setFeedbackChatHistory(messages || []);
        setLoadingFeedbackChat(false);
      });

      chatListenerUnsubscribe.current = unsubscribe;
    } catch (error) {
      console.error("Error setting up feedback chat listener:", error);
      alert(t('alerts.chat.openError'));
      setFeedbackChatHistory([]);
      setLoadingFeedbackChat(false);
    }
  }, [clientInfo, t]);

  const handleCloseFeedbackModal = useCallback(() => {
    // إلغاء الـ listener لو موجود
    if (chatListenerUnsubscribe.current) {
      chatListenerUnsubscribe.current();
      chatListenerUnsubscribe.current = null;
    }
    setSelectedFeedback(null);
    setFeedbackChatHistory([]);
    setMessageInput('');
  }, []);

  // 🔥 دالة إرسال رسالة يدوية للـ Feedback (عبر WhatsApp API)
  const handleSendFeedbackMessage = useCallback(async () => {
    if (!messageInput.trim() || isSendingMessage || !selectedFeedback || !clientInfo?.brand_name) {
      return;
    }

    setIsSendingMessage(true);

    try {
      const orderId = `${clientInfo.brand_name}_${selectedFeedback.customer_phone}`;
      
      console.log('📤 Sending feedback message via WhatsApp API...');
      
      const result = await callSendManualMessage({
        orderId: orderId,
        clientId: clientInfo.brand_name,
        message: messageInput.trim()
      });

      console.log('✅ Feedback message sent successfully:', result);
      
      if (result.time_remaining) {
        const messageWithTime = t('alerts.message.sentWithTime').replace('{time}', result.time_remaining);
        alert(messageWithTime);
      } else {
        alert(t('alerts.message.sent'));
      }

      setMessageInput('');
      
    } catch (error) {
      console.error('Error sending feedback message:', error);
      const errorMessage = error.message || t('alerts.message.sendError');
      alert(`${t('alerts.message.errorPrefix')} ${errorMessage}`);
    } finally {
      setIsSendingMessage(false);
    }
  }, [messageInput, isSendingMessage, selectedFeedback, clientInfo, t]);

  // 🔥 دالة فتح مودال الـ Bosta
  const handleOpenBostaModal = useCallback(async (bostaRecord) => {
    if (!bostaRecord || !clientInfo?.brand_name || !bostaRecord.customer_phone) {
      console.error("Data is incomplete for bosta.", bostaRecord);
      alert(t('alerts.feedback.incomplete'));
      return;
    }

    if (chatListenerUnsubscribe.current) {
      chatListenerUnsubscribe.current();
    }

    setLoadingBostaChat(true);
    setSelectedBosta(bostaRecord);
    setBostaChatHistory([]);
    setMessageInput('');

    // ⚠️ نفس الملاحظة بتاعة resetChatUnreadCount في handleOpenFeedbackModal
    // فوق (jsonb replace مش merge — bug موثّق "Ambiguity #8").
    try {
      await resetChatUnreadCount(clientInfo.brand_name, bostaRecord.customer_phone);
    } catch (error) {
      console.error('Error resetting unread count:', error);
    }

    try {
      const unsubscribe = subscribeChatThread(clientInfo.brand_name, bostaRecord.customer_phone, ({ messages, error }) => {
        if (error) {
          console.error("Error listening to bosta chat:", error);
          alert(t('alerts.chat.followError'));
          setBostaChatHistory([]);
          setLoadingBostaChat(false);
          return;
        }
        setBostaChatHistory(messages || []);
        setLoadingBostaChat(false);
      });

      chatListenerUnsubscribe.current = unsubscribe;
    } catch (error) {
      console.error("Error setting up bosta chat listener:", error);
      alert(t('alerts.chat.openError'));
      setBostaChatHistory([]);
      setLoadingBostaChat(false);
    }
  }, [clientInfo, t]);

  const handleCloseBostaModal = useCallback(() => {
    if (chatListenerUnsubscribe.current) {
      chatListenerUnsubscribe.current();
      chatListenerUnsubscribe.current = null;
    }
    setSelectedBosta(null);
    setBostaChatHistory([]);
    setMessageInput('');
  }, []);

  const handleSendBostaMessage = useCallback(async () => {
    if (!messageInput.trim() || isSendingMessage || !selectedBosta || !clientInfo?.brand_name) {
      return;
    }

    setIsSendingMessage(true);

    try {
      const orderId = `${clientInfo.brand_name}_${selectedBosta.customer_phone}`;
      const result = await callSendManualMessage({
        orderId: orderId,
        clientId: clientInfo.brand_name,
        message: messageInput.trim()
      });

      if (result.time_remaining) {
        const messageWithTime = t('alerts.message.sentWithTime').replace('{time}', result.time_remaining);
        alert(messageWithTime);
      } else {
        alert(t('alerts.message.sent'));
      }

      setMessageInput('');
    } catch (error) {
      console.error('Error sending bosta message:', error);
      const errorMessage = error.message || t('alerts.message.sendError');
      alert(`${t('alerts.message.errorPrefix')} ${errorMessage}`);
    } finally {
      setIsSendingMessage(false);
    }
  }, [messageInput, isSendingMessage, selectedBosta, clientInfo, t]);

  // 🔥 دالة فتح مودال الشات مع Real-time listener
  const handleOpenChatModal = useCallback(async (orderOrChat) => {
    if (!orderOrChat || !clientInfo?.brand_name || !orderOrChat.customer_phone) {
      console.error("Data is incomplete for chat history.", orderOrChat);
      alert(t('alerts.chat.dataIncomplete'));
      return;
    }

    // إلغاء أي listener سابق
    if (chatListenerUnsubscribe.current) {
      chatListenerUnsubscribe.current();
    }

    setLoadingChat(true);
    setSelectedOrderForChat(orderOrChat);
    setChatHistory([]);
    setMessageInput(''); // مسح الـ input

    // 🔥 إعادة تعيين عداد الرسائل غير المقروءة — نفس ملاحظة
    // resetChatUnreadCount في handleOpenFeedbackModal فوق (jsonb replace
    // مش merge، "Ambiguity #8" في confirmationData.js).
    try {
      await resetChatUnreadCount(clientInfo.brand_name, orderOrChat.customer_phone);
      console.log('✅ Reset unread count to 0');
    } catch (error) {
      console.error('Error resetting unread count:', error);
    }

    try {
      // 🔥 استخدام subscribeChatThread للـ Real-time updates (بديل onSnapshot،
      // بيرتّب الرسائل جوّاها بنفس منطق getSafeTime القديم).
      const unsubscribe = subscribeChatThread(clientInfo.brand_name, orderOrChat.customer_phone, ({ messages, error }) => {
        if (error) {
          console.error("Error listening to chat history:", error);
          alert(t('alerts.chat.followError'));
          setChatHistory([]);
          setLoadingChat(false);
          return;
        }
        setChatHistory(messages || []);
        setLoadingChat(false);
      });

      // حفظ الـ unsubscribe function
      chatListenerUnsubscribe.current = unsubscribe;
    } catch (error) {
      console.error("Error setting up chat listener:", error);
      alert(t('alerts.chat.openError'));
      setChatHistory([]);
      setLoadingChat(false);
    }
  }, [clientInfo, t]);

  const handleCloseChatModal = useCallback(() => {
    // إلغاء الـ listener لو موجود
    if (chatListenerUnsubscribe.current) {
      chatListenerUnsubscribe.current();
      chatListenerUnsubscribe.current = null;
    }
    setSelectedOrderForChat(null);
    setChatHistory([]);
    setMessageInput(''); // مسح الـ input
  }, []);

  // Telegram deep link: open the exact customer chat as soon as the merchant's
  // authenticated brand has loaded. This does not trust the URL for identity;
  // subscribeChatThread remains scoped by the signed-in brand.
  useEffect(() => {
    if (deepLinkOpenedRef.current || !deepLinkChat || !clientInfo?.brand_name) return;
    deepLinkOpenedRef.current = true;
    navigate('/dashboard?view=conversations&chat=' + encodeURIComponent(deepLinkChat), { replace: true });
    handleOpenChatModal({
      customer_phone: deepLinkChat,
      customer_name: t('chat.list.conversation'),
    });
  }, [deepLinkChat, clientInfo?.brand_name, handleOpenChatModal, t, navigate]);

  // 🔥 دالة إرسال رسالة يدوية
  const handleSendMessage = useCallback(async () => {
    if (!messageInput.trim() || isSendingMessage || !selectedOrderForChat || !clientInfo?.brand_name) {
      return;
    }

    setIsSendingMessage(true);

    try {
      // 🎯 استدعاء Cloud Function للإرسال عبر WhatsApp
      const orderId = `${clientInfo.brand_name}_${selectedOrderForChat.customer_phone}`;
      
      console.log('📤 Sending manual message via Cloud Function...');
      
      const result = await callSendManualMessage({
        orderId: orderId,
        clientId: clientInfo.brand_name,
        message: messageInput.trim()
      });

      console.log('✅ Message sent successfully:', result);
      
      // عرض رسالة نجاح
      if (result.time_remaining) {
        const messageWithTime = t('alerts.message.sentWithTime').replace('{time}', result.time_remaining);
        alert(messageWithTime);
      } else {
        alert(t('alerts.message.sent'));
      }

      setMessageInput(''); // مسح الـ input بعد الإرسال
      
      // الرسالة الجديدة هتظهر تلقائياً عن طريق onSnapshot
    } catch (error) {
      console.error('Error sending message:', error);
      
      // عرض رسالة الخطأ بشكل واضح
      const errorMessage = error.message || t('alerts.message.sendError');
      alert(`${t('alerts.message.errorPrefix')} ${errorMessage}`);
    } finally {
      setIsSendingMessage(false);
    }
  }, [messageInput, isSendingMessage, selectedOrderForChat, clientInfo, user, t]);

  // 🔥 دالة للتحقق إذا كان الرقم في non-AI chat
  const isNonAiChat = useCallback((phoneNumber) => {
    return nonAiChats.some(chat => chat.phoneNumber === phoneNumber);
  }, [nonAiChats]);

  // 🔥 دالة Toggle للـ non-AI chat
  const handleToggleNonAiChat = useCallback(async (phoneNumber, platform, e, requestedMode) => {
    e.stopPropagation(); // منع فتح المودال

    if (!phoneNumber || !clientInfo?.brand_name) {
      alert(t('alerts.toggle.incompleteData'));
      return;
    }
    if (requestedMode === 'manual' && isNonAiChat(phoneNumber)) return;

    setTogglingChat(prev => ({...prev, [phoneNumber]: true}));

    try {
      const docId = `${clientInfo.brand_name}_${phoneNumber}`;
      const isCurrentlyNonAi = isNonAiChat(phoneNumber);

      if (isCurrentlyNonAi) {
        // حذف من non-AI (رجوع للـ AI)
        // ⚠️ محظور حاليًا تحت الـgrants الحية — مفيش GRANT DELETE لـ
        // authenticated على non_ai_chat_api بعد (MIGRATION_MAP.md "الغموض #2").
        const { error } = await removeFromNonAiChat(docId);
        if (error) throw error;
        setNonAiChats(prev => prev.filter(chat => chat.phoneNumber !== phoneNumber));
        console.log(`✅ Removed ${phoneNumber} from non-AI chat`);
      } else {
        // إضافة لـ non-AI
        console.log('➕ إضافة محادثة يدوية:', docId);
        const { error } = await addToNonAiChat(clientInfo.brand_name, phoneNumber, platform);
        if (error) throw error;
        setNonAiChats(prev => prev.some(chat => chat.phoneNumber === phoneNumber) ? prev : [
          ...prev,
          { id: docId, brand: clientInfo.brand_name, phoneNumber, platform, unreadCount: 0 }
        ]);
        console.log(`✅ Added ${phoneNumber} to non-AI chat with unreadCount: 0`);
      }
    } catch (error) {
      console.error('Error toggling non-AI chat:', error);
      alert(t('alerts.chat.toggleError'));
    } finally {
      setTogglingChat(prev => ({...prev, [phoneNumber]: false}));
    }
  }, [clientInfo, isNonAiChat, t]);

  const handleTextareaEnterPress = useCallback((event, submitType) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault(); 
      if (submitType === 'answer') {
        if (isSubmitting || !answerText.trim()) return;
        handleSubmitAnswer();
      } 
      else if (submitType === 'edit') {
        if (isSubmittingEdit || !editedAnswer.trim()) return;
        handleSubmitEdit();
      }
    }
  }, [isSubmitting, answerText, handleSubmitAnswer, isSubmittingEdit, editedAnswer, handleSubmitEdit]);

  // useMemos
  const stats = useMemo(() => {
    // 🔥 نستخدم totalOrdersStats (كل الأوردرات) بدلاً من clientOrders (المحملة فقط)
    const savedShipping = totalOrdersStats.cancelled * 1.2; // $1.2 لكل أوردر ملغي
    return { 
      confirmed: totalOrdersStats.confirmed,
      aiCreated: totalOrdersStats.aiCreated,
      cancelled: totalOrdersStats.cancelled,
      unclear: totalOrdersStats.unclear,
      total: totalOrdersStats.total,
      savedShipping 
    };
   }, [totalOrdersStats]);

  // دالة للحصول على رقم الأوردر من final أو pending
  const getOrderNumberForChat = useCallback((customerPhone) => {
    if (!customerPhone) return '-';
    
    // 🔥 تنظيف رقم الهاتف من المسافات والأحرف الخاصة
    const cleanPhone = String(customerPhone).trim().replace(/\s+/g, '');
    
    // 🔥 البحث في الـ cache أولاً (فيه كل الأوردرات اللي اتجابت)
    const finalOrder = ordersCache.find(order => {
      const orderPhone = String(order.customer_phone || '').trim().replace(/\s+/g, '');
      return orderPhone === cleanPhone;
    });
    if (finalOrder?.order_number) return finalOrder.order_number;
    
    // لو مفيش، نبحث في pending_orders_api
    const pendingOrder = pendingOrders.find(order => {
      const orderPhone = String(order.customer_phone || '').trim().replace(/\s+/g, '');
      return orderPhone === cleanPhone;
    });
    if (pendingOrder?.order_number) return `${pendingOrder.order_number} (معلق)`;
    
    return '-';
  }, [ordersCache, pendingOrders]);

  const filteredOrdersList = useMemo(() => {
    if (!clientOrders || clientOrders.length === 0) {
      return [];
    }
    // فلترة بالحالة
    const statusFiltered = clientOrders.filter(order => activeStatusFilter === 'ALL' || order.final_status === activeStatusFilter);

    // ⚠️ G11.2.a — الفلترة بالبحث اتشالت من هنا خالص.
    // كانت بتتعمل بـ String.includes على كل الصفوف بعد ما بتتسحب كلها من
    // السيرفر. دلوقتي بتحصل في الاستعلام نفسه (applyOrderSearch في
    // confirmationData.js) — يعني السيرفر بيرجّع المطابق بس. إعادة الفلترة هنا
    // كانت هتشيل صفوف السيرفر قصدها توصل (زي البحث بالاسم اللي مالوش نظير محلي).
    const searchFiltered = statusFiltered;

    // 🔥 (تصليح ما بعد الهجرة) كانت بترجع null لأي حاجة غير Firestore
    // Timestamp — وده كان بيستبعد كل الأوردرات (`if (!orderDate) return
    // false` تحت) لأن `confirmation_date` بقى ISO string عادي بعد النقل
    // لـ Postgres. بتستخدم الهيلبر المشترك `parseFlexibleTimestamp` دلوقتي.
    const convertTimestampToDate = parseFlexibleTimestamp;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
    const dayBefore = new Date(today); dayBefore.setDate(today.getDate() - 2);

    const filteredByDate = searchFiltered.filter(order => {
      const orderDate = convertTimestampToDate(order.confirmation_date);
      if (!orderDate) return false;
      const orderDateNormalized = new Date(orderDate); orderDateNormalized.setHours(0, 0, 0, 0);
      switch (filterType) {
        case 'today': return orderDateNormalized.getTime() === today.getTime();
        case 'yesterday': return orderDateNormalized.getTime() === yesterday.getTime();
        case 'dayBefore': return orderDateNormalized.getTime() === dayBefore.getTime();
        case 'custom':
          if (!startDate || !endDate) return true;
          const start = new Date(startDate); start.setHours(0, 0, 0, 0);
          const end = new Date(endDate); end.setHours(23, 59, 59, 999);
          return orderDate >= start && orderDate <= end;
        case 'all': default: return true;
      }
    });

    // ترتيب تنازلي حسب وقت الإنشاء (الأحدث فوق)
    const sortedByDateDesc = filteredByDate.sort((a, b) => {
      const dateA = convertTimestampToDate(a.confirmation_date)?.getTime() || 0;
      const dateB = convertTimestampToDate(b.confirmation_date)?.getTime() || 0;
      return dateB - dateA;
    });

    return sortedByDateDesc.map(order => {
      const dateObj = convertTimestampToDate(order.confirmation_date);
      const formattedDate = dateObj ? dateObj.toLocaleString('ar-EG', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : 'Invalid Date';
      return { ...order, formattedCreatedAt: formattedDate };
    });
     // البحث اتشال من الـmemo ده (السيرفر بيعمله)، فمابقاش من الـdeps.
     }, [clientOrders, activeStatusFilter, filterType, startDate, endDate]);

  // 🔥 --- useMemo للأوردرات المعلقة (server-side search — no client filter needed) ---
  const filteredPendingOrders = useMemo(() => {
    if (!pendingOrders || pendingOrders.length === 0) return [];

    // ترتيب حسب وقت الإنشاء (الأحدث أولاً)
    const sorted = [...pendingOrders].sort((a, b) => {
      const dateA = parseFlexibleTimestamp(a.confirmation_date || a.created_at)?.getTime() || 0;
      const dateB = parseFlexibleTimestamp(b.confirmation_date || b.created_at)?.getTime() || 0;
      return dateB - dateA;
    });
    return sorted;
  }, [pendingOrders]);

  const statusTitles = useMemo(() => ({
    CONFIRM: t('dashboard.stats.confirmed'),
    AI_CREATED: t('dashboard.stats.ai'),
    CANCELED: t('dashboard.stats.cancelled'),
    UNCLEAR: t('dashboard.stats.unclear'),
    ALL: t('dashboard.stats.total'),
  }), [t]);

  const csvHeaders = useMemo(() => ([
    { label: t('dashboard.orders.table.orderId'), key: 'order_id' },
    { label: t('dashboard.orders.table.orderNumber'), key: 'order_number' },
    { label: t('dashboard.orders.table.created'), key: 'formattedCreatedAt' },
    { label: t('dashboard.orders.table.customer'), key: 'customer_name' },
    { label: t('dashboard.orders.table.phone'), key: 'customer_phone' },
    { label: t('dashboard.orders.table.price'), key: 'total_price' },
    { label: t('dashboard.orders.table.products'), key: 'product_names' },
    { label: t('dashboard.orders.table.message'), key: 'message' },
    { label: t('dashboard.orders.table.status'), key: 'final_status' }
   ]), [t]);

  const formatFirestoreTimestamp = (timestamp) => {
    // 🔥 (تصليح ما بعد الهجرة) بيتعامل مع كل أشكال التواريخ الممكنة بعد
    // النقل لـ Postgres (ISO string / epoch-ms number أو نص / Firestore
    // Timestamp النادر المتبقي) عبر الهيلبر المشترك `parseFlexibleTimestamp`
    // — مهم بالذات لـ `chatDoc.last_updated_timestamp` اللي مخزّن بشكلين
    // مختلطين حيًّا (ISO string ورقم epoch-ms) في نفس العمود.
    if (!timestamp) return 'غير متوفر';

    const date = parseFlexibleTimestamp(timestamp);
    if (!date) return 'وقت غير صحيح';

    return date.toLocaleString(lang === 'ar' ? 'ar-EG' : 'en-US', {
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit', 
      minute: '2-digit'
    });
  };

  // JSX helpers
  const currencyLabel = t('currency.egp');

  // Set the status filter (stays on the current view — used by the Overview KPI cards
  // to filter the "recent orders" table below, and by the Orders-page filter chips).
  const goToOrdersFiltered = (statusFilter) => {
    setActiveStatusFilter(statusFilter);
  };

  // Active nav item label for topbar
  const activeNavItem = NAV_ITEMS.find(n => n.key === activeView);
  const activeViewLabel = activeNavItem ? (lang === 'ar' ? activeNavItem.labelAr : activeNavItem.labelEn) : '';

  // 🔗 Managed billing bridge (Daf One): hide this dashboard's payment/billing
  // UI when (a) the brand is explicitly flagged billing_managed_by === 'daf_one'
  // (set by the bridge on a Daf One purchase — hides everywhere), OR (b) the
  // dashboard is being viewed EMBEDDED inside Daf One (?embedded=1). Case (b)
  // closes the pre-purchase window: a Daf One merchant who hasn't bought a plan
  // yet must top up from Daf One, never pay Daf Confirmation directly.
  // Independent merchants in a normal tab (neither flag nor embed) are unaffected.
  const isBillingManagedByDafOne =
    clientInfo?.billing_managed_by === 'daf_one' || isDafOneEmbedded();
  const visibleNavItems = isBillingManagedByDafOne
    ? NAV_ITEMS.filter(item => item.key !== 'billing')
    : NAV_ITEMS;

  // ============================================================
  // SIDEBAR
  // ============================================================
  const SidebarJSX = (
    <>
      <aside className={`rd-sidebar${sidebarOpen ? ' rd-sidebar--open' : ''}`}>
        {/* Brand — prefer the real Daf One store name (passed via ?brandName=
            when embedded) over the confirmation client's own brand_name, which
            is often just the shop handle. */}
        <div className="rd-sidebar-brand">
          <div className="rd-sidebar-logo">{((getDafOneBrandName() || clientInfo.brand_name || 'D').trim().charAt(0) || 'D').toUpperCase()}</div>
          <div>
            <div className="rd-sidebar-brand-name">{getDafOneBrandName() || clientInfo.brand_name || 'Daf Agency'}</div>
            <div className="rd-sidebar-brand-sub">Daf Agency</div>
          </div>
        </div>

        {/* Nav */}
        <nav className="rd-nav">
          {visibleNavItems.map(item => (
            <button
              key={item.key}
              className={`rd-nav-item${activeView === item.key ? ' active' : ''}`}
              onClick={() => { setActiveView(item.key); setSidebarOpen(false); }}
            >
              <span className="rd-nav-icon">{item.icon}</span>
              <span>{lang === 'ar' ? item.labelAr : item.labelEn}</span>
            </button>
          ))}
        </nav>

        {/* Footer */}
        <div className="rd-sidebar-footer">
          {/* Balance chip */}
          <div className="rd-balance-chip">
            ${(clientInfo.balance || 0).toFixed(2)} رصيد
          </div>
          <button
            className="rd-nav-item"
            onClick={handleLogout}
            style={{ color: '#ff5a6e' }}
          >
            <span className="rd-nav-icon">🚪</span>
            <span>{t('dashboard.logout')}</span>
          </button>
        </div>
      </aside>
      {/* Overlay — closes drawer on click */}
      {sidebarOpen && (
        <div
          className="rd-sidebar-overlay active"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </>
  );

  // ============================================================
  // TOPBAR
  // ============================================================
  const TopBarJSX = (
    <header className="rd-topbar">
      {/* Hamburger (mobile only) */}
      <button
        className="rd-hamburger"
        onClick={() => setSidebarOpen(prev => !prev)}
        aria-label="فتح القائمة"
      >
        ☰
      </button>

      {/* Right side: page title */}
      <h2 className="rd-topbar-title">{activeViewLabel}</h2>

      {/* Left side: actions */}
      <div className="rd-topbar-actions">
        {/* System status */}
        <span className={`rd-status-pill${clientInfo.isActive ? '' : ' inactive'}`}>
          <span className="rd-status-dot"></span>
          {clientInfo.isActive ? t('dashboard.system.on') : t('dashboard.system.off')}
        </span>

        {/* Language toggle */}
        <button
          onClick={toggleLang}
          className="rd-btn rd-btn-ghost rd-btn-sm"
          aria-label={t('lang.toggle')}
        >
          🌐 <span className="rd-topbar-btn-label">{lang === 'en' ? 'EN' : 'AR'}</span>
        </button>

        {/* Edit profile — navigates to Settings where the form lives inline */}
        <button
          onClick={() => setActiveView('settings')}
          className="rd-btn rd-btn-primary rd-btn-sm"
          title={t('dashboard.brandDataTooltip')}
        >
          ✏️ <span className="rd-topbar-btn-label">{t('dashboard.editProfile')}</span>
        </button>

        {/* Contact us */}
        <button
          onClick={() => {
            const msg = t('dashboard.contactUsMessage')
              .replace('{brandName}', clientInfo.brand_name || '')
              .replace('{platform}', clientInfo.platform === 'easyorder' ? 'EasyOrder' : 'Shopify');
            const whatsappUrl = `https://wa.me/201558329566?text=${encodeURIComponent(msg)}`;
            window.open(whatsappUrl, '_blank');
          }}
          className="rd-btn rd-btn-ghost rd-btn-sm"
          title={t('dashboard.contactUsTooltip')}
        >
          📞 <span className="rd-topbar-btn-label">{t('dashboard.contactUs')}</span>
        </button>
      </div>
    </header>
  );

  // ============================================================
  // VIEW: OVERVIEW
  // ============================================================
  const handleOwnerOrderAction = async (order, action, event) => {
    event.stopPropagation();
    const id=String(order.order_id || order.id);
    if(orderActionLocks.current.has(id))return;
    orderActionLocks.current.add(id);
    setOrderActionBusy(prev=>({...prev,[id]:action}));
    setOrderActionNotice(prev=>({...prev,[id]:null}));
    try {
      const result=await callOwnerOrderAction({orderId:id,action,expectedStatus:order.final_status || 'PENDING',notifyCustomer:orderNotifyCustomer[id]!==false,requestId:window.crypto.randomUUID()});
      const updated=result.order || {...order,final_status:result.status};
      setLastOrderActionNotice({error:false,text:`#${String(order.order_number||id).replace(/^#/, '')}: ${result.warning || (lang==='ar'?(action==='confirm'?'تم تأكيد الأوردر':'تم إلغاء الأوردر'):(action==='confirm'?'Order confirmed':'Order cancelled'))}`});
      setPendingOrders(prev=>prev.filter(item=>String(item.order_id || item.id)!==id));
      setClientOrders(prev=>prev.some(item=>String(item.order_id || item.id)===id)?prev.map(item=>String(item.order_id || item.id)===id?{...item,...updated}:item):[updated,...prev]);
      setOrdersCache(prev=>prev.map(item=>String(item.order_id || item.id)===id?{...item,...updated}:item));
      setOrderActionNotice(prev=>({...prev,[id]:{error:false,text:result.warning || (lang==='ar'?(action==='confirm'?'تم تأكيد الأوردر':'تم إلغاء الأوردر'):(action==='confirm'?'Order confirmed':'Order cancelled'))}}));
    } catch(error) {
      setLastOrderActionNotice({error:true,text:error.message});
      setOrderActionNotice(prev=>({...prev,[id]:{error:true,text:error.message}}));
    } finally {
      orderActionLocks.current.delete(id);
      setOrderActionBusy(prev=>({...prev,[id]:null}));
    }
  };
  const handleSendOrderNow = async (order,event) => {
    event.stopPropagation();
    const id=String(order.order_id||order.id);
    if(orderActionLocks.current.has(id))return;
    orderActionLocks.current.add(id);
    setOrderActionBusy(prev=>({...prev,[id]:'send'}));
    setOrderActionNotice(prev=>({...prev,[id]:null}));
    try {
      const result=await callSendOrderNow(id);
      const updated=result.order || {...order,follow_up_count:1};
      setPendingOrders(prev=>prev.map(item=>String(item.order_id||item.id)===id?{...item,...updated}:item));
      const text=lang==='ar'?(result.alreadySent?'رسالة التأكيد اتبعت بالفعل':'تم إرسال رسالة التأكيد للعميل'):(result.alreadySent?'Confirmation was already sent':'Confirmation message sent');
      setOrderActionNotice(prev=>({...prev,[id]:{error:false,text}}));
      setLastOrderActionNotice({error:false,text:`#${String(order.order_number||id).replace(/^#/,'')}: ${text}`});
    } catch(error) {
      setOrderActionNotice(prev=>({...prev,[id]:{error:true,text:error.message}}));
      setLastOrderActionNotice({error:true,text:error.message});
    } finally {
      orderActionLocks.current.delete(id);
      setOrderActionBusy(prev=>({...prev,[id]:null}));
    }
  };
  const renderOrderActions = (order,pending=false) => {
    const id=String(order.order_id || order.id);
    return <OrderActionControls order={order} lang={lang} busy={orderActionBusy[id]} notice={orderActionNotice[id]} notifyCustomer={orderNotifyCustomer[id]!==false} onNotifyChange={checked=>setOrderNotifyCustomer(prev=>({...prev,[id]:checked}))} onAction={(action,event)=>handleOwnerOrderAction(order,action,event)} onSendNow={pending?event=>handleSendOrderNow(order,event):undefined}/>;
  };
  const OverviewView = (
    <div>
      {/* KPI Grid */}
      <div className="rd-kpi-grid">
        {/* Confirmed */}
        <div
          className={`rd-kpi-card rd-kpi-confirmed${activeStatusFilter === 'CONFIRM' ? ' rd-kpi-active' : ''}`}
          onClick={() => goToOrdersFiltered('CONFIRM')}
        >
          <div className="rd-kpi-header">
            <div className="rd-kpi-icon">✓</div>
            <span className="rd-kpi-label">{t('dashboard.stats.confirmed')}</span>
          </div>
          <div className="rd-kpi-number">{stats?.confirmed ?? 0}</div>
          <div className="rd-kpi-pct">{stats?.total > 0 ? ((stats.confirmed / stats.total) * 100).toFixed(1) : 0}%</div>
        </div>

        {/* AI Created */}
        <div className="rd-kpi-card rd-kpi-ai" style={{ cursor: 'default' }}>
          <div className="rd-kpi-header">
            <div className="rd-kpi-icon">🤖</div>
            <span className="rd-kpi-label">{t('dashboard.stats.ai')}</span>
          </div>
          <div className="rd-kpi-number">{stats?.aiCreated ?? 0}</div>
          <div className="rd-kpi-pct">{stats?.total > 0 ? ((stats.aiCreated / stats.total) * 100).toFixed(1) : 0}%</div>
        </div>

        {/* Cancelled */}
        <div
          className={`rd-kpi-card rd-kpi-cancelled${activeStatusFilter === 'CANCELED' ? ' rd-kpi-active' : ''}`}
          onClick={() => goToOrdersFiltered('CANCELED')}
        >
          <div className="rd-kpi-header">
            <div className="rd-kpi-icon">✕</div>
            <span className="rd-kpi-label">{t('dashboard.stats.cancelled')}</span>
          </div>
          <div className="rd-kpi-number">{stats?.cancelled ?? 0}</div>
          <div className="rd-kpi-pct">{stats?.total > 0 ? ((stats.cancelled / stats.total) * 100).toFixed(1) : 0}%</div>
        </div>

        {/* Unclear */}
        <div
          className={`rd-kpi-card rd-kpi-unclear${activeStatusFilter === 'UNCLEAR' ? ' rd-kpi-active' : ''}`}
          onClick={() => goToOrdersFiltered('UNCLEAR')}
        >
          <div className="rd-kpi-header">
            <div className="rd-kpi-icon">?</div>
            <span className="rd-kpi-label">{t('dashboard.stats.unclear')}</span>
          </div>
          <div className="rd-kpi-number">{stats?.unclear ?? 0}</div>
          <div className="rd-kpi-pct">{stats?.total > 0 ? ((stats.unclear / stats.total) * 100).toFixed(1) : 0}%</div>
        </div>

        {/* Total */}
        <div
          className={`rd-kpi-card rd-kpi-total${activeStatusFilter === 'ALL' ? ' rd-kpi-active' : ''}`}
          onClick={() => goToOrdersFiltered('ALL')}
        >
          <div className="rd-kpi-header">
            <div className="rd-kpi-icon">📦</div>
            <span className="rd-kpi-label">{t('dashboard.stats.total')}</span>
          </div>
          <div className="rd-kpi-number">{stats?.total ?? 0}</div>
          <div className="rd-kpi-pct">100%</div>
        </div>

        {/* Saved Shipping */}
        <div className="rd-kpi-card" style={{ cursor: 'default', borderLeft: '3px solid #00c2cb' }}>
          <div className="rd-kpi-header">
            <div className="rd-kpi-icon" style={{ background: 'rgba(0,194,203,0.1)', color: '#00c2cb' }}>💰</div>
            <span className="rd-kpi-label">{t('dashboard.savedShipping.title')}</span>
          </div>
          <div className="rd-kpi-number" style={{ color: '#00c2cb', fontSize: '22px' }}>
            ${(stats?.savedShipping ?? 0).toFixed(2)}
          </div>
          <div className="rd-kpi-pct">≈ {((stats?.savedShipping ?? 0) * 50).toFixed(0)} {currencyLabel}</div>
        </div>
      </div>

      {/* Info boxes row: Balance + Telegram + Feedback */}
      <div className="rd-info-grid">
        {/* Balance */}
        <div className="rd-info-box" style={{ borderLeft: '3px solid #6c5ce7' }}>
          <div className="rd-info-box-title">💳 {t('dashboard.balance')}</div>
          <div className="rd-info-box-value" style={{ color: '#6c5ce7' }}>
            ${(clientInfo.balance || 0).toFixed(2)}
          </div>
          <div className="rd-info-box-sub">≈ {((clientInfo.balance || 0) * 50).toFixed(0)} {currencyLabel}</div>
          {clientInfo.platform === 'shopify' && clientInfo.platform_type === 'shopify' && (
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
              {t('dashboard.currentPlan')}: <strong style={{ color: '#6c5ce7' }}>{t(`dashboard.plan.${(clientInfo.billing_plan || 'starter').toLowerCase()}`)}</strong>
            </div>
          )}
          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
            📦 {t('dashboard.ordersAvailable')}: <strong style={{ color: '#1b1f2a' }}>{Math.floor((clientInfo.balance || 0) / (clientInfo.ai_mode !== false ? (clientInfo.order_cost_ai || clientInfo.order_cost || 0.06) : (clientInfo.order_cost_no_ai || clientInfo.order_cost || 0.02)))}</strong>
          </div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            {t('dashboard.orderCost')}: <strong>${(clientInfo.ai_mode !== false ? (clientInfo.order_cost_ai || clientInfo.order_cost || 0.06) : (clientInfo.order_cost_no_ai || clientInfo.order_cost || 0.02)).toFixed(2)}</strong>
            {clientInfo.ai_mode === true ? ' 🤖 AI' : ' 👤 Manual'}
          </div>
          {isBillingManagedByDafOne ? (
            <div
              style={{ marginTop: '12px', width: '100%', padding: '10px 12px', borderRadius: '8px', background: 'rgba(108,92,231,0.08)', color: '#6c5ce7', fontSize: '13px', fontWeight: 600, textAlign: 'center' }}
            >
              🔗 {t('dashboard.billing.managedByDafOne')}
            </div>
          ) : (
            <button
              onClick={() => navigate('/billing', { state: { clientInfo } })}
              className="rd-btn rd-btn-primary"
              style={{ marginTop: '12px', width: '100%' }}
            >
              💳 {t('dashboard.buyCredits')}
            </button>
          )}
        </div>

        {/* Telegram */}
        <div className="rd-info-box" style={{ borderLeft: '3px solid #3fa9f5' }}>
          <div className="rd-info-box-title">🔔 {t('dashboard.telegram.title')}</div>
          <div style={{ fontSize: '13px', color: '#6b7280', lineHeight: '1.6' }}>
            {t('dashboard.telegram.description')}
          </div>
          <ul style={{ fontSize: '12px', color: '#3fa9f5', paddingRight: '16px', marginTop: '8px', lineHeight: '1.8' }}>
            <li>{t('dashboard.telegram.itemQuestions')}</li>
            <li>{t('dashboard.telegram.itemOrders')}</li>
            <li>{t('dashboard.telegram.itemAlerts')}</li>
          </ul>

          {/* ربط تليجرام — magic link */}
          <button
            onClick={handleGetTelegramLink}
            disabled={isLoadingTelegramLink}
            className="rd-btn rd-btn-primary"
            style={{ marginTop: '12px', width: '100%', background: '#0088cc' }}
          >
            {isLoadingTelegramLink
              ? '⏳ جاري الفتح...'
              : telegramLinkUrl
                ? '✈️ فتح البوت مرة تانية'
                : '✈️ ربط تليجرام'}
          </button>

          {/* لو البراند عنده ID مربوط — بيظهر */}
          {clientInfo?.brand_owner_telegram_ID && (
            <div style={{ fontSize: '11px', color: '#22c55e', marginTop: '6px', textAlign: 'center' }}>
              ✅ مربوط ({clientInfo.brand_owner_telegram_ID.split(',').filter(Boolean).length} شات)
            </div>
          )}

          {/* بعد الضغط على الزرار — رسالة توجيه بسيطة */}
          {telegramLinkUrl && (
            <div style={{ marginTop: '8px', padding: '8px', background: '#f0fff4', borderRadius: '6px', fontSize: '11px', textAlign: 'center' }}>
              <div style={{ color: '#16a34a', fontWeight: 600, marginBottom: '4px' }}>
                🚀 فُتح تليجرام!
              </div>
              <div style={{ color: '#374151' }}>
                اضغط زرار ✅ في البوت عشان تكمّل الربط
              </div>
              <button
                onClick={() => { navigator.clipboard?.writeText(telegramLinkUrl); }}
                style={{ marginTop: '6px', fontSize: '10px', background: 'none', border: '1px solid #0088cc', borderRadius: '4px', padding: '2px 8px', cursor: 'pointer', color: '#0088cc' }}
              >
                📋 نسخ الرابط (لإضافة فريقك)
              </button>
            </div>
          )}

          <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '6px', textAlign: 'center' }}>
            {t('dashboard.telegram.hint')}
          </div>
        </div>

        {/* Feedback */}
        <div className="rd-info-box" style={{ borderLeft: '3px solid #f5a623' }}>
          <div className="rd-info-box-title">⭐ {t('dashboard.feedback.title')}</div>
          <button
            onClick={handleToggleFeedback}
            disabled={isUpdatingFeedback}
            className={`rd-btn${feedbackEnabled ? ' rd-btn-success' : ' rd-btn-ghost'}`}
            style={{ width: '100%', marginTop: '6px' }}
          >
            {isUpdatingFeedback ? '...' : (feedbackEnabled ? t('dashboard.feedback.toggle.on') : t('dashboard.feedback.toggle.off'))}
          </button>
          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
            {t('dashboard.feedback.priceLabel')}: <strong style={{ color: '#1b1f2a' }}>${(clientInfo?.feedback_cost ?? 0).toFixed(2)}</strong>
          </div>
        </div>
      </div>

      {/* Recent Orders table */}
      <div className="rd-card" style={{ marginTop: '4px' }}>
        <div className="rd-section-header">
          <h2 className="rd-section-title" style={{ fontSize: '16px' }}>
            أحدث الأوردرات
            <span className="rd-badge" style={{ background: activeStatusFilter !== 'ALL' ? '#6c5ce7' : '#21c97a' }}>
              {statusTitles[activeStatusFilter] || t('dashboard.stats.total')} ({filteredOrdersList.length})
            </span>
          </h2>
          <button
            onClick={() => setActiveView('orders')}
            className="rd-btn rd-btn-ghost rd-btn-sm"
            style={{ fontSize: '13px' }}
          >
            عرض الكل ←
          </button>
        </div>
        <div className="rd-table-wrap">
          <table className="rd-table">
            <thead>
              <tr>
                <th>{lang==='ar'?'إجراءات الأوردر':'Order actions'}</th>
                <th>رقم الأوردر</th>
                <th>العميل</th>
                <th>القيمة</th>
                <th>الحالة</th>
                <th>التاريخ</th>
              </tr>
            </thead>
            <tbody>
              {loadingOrders ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', color: '#6b7280', padding: '20px' }}>جاري التحميل...</td>
                </tr>
              ) : filteredOrdersList.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', color: '#6b7280', padding: '20px' }}>لا توجد أوردرات</td>
                </tr>
              ) : (
                filteredOrdersList.slice(0, 6).map((order) => {
                  const dateObj = parseFlexibleTimestamp(order.confirmation_date);
                  const dateStr = dateObj
                    ? dateObj.toLocaleDateString('ar-EG', { day: '2-digit', month: '2-digit', year: 'numeric' })
                    : '—';
                  const statusClass =
                    order.final_status === 'CONFIRM'   ? 'rd-pill rd-pill-confirmed' :
                    order.final_status === 'CANCELED'  ? 'rd-pill rd-pill-cancelled' :
                    order.final_status === 'UNCLEAR'   ? 'rd-pill rd-pill-pending'   :
                    'rd-pill rd-pill-neutral';
                  return (
                    <tr
                      key={order.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setActiveView('orders')}
                      title="عرض في صفحة الأوردرات"
                    >
                      <td>{renderOrderActions(order)}</td>
                      <td><strong style={{ color: '#6c5ce7' }}>{order.order_number || '—'}</strong></td>
                      <td>{order.customer_name || '—'}</td>
                      <td>{order.total_price || '—'}</td>
                      <td><span className={statusClass}>{order.final_status || '—'}</span></td>
                      <td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{dateStr}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // ============================================================
  // VIEW: ORDERS (existing confirmed/final orders table)
  // ============================================================

  const OrdersView = (
    <div className="rd-card">
      {lastOrderActionNotice && <p role={lastOrderActionNotice.error?'alert':'status'} style={{color:lastOrderActionNotice.error?'#b42318':'#167345'}}>{lastOrderActionNotice.text}</p>}
      <div className="rd-section-header">
        <h2 className="rd-section-title">
          {t('dashboard.orders.heading')}
          <span className="rd-badge" style={{ background: activeStatusFilter !== 'ALL' ? '#6c5ce7' : '#21c97a' }}>
            {/* 🔥 (2026-08-21) لما "All Orders" (مفيش فلتر حالة ولا تاريخ) بتكون
                شغّالة، ده لازم يبقى **إجمالي البراند الحقيقي** (من countOrders
                على الداتابيز) مش "اللي اتحمّل لحد دلوقتي" — كان بيعرض 100
                (أول صفحتين اتحمّلوا) بدل الـ~8000+ الحقيقيين، وده اللي كان
                شكله رقم غلط. لما فيه فلتر حالة أو تاريخ فعلي شغّال،
                filteredOrdersList.length صح لأنه بيعكس الفلترة الفعلية. */}
            {statusTitles[activeStatusFilter] || t('dashboard.stats.total')} (
            {activeStatusFilter === 'ALL' && filterType === 'all' && ordersTotal != null
              ? ordersTotal
              : filteredOrdersList.length}
            )
          </span>
        </h2>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <CSVLink
            data={filteredOrdersList}
            headers={csvHeaders}
            filename={`orders_${new Date().toLocaleDateString()}.csv`}
            className="rd-btn rd-btn-ghost rd-btn-sm"
            style={{ textDecoration: 'none' }}
          >
            ⬇ CSV
          </CSVLink>
        </div>
      </div>

      {/* Search fields */}
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
        <input
          type="text"
          placeholder={t('dashboard.orders.searchOrder')}
          value={searchOrderNumber}
          onChange={(e) => setSearchOrderNumber(e.target.value)}
          className="rd-input"
          style={{ flex: '1', minWidth: '200px' }}
        />
        <input
          type="text"
          placeholder={t('dashboard.orders.searchPhone')}
          value={searchPhoneNumber}
          onChange={(e) => setSearchPhoneNumber(e.target.value)}
          className="rd-input"
          style={{ flex: '1', minWidth: '200px' }}
        />
        {/* G11.2.b — البحث بالاسم. جو طلب بار زي بتاع بوسطة (اسم + تليفون +
            رقم أوردر) والداشبورد مكانش فيها بحث بالاسم خالص. */}
        <input
          type="text"
          placeholder={t('dashboard.orders.searchName')}
          value={searchCustomerName}
          onChange={(e) => setSearchCustomerName(e.target.value)}
          className="rd-input"
          style={{ flex: '1', minWidth: '200px' }}
        />
        {(searchOrderNumber || searchPhoneNumber || searchCustomerName) && (
          <button
            onClick={() => { setSearchOrderNumber(''); setSearchPhoneNumber(''); setSearchCustomerName(''); }}
            className="rd-btn rd-btn-danger rd-btn-sm"
          >
            {t('dashboard.orders.clearSearch')}
          </button>
        )}
      </div>

      {/* Status filters (control directly from the Orders page) */}
      <div className="rd-filter-bar" style={{ marginBottom: '8px' }}>
        {['ALL', 'CONFIRM', 'CANCELED', 'UNCLEAR', 'AI_CREATED'].map(st => (
          <button
            key={st}
            className={`rd-chip${activeStatusFilter === st ? ' active' : ''}`}
            onClick={() => setActiveStatusFilter(st)}
          >
            {statusTitles[st]}
          </button>
        ))}
      </div>

      {/* Date filters */}
      <div className="rd-filter-bar">
        {['all', 'today', 'yesterday', 'dayBefore'].map(ft => (
          <button
            key={ft}
            className={`rd-chip${filterType === ft ? ' active' : ''}`}
            onClick={() => setFilterType(ft)}
          >
            {t(`dashboard.orders.filters.${ft}`)}
          </button>
        ))}
        <span style={{ fontSize: '12px', color: '#6b7280' }}>{t('dashboard.orders.filters.from')}</span>
        <input type="date" className="rd-input" style={{ padding: '5px 8px', fontSize: '13px' }} value={startDate} onChange={(e) => { setStartDate(e.target.value); setFilterType('custom'); }} />
        <span style={{ fontSize: '12px', color: '#6b7280' }}>{t('dashboard.orders.filters.to')}</span>
        <input type="date" className="rd-input" style={{ padding: '5px 8px', fontSize: '13px' }} value={endDate} onChange={(e) => { setEndDate(e.target.value); setFilterType('custom'); }} />
      </div>

      {/* Table */}
      <div className="rd-table-wrap rd-table-scroll" ref={ordersScrollRef}>
        <table className="rd-table">
          <thead>
            <tr>
              <th>{lang==='ar'?'إجراءات الأوردر':'Order actions'}</th>
              <th>{t('dashboard.orders.table.chatMode')}</th>
              <th>{t('dashboard.orders.table.message')}</th>
              <th>{t('dashboard.orders.table.products')}</th>
              <th>{t('dashboard.orders.table.price')}</th>
              <th>{t('dashboard.orders.table.phone')}</th>
              <th>{t('dashboard.orders.table.customer')}</th>
              <th>{t('dashboard.orders.table.created')}</th>
              <th>{t('dashboard.orders.table.orderNumber')}</th>
              <th>{t('dashboard.orders.table.status')}</th>
            </tr>
          </thead>
          <tbody>
            {loadingOrders ? (
              <tr><td colSpan="10" style={{ textAlign: 'center', color: '#6b7280', padding: '24px' }}>{t('dashboard.orders.loading')}</td></tr>
            ) : filteredOrdersList.length > 0 ? (
              filteredOrdersList.map((order) => (
                <tr
                  key={order.id}
                  style={chatStyles.clickableRow}
                  onClick={() => handleOpenChatModal(order)}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(108,92,231,0.05)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = ''}
                >
                  <td>{renderOrderActions(order)}</td>
                  <td>
                    <button
                      onClick={(e) => handleToggleNonAiChat(order.customer_phone, order.platform_type || 'shopify', e)}
                      disabled={togglingChat[order.customer_phone]}
                      className={`rd-btn rd-btn-sm${isNonAiChat(order.customer_phone) ? ' rd-btn-danger' : ' rd-btn-success'}`}
                      style={{ minWidth: '70px' }}
                    >
                      {togglingChat[order.customer_phone] ? '...' : isNonAiChat(order.customer_phone) ? t('dashboard.nonAi.toggle.ai') : t('dashboard.nonAi.toggle.manual')}
                    </button>
                  </td>
                  <td style={{ maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{order.message}</td>
                  <td style={{ maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{order.product_names}</td>
                  <td>{order.total_price}</td>
                  <td>{order.customer_phone}</td>
                  <td>{order.customer_name}</td>
                  <td style={{ whiteSpace: 'nowrap', fontSize: '12px' }}>{order.formattedCreatedAt}</td>
                  <td><strong>{order.order_number}</strong></td>
                  <td>
                    <span className={`rd-pill${order.final_status === 'CONFIRM' ? ' rd-pill-confirmed' : order.final_status === 'CANCELED' ? ' rd-pill-cancelled' : order.final_status === 'UNCLEAR' ? ' rd-pill-pending' : ' rd-pill-neutral'}`}>
                      {order.final_status}
                    </span>
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan="10" style={{ textAlign: 'center', color: '#6b7280', padding: '24px' }}>{t('dashboard.orders.empty')}</td></tr>
            )}
          </tbody>
        </table>

        {/* ══ G12.2.c — زي فيسبوك: بتنزل جوّه صندوق الجدول نفسه، بيتحمّل ══
            الـsentinel هنا جوّه rd-table-scroll (مش برّاها) عشان الـobserver
            يقيس على سكرول الجدول، مش سكرول الصفحة الكبيرة. من غير زرار
            ومن غير سقف — بيفضل كده لغاية آخر الليستة. */}
        {hasMoreOrders && !loadingOrders && !ordersDateBoundaryReached && (
          <>
            {loadingMoreOrders && (
              <div className="rd-skeleton-rows" aria-live="polite">
                {/* هياكل صفوف، مش سبينر بيغطّي الجدول — التاجر يفضل قارئ اللي
                    قدامه وهو بيحمّل */}
                <div className="rd-skeleton-row" />
                <div className="rd-skeleton-row" />
                <div className="rd-skeleton-row" />
              </div>
            )}
            <div ref={ordersSentinelRef} style={{ height: 1 }} aria-hidden="true" />
          </>
        )}
      </div>
      {/* G11.3.d — العدد من الداتابيز، مش عدد اللي اتحمّل.
          🔥 (2026-08-21) `ordersTotal` (من countOrders) مش عارف بفلتر
          التاريخ خالص — بيفضل رقم إجمالي البراند كله. لما فلتر زي "اليوم"
          يكون شغّال، عرض "150 / 8673" جنب بعض كان بيبان زي رقم غلط (وهو مش
          غلط، بس مش نفس المقياس). دلوقتي لو فلتر تاريخي شغّال بيعرض عدد
          النتايج المفلترة فعليًا بس (اللي هو الرقم الصح والوحيد اللي له معنى
          هنا)، ومقارنة بالإجمالي بترجع تاني لما نرجع "All Orders". */}
      <div className="rd-list-footer">
        {filterType !== 'all'
          ? filteredOrdersList.length
          : (ordersTotal != null && ordersTotal > clientOrders.length
              ? `${clientOrders.length} / ${ordersTotal}`
              : `${clientOrders.length}`)}
        {(filterType !== 'all' ? ordersDateBoundaryReached || !hasMoreOrders : !hasMoreOrders) && clientOrders.length > 0 && (
          <span className="rd-end-of-list"> · {t('dashboard.orders.endOfList')}</span>
        )}
      </div>
    </div>
  );

  // ============================================================
  // VIEW: PENDING
  // ============================================================
  const PendingView = (
    <div className="rd-card">
      {lastOrderActionNotice && <p role={lastOrderActionNotice.error?'alert':'status'} style={{color:lastOrderActionNotice.error?'#b42318':'#167345'}}>{lastOrderActionNotice.text}</p>}
      <div className="rd-section-header">
        <h2 className="rd-section-title">
          {t('dashboard.pending.title')}
          <span className="rd-badge" style={{ background: '#f5a623' }}>{filteredPendingOrders.length}</span>
        </h2>
        <button onClick={exportPendingOrdersToExcel} className="rd-btn rd-btn-ghost rd-btn-sm">{t('dashboard.pending.export')}</button>
      </div>
      <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '-12px', marginBottom: '16px' }}>{t('dashboard.pending.hint')}</p>

      <div className="rd-filter-bar" style={{ marginBottom: '12px' }}>
        <input type="text" placeholder={t('dashboard.pending.searchOrder')} value={searchPendingOrderNumber} onChange={(e) => setSearchPendingOrderNumber(e.target.value)} className="rd-input" style={{ flex: '1', minWidth: '180px' }} />
        <input type="text" placeholder={t('dashboard.pending.searchPhone')} value={searchPendingPhone} onChange={(e) => setSearchPendingPhone(e.target.value)} className="rd-input" style={{ flex: '1', minWidth: '180px' }} />
        {(searchPendingOrderNumber || searchPendingPhone) && (
          <button onClick={() => { setSearchPendingOrderNumber(''); setSearchPendingPhone(''); }} className="rd-btn rd-btn-danger rd-btn-sm">{t('dashboard.pending.clearSearch')}</button>
        )}
      </div>

      <div className="rd-table-wrap rd-table-scroll">
        <table className="rd-table">
          <thead>
            <tr>
              <th>{lang==='ar'?'إجراءات الأوردر':'Order actions'}</th>
              <th>{t('dashboard.orders.table.chatMode')}</th>
              <th>{t('dashboard.pending.table.message')}</th>
              <th>{t('dashboard.pending.table.products')}</th>
              <th>{t('dashboard.pending.table.price')}</th>
              <th>{t('dashboard.pending.table.phone')}</th>
              <th>{t('dashboard.pending.table.customer')}</th>
              <th>{t('dashboard.pending.table.created')}</th>
              <th>{t('dashboard.pending.table.orderNumber')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredPendingOrders.length > 0 ? (
              filteredPendingOrders.map((order) => {
                const createdAt = order.confirmation_date || order.created_at;
                const formattedDate = createdAt ? formatFirestoreTimestamp(createdAt) : 'N/A';
                return (
                  <tr key={order.id} style={chatStyles.clickableRow} onClick={() => handleOpenChatModal(order)} onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(245,166,35,0.05)'} onMouseLeave={(e) => e.currentTarget.style.background = ''}>
                    <td>{renderOrderActions(order,true)}</td>
                    <td>
                      <button onClick={(e) => handleToggleNonAiChat(order.customer_phone, order.platform_type || 'shopify', e)} disabled={togglingChat[order.customer_phone]} className={`rd-btn rd-btn-sm${isNonAiChat(order.customer_phone) ? ' rd-btn-danger' : ' rd-btn-success'}`}>
                        {togglingChat[order.customer_phone] ? '...' : isNonAiChat(order.customer_phone) ? t('dashboard.nonAi.toggle.ai') : t('dashboard.nonAi.toggle.manual')}
                      </button>
                    </td>
                    <td>{order.message || order.whatsapp_message || '-'}</td>
                    <td>{order.product_names || order.products?.map(p => p.name).join(', ') || 'N/A'}</td>
                    <td>{order.total_price || 'N/A'}</td>
                    <td>{order.customer_phone || 'N/A'}</td>
                    <td>{order.customer_name || 'N/A'}</td>
                    <td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{formattedDate}</td>
                    <td><strong style={{ color: '#6c5ce7' }}>{order.order_number || 'N/A'}</strong></td>
                  </tr>
                );
              })
            ) : (
              <tr><td colSpan="9" style={{ textAlign: 'center', color: '#6b7280', padding: '24px' }}>{(searchPendingOrderNumber || searchPendingPhone) ? t('dashboard.pending.emptySearch') : t('dashboard.pending.emptyAll')}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  // ============================================================
  // VIEW: CONVERSATIONS (manual + leads tables)
  // ============================================================
  const ConversationsView = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Manual mode clients */}
      <div className="rd-card">
        <div className="rd-section-header">
          <h2 className="rd-section-title">
            {t('dashboard.manual.title')}
            <span className="rd-badge" style={{ background: '#ff5a6e' }}>{nonAiChats.length}</span>
          </h2>
        </div>
        <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '-12px', marginBottom: '14px' }}>{t('dashboard.manual.description')}</p>
        {nonAiChats.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px', color: '#6b7280' }}>
            <p style={{ fontSize: '16px', margin: 0 }}>{t('dashboard.manual.emptyTitle')}</p>
            <p style={{ fontSize: '13px', marginTop: '8px' }}>{t('dashboard.manual.emptyHint')}</p>
          </div>
        ) : (
          <>
            <div className="rd-filter-bar" style={{ marginBottom: '12px' }}>
              <input type="text" placeholder={t('dashboard.manual.searchPlaceholder')} value={searchNonAiPhone} onChange={(e) => setSearchNonAiPhone(e.target.value)} className="rd-input" style={{ flex: '1' }} />
            </div>
            <div className="rd-table-wrap rd-table-scroll">
              <table className="rd-table">
                <thead>
                  <tr>
                    <th>{t('dashboard.manual.actions')}</th>
                    <th>{t('dashboard.manual.addedAt')}</th>
                    <th>{t('dashboard.manual.customerPhone')}</th>
                    <th style={{ width: '40%' }}>{t('dashboard.manual.lastMessage')}</th>
                  </tr>
                </thead>
                <tbody>
                  {nonAiChats.filter(chat => {
                    if (!searchNonAiPhone.trim()) return true;
                    const digits = searchNonAiPhone.trim().replace(/\D/g, '');
                    const phoneDigits = String(chat.phoneNumber || '').replace(/\D/g, '');
                    return phoneDigits.includes(digits);
                  }).map((chat, index) => (
                    <tr key={chat.phoneNumber || index} style={{ cursor: 'pointer' }} onClick={() => { handleOpenChatModal({ customer_phone: chat.phoneNumber, customer_name: 'عميل' }); }} onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,90,110,0.05)'} onMouseLeave={(e) => e.currentTarget.style.background = ''}>
                      <td>
                        <button onClick={(e) => handleToggleNonAiChat(chat.phoneNumber, chat.platform, e)} disabled={togglingChat[chat.phoneNumber]} className="rd-btn rd-btn-success rd-btn-sm">
                          {togglingChat[chat.phoneNumber] ? '...' : t('dashboard.manual.reenableAi')}
                        </button>
                      </td>
                      <td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{(() => {
                        // 🔥 (تصليح ما بعد الهجرة) كان بيفترض Firestore Timestamp
                        // (`chat.addedAt.seconds`) — بعد الهجرة `addedAt` بقى ISO
                        // string عادي (شوف `addToNonAiChat` في confirmationData.js)،
                        // فكان دايمًا بيطلع "Invalid Date". بيستخدم الهيلبر المشترك دلوقتي.
                        const addedAtDate = parseFlexibleTimestamp(chat.addedAt);
                        return addedAtDate
                          ? addedAtDate.toLocaleString(lang === 'ar' ? 'ar-EG' : 'en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                          : '-';
                      })()}</td>
                      <td>{chat.phoneNumber || 'N/A'}</td>
                      <td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {(() => {
                          const chatHistoryDoc = allChats.find(c => c.customer_phone === chat.phoneNumber);
                          if (!chatHistoryDoc) return <span style={{ color: '#9ca3af', fontSize: '12px' }}>-</span>;
                          const messages = chatHistoryDoc.messages || [];
                          const lastMessage = messages[messages.length - 1];
                          if (!lastMessage) return <span style={{ color: '#9ca3af', fontSize: '12px' }}>{t('dashboard.manual.lastMessage.none')}</span>;
                          return (
                            <span style={{ color: lastMessage.role === 'user' ? '#3fa9f5' : '#1b1f2a', fontSize: '12px' }}>
                              {lastMessage.role === 'user' ? t('dashboard.manual.lastMessage.customer') : t('dashboard.manual.lastMessage.bot')}{lastMessage.content?.substring(0, 50)}{lastMessage.content?.length > 50 ? '...' : ''}
                            </span>
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* All conversations (leads) */}
      <div className="rd-card">
        <div className="rd-section-header">
          <h2 className="rd-section-title">
            {t('dashboard.leads.title')}
            <span className="rd-badge" style={{ background: '#3fa9f5' }}>
              {(() => { try { return allChats.length; } catch { return 0; } })()}
            </span>
          </h2>
        </div>
        <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '-12px', marginBottom: '14px' }}>{t('dashboard.leads.description')}</p>

        <div className="rd-filter-bar" style={{ marginBottom: '12px' }}>
          <input type="text" placeholder={t('dashboard.leads.searchPhone')} value={searchChatPhone} onChange={(e) => setSearchChatPhone(e.target.value)} className="rd-input" style={{ flex: '1', minWidth: '180px' }} />
          <input type="text" placeholder={t('dashboard.leads.searchOrder')} value={searchLeadsOrderNumber} onChange={(e) => setSearchLeadsOrderNumber(e.target.value)} className="rd-input" style={{ flex: '1', minWidth: '180px' }} />
          {(searchChatPhone || searchLeadsOrderNumber) && (
            <button onClick={() => { setSearchChatPhone(''); setSearchLeadsOrderNumber(''); }} className="rd-btn rd-btn-danger rd-btn-sm">{t('dashboard.leads.clearSearch')}</button>
          )}
        </div>

        <div className="rd-table-wrap rd-table-scroll" ref={chatsScrollRef}>
          <table className="rd-table">
            <thead>
              <tr>
                <th>{t('dashboard.leads.table.chatMode')}</th>
                <th>{t('dashboard.leads.table.lastUpdated')}</th>
                <th style={{ width: '30%' }}>{t('dashboard.leads.table.lastMessage')}</th>
                <th>{t('dashboard.leads.table.orderNumber')}</th>
                <th>{t('dashboard.leads.table.customerPhone')}</th>
                <th style={{ width: '20%' }}>{t('dashboard.leads.table.status')}</th>
              </tr>
            </thead>
            <tbody>
              {loadingChats ? (
                <tr><td colSpan="6" style={{ textAlign: 'center', color: '#6b7280', padding: '24px' }}>{t('dashboard.leads.loading')}</td></tr>
              ) : (() => {
                // phone filter = server-side already; order-number filter = client-side (local cache)
                const filteredChats = allChats.filter(chat => {
                  if (searchLeadsOrderNumber.trim()) {
                    const orderNum = getOrderNumberForChat(chat.customer_phone);
                    return orderNum && orderNum.toString().includes(searchLeadsOrderNumber.trim());
                  }
                  return true;
                }).sort((a, b) => {
                  const numA = (() => { const n = getOrderNumberForChat(a.customer_phone); return n === '-' ? 0 : parseInt(String(n).replace(/[^0-9]/g, '')) || 0; })();
                  const numB = (() => { const n = getOrderNumberForChat(b.customer_phone); return n === '-' ? 0 : parseInt(String(n).replace(/[^0-9]/g, '')) || 0; })();
                  return numB - numA;
                });

                return filteredChats.length > 0 ? filteredChats.map((chatDoc) => (
                  <tr key={chatDoc.id} style={chatStyles.clickableRow} onClick={() => handleOpenChatModal(chatDoc)} onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(63,169,245,0.05)'} onMouseLeave={(e) => e.currentTarget.style.background = ''}>
                    <td>
                      <button onClick={(e) => handleToggleNonAiChat(chatDoc.customer_phone, chatDoc.platform || 'shopify', e)} disabled={togglingChat[chatDoc.customer_phone]} className={`rd-btn rd-btn-sm${isNonAiChat(chatDoc.customer_phone) ? ' rd-btn-danger' : ' rd-btn-success'}`} style={{ minWidth: '70px' }}>
                        {togglingChat[chatDoc.customer_phone] ? '...' : isNonAiChat(chatDoc.customer_phone) ? t('dashboard.nonAi.toggle.ai') : t('dashboard.nonAi.toggle.manual')}
                      </button>
                    </td>
                    <td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{formatFirestoreTimestamp(chatDoc.last_updated_timestamp)}</td>
                    <td style={{ fontSize: '12px' }}>{chatDoc.messages && chatDoc.messages.length > 0 ? `${chatDoc.messages[chatDoc.messages.length - 1].role === 'user' ? t('dashboard.manual.lastMessage.customer') : t('dashboard.manual.lastMessage.bot')}${String(chatDoc.messages[chatDoc.messages.length - 1].content).substring(0, 50)}...` : t('dashboard.manual.lastMessage.none')}</td>
                    <td style={{ fontWeight: 600 }}>{getOrderNumberForChat(chatDoc.customer_phone)}</td>
                    <td>{chatDoc.customer_phone}</td>
                    <td>
                      {chatDoc.status ? <span className="rd-pill rd-pill-pending">{chatDoc.status}</span> : <span style={{ color: '#9ca3af', fontSize: '12px' }}>-</span>}
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan="6" style={{ textAlign: 'center', color: '#6b7280', padding: '24px' }}>{searchChatPhone ? t('dashboard.leads.emptySearch') : t('dashboard.leads.empty')}</td></tr>
                );
              })()}
            </tbody>
          </table>

          {hasMoreChats && !loadingChats && (
            <>
              {loadingMoreChats && <div className="rd-skeleton-rows" aria-live="polite"><div className="rd-skeleton-row" /><div className="rd-skeleton-row" /></div>}
              <div ref={chatsSentinelRef} style={{ height: 1 }} aria-hidden="true" />
            </>
          )}
        </div>
      </div>
    </div>
  );

  // ============================================================
  // VIEW: QUESTIONS
  // ============================================================
  const QuestionsView = (
    <div className="rd-card">
      <div className="rd-section-header">
        <h2 className="rd-section-title">
          {t('questions.title')}
          <span className="rd-badge">{questions.length}</span>
        </h2>
      </div>
      <div className="rd-table-wrap rd-table-scroll">
        <table className="rd-table">
          <thead>
            <tr>
              <th>{t('questions.table.action')}</th>
              <th style={{ width: '35%' }}>{t('questions.table.text')}</th>
              <th>{t('questions.table.product')}</th>
              <th>{t('questions.table.order')}</th>
              <th>{t('questions.table.phone')}</th>
              <th>{t('questions.table.date')}</th>
            </tr>
          </thead>
          <tbody>
            {loadingQuestions ? (
              <tr><td colSpan="6" style={{ textAlign: 'center', color: '#6b7280', padding: '24px' }}>{t('questions.loading')}</td></tr>
            ) : questions.length > 0 ? (
              questions.map((q) => (
                <tr key={q.id} style={chatStyles.clickableRow} onClick={() => handleOpenChatModal({ customer_phone: q.customer_phone, customer_name: q.customer_name || '', id: q.id })} onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(108,92,231,0.04)'} onMouseLeave={(e) => e.currentTarget.style.background = ''}>
                  <td>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      <button onClick={(e) => { e.stopPropagation(); handleOpenReplyModal(q); }} className="rd-btn rd-btn-primary rd-btn-sm">{t('questions.button.reply')}</button>
                      <button onClick={(e) => { e.stopPropagation(); handleDeleteQuestion(q.id); }} className="rd-btn rd-btn-danger rd-btn-sm">{t('questions.button.delete')}</button>
                    </div>
                  </td>
                  <td>{q.whatsapp_message}</td>
                  <td><span className="rd-pill rd-pill-pending">{q.product_context || 'N/A'}</span></td>
                  <td>{getOrderNumberForChat(q.customer_phone)}</td>
                  <td>{q.customer_phone}</td>
                  <td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{formatFirestoreTimestamp(q.date_added)}</td>
                </tr>
              ))
            ) : (
              <tr><td colSpan="6" style={{ textAlign: 'center', color: '#6b7280', padding: '24px' }}>{t('questions.empty')}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  // ============================================================
  // VIEW: FEEDBACK
  // ============================================================
  const FeedbackView = (
    <div className="rd-card">
      <div className="rd-section-header">
        <h2 className="rd-section-title">
          {t('feedbacks.title')}
          <span className="rd-badge" style={{ background: '#f5a623' }}>
            {(() => { try { return feedbacks.length; } catch { return 0; } })()}
          </span>
        </h2>
      </div>
      <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '-12px', marginBottom: '14px' }}>{t('feedbacks.description')}</p>
      <div className="rd-filter-bar" style={{ marginBottom: '12px' }}>
        <input type="text" placeholder={t('feedbacks.searchPlaceholder')} value={searchFeedbackPhone} onChange={(e) => setSearchFeedbackPhone(e.target.value)} className="rd-input" style={{ flex: '1', minWidth: '180px' }} />
        {searchFeedbackPhone && (
          <button onClick={() => setSearchFeedbackPhone('')} className="rd-btn rd-btn-danger rd-btn-sm">{t('feedbacks.clearSearch')}</button>
        )}
      </div>
      <div className="rd-table-wrap rd-table-scroll" ref={feedbacksScrollRef}>
        <table className="rd-table">
          <thead>
            <tr>
              <th>{t('feedbacks.table.chatMode')}</th>
              <th>{t('feedbacks.table.date')}</th>
              <th>{t('feedbacks.table.status')}</th>
              <th style={{ width: '30%' }}>{t('feedbacks.table.comment')}</th>
              <th>{t('feedbacks.table.customerPhone')}</th>
              <th>{t('feedbacks.table.brandName')}</th>
            </tr>
          </thead>
          <tbody>
            {loadingFeedbacks ? (
              <tr><td colSpan="6" style={{ textAlign: 'center', color: '#6b7280', padding: '24px' }}>{t('feedbacks.loading')}</td></tr>
            ) : (() => {
              const filteredFeedbacks = feedbacks;
              return filteredFeedbacks.length > 0 ? filteredFeedbacks.map((feedback) => (
                <tr key={feedback.id} style={chatStyles.clickableRow} onClick={() => handleOpenFeedbackModal(feedback)} onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(245,166,35,0.04)'} onMouseLeave={(e) => e.currentTarget.style.background = ''}>
                  <td>
                    <button onClick={(e) => handleToggleNonAiChat(feedback.customer_phone, 'shopify', e)} disabled={togglingChat[feedback.customer_phone]} className={`rd-btn rd-btn-sm${isNonAiChat(feedback.customer_phone) ? ' rd-btn-danger' : ' rd-btn-success'}`}>
                      {togglingChat[feedback.customer_phone] ? '...' : isNonAiChat(feedback.customer_phone) ? t('dashboard.nonAi.toggle.ai') : t('dashboard.nonAi.toggle.manual')}
                    </button>
                  </td>
                  <td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{(() => {
                    // 🔥 (تصليح ما بعد الهجرة) `feedback.timestamp?.toDate` كان دايمًا
                    // false بعد الهجرة (الحقل بقى ISO string عادي في `feedbacks_api`،
                    // مؤكّد حيًّا) فكانت الخانة بتفضل "-" دايمًا. بيستخدم الهيلبر
                    // المشترك دلوقتي بدل افتراض شكل Firestore Timestamp.
                    const feedbackDate = parseFlexibleTimestamp(feedback.timestamp);
                    return feedbackDate
                      ? feedbackDate.toLocaleString(lang === 'ar' ? 'ar-EG' : 'en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                      : '-';
                  })()}</td>
                  <td>
                    <span className={`rd-pill${feedback.status === 'Issue ⚠️' ? ' rd-pill-cancelled' : ' rd-pill-confirmed'}`}>{feedback.status || '-'}</span>
                  </td>
                  <td style={{ fontSize: '13px' }}>{feedback.comment || '-'}</td>
                  <td>{feedback.customer_phone}</td>
                  <td style={{ color: '#3fa9f5' }}>{feedback.brand_name}</td>
                </tr>
              )) : (
                <tr><td colSpan="6" style={{ textAlign: 'center', color: '#6b7280', padding: '24px' }}>{searchFeedbackPhone ? t('feedbacks.emptySearch') : t('feedbacks.empty')}</td></tr>
              );
            })()}
          </tbody>
        </table>

        {hasMoreFeedbacks && !loadingFeedbacks && (
          <>
            {loadingMoreFeedbacks && <div className="rd-skeleton-rows" aria-live="polite"><div className="rd-skeleton-row" /><div className="rd-skeleton-row" /></div>}
            <div ref={feedbacksSentinelRef} style={{ height: 1 }} aria-hidden="true" />
          </>
        )}
      </div>
    </div>
  );

  // ============================================================
  // VIEW: KNOWLEDGE BASE
  // ============================================================
  const KnowledgeView = (
    <div className="rd-card">
      <div className="rd-section-header">
        <h2 className="rd-section-title">
          {t('kb.title')}
          <span className="rd-badge" style={{ background: '#6c5ce7' }}>{knowledgeBase.length}</span>
        </h2>
        <button onClick={handleOpenAddModal} className="rd-btn rd-btn-primary rd-btn-sm">➕ إضافة سؤال جديد</button>
      </div>
      <div className="rd-table-wrap rd-table-scroll">
        <table className="rd-table">
          <thead>
            <tr>
              <th>{t('kb.table.action')}</th>
              <th style={{ width: '30%' }}>{t('kb.table.autoAnswer')}</th>
              <th style={{ width: '30%' }}>{t('kb.table.question')}</th>
              <th>{t('kb.table.context')}</th>
              <th>{t('kb.table.addedAt')}</th>
            </tr>
          </thead>
          <tbody>
            {loadingKnowledgeBase ? (
              <tr><td colSpan="5" style={{ textAlign: 'center', color: '#6b7280', padding: '24px' }}>{t('kb.loading')}</td></tr>
            ) : knowledgeBase.length > 0 ? (
              knowledgeBase.map((item) => (
                <tr key={item.id}>
                  <td>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button onClick={() => handleOpenEditModal(item)} className="rd-btn rd-btn-ghost rd-btn-sm">{t('kb.button.edit')}</button>
                      <button onClick={() => handleDeleteKnowledgeItem(item.id)} className="rd-btn rd-btn-danger rd-btn-sm">{t('kb.button.delete')}</button>
                    </div>
                  </td>
                  <td style={{ color: '#21c97a' }}>{item.answer_text}</td>
                  <td>{item.question_text}</td>
                  <td><span className="rd-pill rd-pill-pending">{item.product_context || t('kb.context.default')}</span></td>
                  <td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{formatFirestoreTimestamp(item.date_added)}</td>
                </tr>
              ))
            ) : (
              <tr><td colSpan="5" style={{ textAlign: 'center', color: '#6b7280', padding: '24px' }}>{t('kb.empty')}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  // ============================================================
  // VIEW: SETTINGS (all toggles grouped)
  // ============================================================
  const SettingsView = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

      {/* AI & Routing */}
      <div className="rd-settings-group">
        <div className="rd-settings-group-title">🤖 AI & الردود</div>

        {/* AI Mode */}
        <div className="rd-settings-row">
          <div>
            <div className="rd-settings-label">{aiModeEnabled ? t('dashboard.aiMode.on') : t('dashboard.aiMode.off')}</div>
            <div className="rd-settings-desc">{t('dashboard.aiMode.tooltip')}</div>
          </div>
          <label className="rd-toggle">
            <input type="checkbox" checked={aiModeEnabled} onChange={handleToggleAiMode} disabled={isUpdatingAiMode} />
            <span className="rd-toggle-slider"></span>
          </label>
        </div>

        {/* Shipping Check (only if AI enabled) */}
        {aiModeEnabled && (
          <div className="rd-settings-row">
            <div>
              <div className="rd-settings-label">{shippingCheckEnabled ? t('dashboard.shippingCheck.on') : t('dashboard.shippingCheck.off')}</div>
              <div className="rd-settings-desc">فحص الشحن أثناء المحادثة</div>
            </div>
            <label className="rd-toggle">
              <input type="checkbox" checked={shippingCheckEnabled} onChange={handleToggleShippingCheck} disabled={isUpdatingShippingCheck} />
              <span className="rd-toggle-slider"></span>
            </label>
          </div>
        )}

        {/* Duplicated order note */}
        <div className="rd-settings-row">
          <div>
            <div className="rd-settings-label">{duplicatedOrderNoteEnabled ? t('dashboard.duplicatedOrderNote.short.on') : t('dashboard.duplicatedOrderNote.short.off')}</div>
            <div className="rd-settings-desc">{t('dashboard.duplicatedOrderNote.tooltip')}</div>
          </div>
          <label className="rd-toggle">
            <input type="checkbox" checked={duplicatedOrderNoteEnabled} onChange={handleToggleDuplicatedOrderNote} disabled={isUpdatingDuplicatedOrderNote} />
            <span className="rd-toggle-slider"></span>
          </label>
        </div>
      </div>

      {/* Auto-cancel */}
      <div className="rd-settings-group">
        <div className="rd-settings-group-title">⏰ الإلغاء التلقائي</div>
        <div className="rd-settings-row">
          <div>
            <div className="rd-settings-label">{autoCancelEnabled ? t('dashboard.stats.autocancel.on').replace('16', cancelTimeoutHours) : t('dashboard.stats.autocancel.off')}</div>
            <div className="rd-settings-desc">{t('dashboard.stats.autocancel.tooltip')}</div>
          </div>
          <label className="rd-toggle">
            <input type="checkbox" checked={autoCancelEnabled} onChange={handleToggleAutoCancel} disabled={isUpdatingAutoCancel} />
            <span className="rd-toggle-slider"></span>
          </label>
        </div>
        <div className="rd-settings-row" style={{ flexWrap: 'wrap', gap: '8px' }}>
          <div className="rd-settings-label">مدة الانتظار قبل الإلغاء</div>
          <div style={{ display: 'flex', gap: '6px' }}>
            {[16, 24, 72].map(h => (
              <button key={h} onClick={() => handleCancelTimeoutChange(h)} disabled={isUpdatingCancelTimeout} className={`rd-btn rd-btn-sm${cancelTimeoutHours === h ? ' rd-btn-primary' : ' rd-btn-ghost'}`}>
                {h}h
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* First message delay */}
      <div className="rd-settings-group">
        <div className="rd-settings-group-title">⏱ توقيت أول رسالة</div>
        <div className="rd-settings-row">
          <div className="rd-settings-label">{t('dashboard.firstMsgDelay.button')}: {formatDelayLabel(firstMessageDelayHours)}</div>
          <select
            value={firstMessageDelayHours}
            onChange={(e) => handleFirstMsgDelayChange(parseFloat(e.target.value))}
            disabled={isUpdatingFirstMsgDelay}
            className="rd-input"
            style={{ minWidth: '160px' }}
          >
            {FIRST_MSG_DELAY_OPTIONS.map(opt => (
              <option key={opt} value={opt}>{formatDelayLabel(opt)}</option>
            ))}
          </select>
        </div>
        <p style={{ fontSize: '12px', color: '#6b7280', margin: '8px 0 0 0' }}>{t('dashboard.firstMsgDelay.tooltip')}</p>
      </div>

      {/* Expected delivery date */}
      <div className="rd-settings-group">
        <div className="rd-settings-group-title">📅 موعد التسليم المتوقع</div>
        <div className="rd-settings-row">
          <div>
            <div className="rd-settings-label">{deliveryDateEnabled ? t('dashboard.deliveryDate.toggle.on') : t('dashboard.deliveryDate.toggle.off')}</div>
            <div className="rd-settings-desc">{t('dashboard.deliveryDate.tooltip')}</div>
          </div>
          <label className="rd-toggle">
            <input type="checkbox" checked={deliveryDateEnabled} onChange={handleToggleDeliveryDate} disabled={isUpdatingDeliveryDate} />
            <span className="rd-toggle-slider"></span>
          </label>
        </div>
        {deliveryDateEnabled && (
          <div className="rd-settings-row">
            <div className="rd-settings-label">{t('dashboard.deliveryDate.daysLabel')}</div>
            <select
              value={deliveryAverageDays}
              disabled={isUpdatingDeliveryDays}
              onChange={(e) => { const val = parseInt(e.target.value, 10); if (!isNaN(val) && val >= 1) { setDeliveryAverageDays(val); handleDeliveryDaysChange(val); } }}
              className="rd-input"
              style={{ minWidth: '120px' }}
            >
              {Array.from({ length: 30 }, (_, i) => i + 1).map(n => {
                const label = lang === 'ar' ? (n === 1 ? 'يوم' : n === 2 ? 'يومين' : n <= 10 ? `${n} أيام` : `${n} يوم`) : (n === 1 ? '1 day' : `${n} days`);
                return <option key={n} value={n}>{label}</option>;
              })}
            </select>
          </div>
        )}
      </div>

      {/* Feedback */}
      <div className="rd-settings-group">
        <div className="rd-settings-group-title">⭐ التقييمات</div>
        <div className="rd-settings-row">
          <div>
            <div className="rd-settings-label">{feedbackEnabled ? t('dashboard.feedback.toggle.on') : t('dashboard.feedback.toggle.off')}</div>
            <div className="rd-settings-desc">{clientInfo?.platform === 'shopify' ? t('dashboard.feedback.when.shopify') : t('dashboard.feedback.when.easyorder')}</div>
          </div>
          <label className="rd-toggle">
            <input type="checkbox" checked={feedbackEnabled} onChange={handleToggleFeedback} disabled={isUpdatingFeedback} />
            <span className="rd-toggle-slider"></span>
          </label>
        </div>
      </div>

      {/* Client info — inline form (no modal) */}
      <div className="rd-settings-group">
        <div className="rd-settings-group-title">👤 {t('clientInfo.title')}</div>

        {/* Read-only info */}
        <div className="rd-settings-row">
          <div className="rd-settings-label">{t('clientInfo.brandName')}</div>
          <span style={{ fontSize: '14px', color: '#6c5ce7', fontWeight: 600 }}>{clientInfo.brand_name}</span>
        </div>
        <div className="rd-settings-row">
          <div className="rd-settings-label">{t('clientInfo.email')}</div>
          <span style={{ fontSize: '13px', color: '#6b7280' }}>{clientInfo.email || '—'}</span>
        </div>
        <div className="rd-settings-row">
          <div className="rd-settings-label">{t('clientInfo.platform')}</div>
          <span style={{ fontSize: '13px', color: '#6b7280' }}>
            {clientInfo.platform === 'shopify' ? t('clientInfo.platform.shopify') : clientInfo.platform === 'easyorder' ? t('clientInfo.platform.easyorder') : clientInfo.platform || t('clientInfo.platform.default')}
          </span>
        </div>
        <div className="rd-settings-row">
          <div className="rd-settings-label">{t('clientInfo.shopDomain')}</div>
          <span style={{ fontSize: '13px', color: '#6b7280' }}>{clientInfo.shopify_domain || clientInfo.easyOrder_domain || t('clientInfo.shopDomain.fallback')}</span>
        </div>

        {/* Editable: Telegram ID */}
        <div className="rd-settings-row" style={{ flexWrap: 'wrap', alignItems: 'flex-start', flexDirection: 'column', gap: '8px' }}>
          <div className="rd-settings-label" style={{ color: '#3fa9f5' }}>📱 Telegram</div>

          {/* Magic link — الطريقة الأسهل */}
          <button
            onClick={handleGetTelegramLink}
            disabled={isLoadingTelegramLink}
            className="rd-btn rd-btn-primary"
            style={{ width: '100%', background: '#0088cc', fontSize: '13px' }}
          >
            {isLoadingTelegramLink
              ? '⏳ جاري الفتح...'
              : telegramLinkUrl
                ? '✈️ فتح البوت مرة تانية'
                : '✈️ ربط تليجرام (الأسهل)'}
          </button>
          {telegramLinkUrl && (
            <div style={{ fontSize: '11px', color: '#16a34a', textAlign: 'center', width: '100%' }}>
              🚀 اضغط ✅ في البوت عشان تكمّل الربط
            </div>
          )}
          {clientInfo?.brand_owner_telegram_ID && (
            <div style={{ fontSize: '11px', color: '#22c55e' }}>
              ✅ مربوط ({clientInfo.brand_owner_telegram_ID.split(',').filter(Boolean).length} شات)
            </div>
          )}

          {/* أو: إدخال يدوي */}
          <div className="rd-settings-label" style={{ color: '#9ca3af', fontSize: '11px', marginTop: '4px' }}>أو أدخل الـ ID يدوياً:</div>
          <input
            type="text"
            value={editingTelegram}
            onChange={(e) => setEditingTelegram(e.target.value.replace(/\D/g, ''))}
            placeholder={t('clientInfo.telegram.placeholder')}
            className="rd-input"
            style={{ width: '100%' }}
            disabled={isSavingInfo}
          />
          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            {t('clientInfo.telegram.getIdHint')}{' '}
            <a href="https://t.me/Get_myidrobot" target="_blank" rel="noopener noreferrer" style={{ color: '#3fa9f5', fontWeight: 'bold' }}>@get_id_bot</a>
          </div>
        </div>

        {/* Editable: Facebook URL */}
        <div className="rd-settings-row" style={{ flexWrap: 'wrap', alignItems: 'flex-start', flexDirection: 'column', gap: '8px' }}>
          <div className="rd-settings-label" style={{ color: '#3fa9f5' }}>📘 Facebook URL</div>
          <input
            type="text"
            value={editingFacebook}
            onChange={(e) => setEditingFacebook(e.target.value)}
            placeholder={t('clientInfo.facebook.placeholder')}
            className="rd-input"
            style={{ width: '100%' }}
            disabled={isSavingInfo}
          />
        </div>

        {/* Editable: Instagram URL */}
        <div className="rd-settings-row" style={{ flexWrap: 'wrap', alignItems: 'flex-start', flexDirection: 'column', gap: '8px' }}>
          <div className="rd-settings-label" style={{ color: '#3fa9f5' }}>📸 Instagram URL</div>
          <input
            type="text"
            value={editingInstagram}
            onChange={(e) => setEditingInstagram(e.target.value)}
            placeholder={t('clientInfo.instagram.placeholder')}
            className="rd-input"
            style={{ width: '100%' }}
            disabled={isSavingInfo}
          />
        </div>

        {/* Editable: Message Language */}
        <div className="rd-settings-row" style={{ flexWrap: 'wrap', alignItems: 'flex-start', flexDirection: 'column', gap: '8px' }}>
          <div className="rd-settings-label" style={{ color: '#f5a623' }}>💬 {t('onboarding.messageLanguage')}</div>
          <div style={{ display: 'flex', gap: '10px', width: '100%' }}>
            {['ar', 'en'].map(lng => (
              <label key={lng} style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px', padding: '10px', background: editingMessageLanguage === lng ? 'rgba(108,92,231,0.10)' : '#ffffff', border: editingMessageLanguage === lng ? '2px solid #6c5ce7' : '1px solid #eceef3', borderRadius: '8px', cursor: isSavingInfo ? 'not-allowed' : 'pointer', fontWeight: editingMessageLanguage === lng ? 'bold' : 'normal' }}>
                <input type="radio" name="settingsMsgLang" value={lng} checked={editingMessageLanguage === lng} onChange={() => setEditingMessageLanguage(lng)} disabled={isSavingInfo} />
                {lng === 'ar' ? 'AR - عربي' : 'EN - English'}
              </label>
            ))}
          </div>
        </div>

        {/* Save button */}
        <div style={{ paddingTop: '16px' }}>
          <button
            onClick={handleSaveInfo}
            className="rd-btn rd-btn-primary"
            disabled={isSavingInfo}
            style={{ opacity: isSavingInfo ? 0.6 : 1, minWidth: '140px' }}
          >
            {isSavingInfo ? t('clientInfo.saving') : t('clientInfo.save')}
          </button>
        </div>
      </div>

    </div>
  );

  // ============================================================
  // VIEW: BILLING
  // ============================================================
  const BillingView = (
    <div className="rd-card" style={{ textAlign: 'center', padding: '48px 24px' }}>
      <div style={{ fontSize: '48px', marginBottom: '16px' }}>💳</div>
      <h2 style={{ color: '#6c5ce7', marginBottom: '8px' }}>{t('dashboard.balance')}: ${(clientInfo.balance || 0).toFixed(2)}</h2>
      <p style={{ color: '#6b7280', marginBottom: '24px' }}>≈ {((clientInfo.balance || 0) * 50).toFixed(0)} {currencyLabel}</p>
      {isBillingManagedByDafOne ? (
        <div
          style={{ display: 'inline-block', padding: '14px 24px', borderRadius: '10px', background: 'rgba(108,92,231,0.08)', color: '#6c5ce7', fontSize: '15px', fontWeight: 600 }}
        >
          🔗 {t('dashboard.billing.managedByDafOne')}
        </div>
      ) : (
        <button
          onClick={() => navigate('/billing', { state: { clientInfo } })}
          className="rd-btn rd-btn-primary"
          style={{ fontSize: '16px', padding: '12px 32px' }}
        >
          💳 {t('dashboard.buyCredits')}
        </button>
      )}
    </div>
  );

  // IntegrationsView removed — was never rendered (dead code)

  // ============================================================
  // MODALS (shared across views)
  // ============================================================
  const ModalsJSX = (
    <>
      {/* Chat modal */}
      <ChatHistoryModal
        order={selectedOrderForChat}
        history={chatHistory}
        loading={loadingChat}
        onClose={handleCloseChatModal}
        isManualMode={selectedOrderForChat && nonAiChatsLoaded && !nonAiChatsError && isNonAiChat(selectedOrderForChat.customer_phone)}
        onSwitchToManual={handleToggleNonAiChat}
        isSwitchingMode={selectedOrderForChat && !!togglingChat[selectedOrderForChat.customer_phone]}
        isModeReady={nonAiChatsLoaded && !nonAiChatsError}
        modeUnavailable={nonAiChatsError}
        messageInput={messageInput}
        setMessageInput={setMessageInput}
        onSendMessage={handleSendMessage}
        isSending={isSendingMessage}
      />

      {/* Feedback modal */}
      <FeedbackModal
        feedback={selectedFeedback}
        history={feedbackChatHistory}
        loading={loadingFeedbackChat}
        onClose={handleCloseFeedbackModal}
        isManualMode={selectedFeedback && nonAiChatsLoaded && !nonAiChatsError && isNonAiChat(selectedFeedback.customer_phone)}
        onSwitchToManual={handleToggleNonAiChat}
        isSwitchingMode={selectedFeedback && !!togglingChat[selectedFeedback.customer_phone]}
        isModeReady={nonAiChatsLoaded && !nonAiChatsError}
        modeUnavailable={nonAiChatsError}
        messageInput={messageInput}
        setMessageInput={setMessageInput}
        onSendMessage={handleSendFeedbackMessage}
        isSending={isSendingMessage}
      />

      {/* Bosta modal */}
      <FeedbackModal
        feedback={selectedBosta}
        history={bostaChatHistory}
        loading={loadingBostaChat}
        onClose={handleCloseBostaModal}
        isManualMode={selectedBosta && nonAiChatsLoaded && !nonAiChatsError && isNonAiChat(selectedBosta.customer_phone)}
        onSwitchToManual={handleToggleNonAiChat}
        isSwitchingMode={selectedBosta && !!togglingChat[selectedBosta.customer_phone]}
        isModeReady={nonAiChatsLoaded && !nonAiChatsError}
        modeUnavailable={nonAiChatsError}
        messageInput={messageInput}
        setMessageInput={setMessageInput}
        onSendMessage={handleSendBostaMessage}
        isSending={isSendingMessage}
      />

      {/* Question reply modal */}
      {selectedQuestion && (
        <div className="rd-modal-backdrop">
          <div style={{ background: '#fff', padding: '28px', borderRadius: '16px', width: '90%', maxWidth: '560px', textAlign: 'right', boxShadow: '0 12px 40px rgba(27,31,42,0.15)', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ color: '#6c5ce7', marginTop: 0 }}>{t('questions.modal.answerTitle')}</h3>
            <div style={{ background: '#f4f3fb', padding: '14px', borderRadius: '10px', border: '1px solid #eceef3', marginBottom: '16px' }}>
              <strong style={{ color: '#6b7280', fontSize: '13px' }}>{t('questions.modal.customerQuestion')}</strong>
              <p style={{ marginTop: '5px', color: '#1b1f2a', margin: '5px 0 0 0' }}>{selectedQuestion.whatsapp_message}</p>
              {selectedQuestion.product_context && (
                <div style={{ borderTop: '1px solid #eceef3', paddingTop: '10px', marginTop: '10px' }}>
                  <strong style={{ color: '#6c5ce7', fontSize: '13px' }}>{t('questions.modal.productContext')}</strong>
                  <p style={{ marginTop: '5px', color: '#f5a623', background: 'rgba(245,166,35,0.1)', padding: '5px 8px', borderRadius: '6px', fontSize: '14px', margin: '5px 0 0 0' }}>{selectedQuestion.product_context}</p>
                </div>
              )}
            </div>
            <textarea className="rd-input" style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', minHeight: '100px', fontSize: '15px' }} placeholder={t('questions.modal.placeholder')} rows="5" value={answerText} onChange={(e) => setAnswerText(e.target.value)} disabled={isSubmitting} onKeyDown={(e) => handleTextareaEnterPress(e, 'answer')} />
            <div style={{ display: 'flex', alignItems: 'center', marginTop: '12px', gap: '8px' }}>
              <input type="checkbox" id="saveToKB" style={{ width: '17px', height: '17px', cursor: 'pointer', accentColor: '#6c5ce7' }} checked={saveToKnowledgeBase} onChange={(e) => setSaveToKnowledgeBase(e.target.checked)} disabled={isSubmitting} />
              <label htmlFor="saveToKB" style={{ cursor: 'pointer', fontSize: '14px', color: '#1b1f2a' }}>{t('questions.modal.saveToKb')}</label>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px', gap: '10px' }}>
              <button onClick={handleCloseReplyModal} className="rd-btn rd-btn-ghost" disabled={isSubmitting}>{t('questions.modal.cancel')}</button>
              <button onClick={handleSubmitAnswer} className="rd-btn rd-btn-primary" disabled={isSubmitting || !answerText.trim()}>{isSubmitting ? t('questions.modal.sending') : t('questions.modal.send')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit knowledge base modal */}
      {editingItem && (
        <div className="rd-modal-backdrop">
          <div style={{ background: '#fff', padding: '28px', borderRadius: '16px', width: '90%', maxWidth: '600px', textAlign: 'right', boxShadow: '0 12px 40px rgba(27,31,42,0.15)', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ color: '#6c5ce7', marginTop: 0 }}>✏️ تعديل السؤال والإجابة</h3>
            <label htmlFor="editQuestion" style={{ display: 'block', marginBottom: '6px', fontWeight: 700, color: '#f5a623', fontSize: '14px' }}>السؤال <span style={{ color: '#ff5a6e' }}>*</span></label>
            <input id="editQuestion" type="text" className="rd-input" style={{ width: '100%', marginBottom: '14px', boxSizing: 'border-box', fontSize: '15px' }} placeholder="مثال: هل المنتج متوفر؟" value={editedQuestion} onChange={(e) => setEditedQuestion(e.target.value)} disabled={isSubmittingEdit} />
            <label htmlFor="editAnswer" style={{ display: 'block', marginBottom: '6px', fontWeight: 700, color: '#21c97a', fontSize: '14px' }}>الإجابة <span style={{ color: '#ff5a6e' }}>*</span></label>
            <textarea id="editAnswer" className="rd-input" style={{ width: '100%', marginBottom: '14px', boxSizing: 'border-box', fontSize: '15px', resize: 'vertical', minHeight: '90px' }} placeholder="مثال: نعم المنتج متوفر" rows="4" value={editedAnswer} onChange={(e) => setEditedAnswer(e.target.value)} disabled={isSubmittingEdit} onKeyDown={(e) => handleTextareaEnterPress(e, 'edit')} />
            <label htmlFor="editProduct" style={{ display: 'block', marginBottom: '6px', fontWeight: 700, color: '#6c5ce7', fontSize: '14px' }}>المنتج (اختياري)</label>
            <input id="editProduct" type="text" className="rd-input" style={{ width: '100%', marginBottom: '6px', boxSizing: 'border-box', fontSize: '15px' }} placeholder="مثال: Example Jeans - 28" value={editedProduct} onChange={(e) => setEditedProduct(e.target.value)} disabled={isSubmittingEdit} />
            <small style={{ display: 'block', color: '#9ca3af', fontSize: '12px', marginBottom: '20px', fontStyle: 'italic' }}>💡 إذا تركت هذا الحقل فارغاً، سيطبق السؤال على جميع المنتجات</small>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
              <button onClick={handleCloseEditModal} className="rd-btn rd-btn-ghost" disabled={isSubmittingEdit}>إلغاء</button>
              <button onClick={handleSubmitEdit} className="rd-btn rd-btn-primary" disabled={isSubmittingEdit || !editedQuestion.trim() || !editedAnswer.trim()}>{isSubmittingEdit ? 'جاري الحفظ...' : '✅ حفظ التعديلات'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Add knowledge base modal */}
      {showAddKnowledgeModal && (
        <div className="rd-modal-backdrop" onClick={handleCloseAddModal}>
          <div style={{ background: '#fff', padding: '28px', borderRadius: '16px', width: '90%', maxWidth: '600px', textAlign: 'right', boxShadow: '0 12px 40px rgba(27,31,42,0.15)', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ color: '#21c97a', marginTop: 0 }}>➕ إضافة سؤال وإجابة جديدة</h3>
            <label htmlFor="newQuestion" style={{ display: 'block', marginBottom: '6px', fontWeight: 700, color: '#f5a623', fontSize: '14px' }}>السؤال <span style={{ color: '#ff5a6e' }}>*</span></label>
            <input id="newQuestion" type="text" className="rd-input" style={{ width: '100%', marginBottom: '14px', boxSizing: 'border-box', fontSize: '15px' }} placeholder="مثال: ما هي مدة الشحن؟" value={newQuestion} onChange={(e) => setNewQuestion(e.target.value)} disabled={isSubmittingNew} />
            <label htmlFor="newAnswer" style={{ display: 'block', marginBottom: '6px', fontWeight: 700, color: '#21c97a', fontSize: '14px' }}>الإجابة <span style={{ color: '#ff5a6e' }}>*</span></label>
            <textarea id="newAnswer" className="rd-input" style={{ width: '100%', marginBottom: '14px', boxSizing: 'border-box', fontSize: '15px', resize: 'vertical', minHeight: '90px' }} placeholder="مثال: مدة الشحن من 2 إلى 5 أيام عمل" rows="4" value={newAnswer} onChange={(e) => setNewAnswer(e.target.value)} disabled={isSubmittingNew} />
            <label htmlFor="newProduct" style={{ display: 'block', marginBottom: '6px', fontWeight: 700, color: '#6c5ce7', fontSize: '14px' }}>المنتج (اختياري)</label>
            <input id="newProduct" type="text" className="rd-input" style={{ width: '100%', marginBottom: '6px', boxSizing: 'border-box', fontSize: '15px' }} placeholder="مثال: Example Jeans - 28" value={newProduct} onChange={(e) => setNewProduct(e.target.value)} disabled={isSubmittingNew} />
            <small style={{ display: 'block', color: '#9ca3af', fontSize: '12px', marginBottom: '20px', fontStyle: 'italic' }}>💡 إذا تركت هذا الحقل فارغاً، سيطبق السؤال على جميع المنتجات</small>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
              <button onClick={handleCloseAddModal} className="rd-btn rd-btn-ghost" disabled={isSubmittingNew}>إلغاء</button>
              <button onClick={handleSubmitNew} className="rd-btn rd-btn-primary" disabled={isSubmittingNew || !newQuestion.trim() || !newAnswer.trim()}>{isSubmittingNew ? 'جاري الإضافة...' : '➕ إضافة'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Client info modal (legacy path — primary path is Settings inline form) */}
      {showClientInfoModal && (
        <div className="rd-modal-backdrop" onClick={() => setShowClientInfoModal(false)}>
          <div style={{ background: '#fff', padding: '28px', borderRadius: '16px', width: '90%', maxWidth: '600px', textAlign: 'right', boxShadow: '0 12px 40px rgba(27,31,42,0.15)', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ color: '#6c5ce7', marginTop: 0 }}>{t('clientInfo.title')}</h2>
            <div style={{ marginBottom: '20px' }}>
              {[
                { label: t('clientInfo.brandName'), value: clientInfo.brand_name },
                { label: t('clientInfo.clientId'), value: clientInfo.client_id },
                { label: t('clientInfo.email'), value: clientInfo.email },
              ].map(({ label, value }) => (
                <div key={label} style={{ marginBottom: '10px', padding: '10px 14px', background: '#f4f3fb', borderRadius: '8px', border: '1px solid #eceef3' }}>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px' }}>{label}</div>
                  <div style={{ fontSize: '15px', color: '#1b1f2a', fontWeight: 600 }}>{value}</div>
                </div>
              ))}

              {/* Telegram */}
              <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 700, color: '#3fa9f5', fontSize: '14px' }}>📱 Telegram ID</label>
                <input type="text" value={editingTelegram} onChange={(e) => setEditingTelegram(e.target.value.replace(/\D/g, ''))} placeholder={t('clientInfo.telegram.placeholder')} className="rd-input" style={{ width: '100%', boxSizing: 'border-box' }} disabled={isSavingInfo} />
                <div style={{ marginTop: '5px', fontSize: '12px', color: '#6b7280' }}>{t('clientInfo.telegram.getIdHint')}{' '}<a href="https://t.me/Get_myidrobot" target="_blank" rel="noopener noreferrer" style={{ color: '#3fa9f5', fontWeight: 'bold' }}>@get_id_bot</a></div>
              </div>
              {/* Facebook */}
              <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 700, color: '#3fa9f5', fontSize: '14px' }}>📘 Facebook URL</label>
                <input type="text" value={editingFacebook} onChange={(e) => setEditingFacebook(e.target.value)} placeholder={t('clientInfo.facebook.placeholder')} className="rd-input" style={{ width: '100%', boxSizing: 'border-box' }} disabled={isSavingInfo} />
              </div>
              {/* Instagram */}
              <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 700, color: '#3fa9f5', fontSize: '14px' }}>📸 Instagram URL</label>
                <input type="text" value={editingInstagram} onChange={(e) => setEditingInstagram(e.target.value)} placeholder={t('clientInfo.instagram.placeholder')} className="rd-input" style={{ width: '100%', boxSizing: 'border-box' }} disabled={isSavingInfo} />
              </div>
              {/* Message language */}
              <div style={{ marginBottom: '10px' }}>
                <div style={{ fontWeight: 700, color: '#f5a623', fontSize: '14px', marginBottom: '8px' }}>💬 {t('onboarding.messageLanguage')}</div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  {['ar', 'en'].map(lng => (
                    <label key={lng} style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px', padding: '10px', background: editingMessageLanguage === lng ? 'rgba(108,92,231,0.10)' : '#faf8ff', border: editingMessageLanguage === lng ? '2px solid #6c5ce7' : '1px solid #eceef3', borderRadius: '8px', cursor: isSavingInfo ? 'not-allowed' : 'pointer', fontWeight: editingMessageLanguage === lng ? 700 : 400 }}>
                      <input type="radio" name="editMsgLang" value={lng} checked={editingMessageLanguage === lng} onChange={() => setEditingMessageLanguage(lng)} disabled={isSavingInfo} />
                      {lng === 'ar' ? 'AR - عربي' : 'EN - English'}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowClientInfoModal(false)} className="rd-btn rd-btn-ghost" disabled={isSavingInfo}>{t('clientInfo.close')}</button>
              <button onClick={handleSaveInfo} className="rd-btn rd-btn-primary" disabled={isSavingInfo} style={{ opacity: isSavingInfo ? 0.6 : 1 }}>{isSavingInfo ? t('clientInfo.saving') : t('clientInfo.save')}</button>
            </div>
          </div>
        </div>
      )}

      {/* 🏗️ "Finish setting up" modal — Daf One auto-provisioned merchants
          only (see needsOperationalSetup above). Closable (X) — this is a
          nudge, not a blocker; the merchant can keep using the dashboard
          normally and come back to it later via the banner. */}
      {showOperationalSetupModal && (
        <div className="rd-modal-backdrop" onClick={() => !isSavingOpSetup && setShowOperationalSetupModal(false)}>
          <div style={{ background: '#fff', padding: '28px', borderRadius: '16px', width: '90%', maxWidth: '480px', textAlign: 'right', boxShadow: '0 12px 40px rgba(27,31,42,0.15)', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <h2 style={{ color: '#6c5ce7', marginTop: 0, marginBottom: '4px' }}>{t('operationalSetup.modal.title')}</h2>
              <button
                type="button"
                onClick={() => setShowOperationalSetupModal(false)}
                disabled={isSavingOpSetup}
                aria-label={t('operationalSetup.modal.close')}
                style={{ background: 'transparent', border: 'none', fontSize: '20px', cursor: isSavingOpSetup ? 'not-allowed' : 'pointer', color: '#9ca3af', lineHeight: 1 }}
              >
                ✕
              </button>
            </div>
            <p style={{ color: '#6b7280', fontSize: '14px', marginTop: 0, marginBottom: '20px' }}>{t('operationalSetup.modal.subtitle')}</p>

            {/* Telegram (required) */}
            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 700, color: '#3fa9f5', fontSize: '14px' }}>
                📱 {t('operationalSetup.modal.telegramRequired')} <span style={{ color: '#ff5a6e' }}>*</span>
              </label>
              <input
                type="text"
                value={opSetupTelegram}
                onChange={(e) => setOpSetupTelegram(e.target.value.replace(/\D/g, ''))}
                placeholder={t('clientInfo.telegram.placeholder')}
                className="rd-input"
                style={{ width: '100%', boxSizing: 'border-box' }}
                disabled={isSavingOpSetup}
              />
              <div style={{ marginTop: '5px', fontSize: '12px', color: '#6b7280' }}>
                {t('clientInfo.telegram.getIdHint')}{' '}
                <a href="https://t.me/Get_myidrobot" target="_blank" rel="noopener noreferrer" style={{ color: '#3fa9f5', fontWeight: 'bold' }}>@get_id_bot</a>
              </div>
            </div>

            {/* Facebook (optional) */}
            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 700, color: '#3fa9f5', fontSize: '14px' }}>📘 {t('onboarding.facebook')}</label>
              <input
                type="text"
                value={opSetupFacebook}
                onChange={(e) => setOpSetupFacebook(e.target.value)}
                placeholder={t('clientInfo.facebook.placeholder')}
                className="rd-input"
                style={{ width: '100%', boxSizing: 'border-box' }}
                disabled={isSavingOpSetup}
              />
            </div>

            {/* Instagram (optional) */}
            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 700, color: '#3fa9f5', fontSize: '14px' }}>📸 {t('onboarding.instagram')}</label>
              <input
                type="text"
                value={opSetupInstagram}
                onChange={(e) => setOpSetupInstagram(e.target.value)}
                placeholder={t('clientInfo.instagram.placeholder')}
                className="rd-input"
                style={{ width: '100%', boxSizing: 'border-box' }}
                disabled={isSavingOpSetup}
              />
            </div>

            {/* Message language (optional, defaults to 'ar') */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontWeight: 700, color: '#f5a623', fontSize: '14px', marginBottom: '4px' }}>💬 {t('operationalSetup.modal.messageLanguage')}</div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>{t('operationalSetup.modal.messageLanguage.hint')}</div>
              <div style={{ display: 'flex', gap: '10px' }}>
                {['ar', 'en'].map(lng => (
                  <label key={lng} style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px', padding: '10px', background: opSetupMessageLanguage === lng ? 'rgba(108,92,231,0.10)' : '#faf8ff', border: opSetupMessageLanguage === lng ? '2px solid #6c5ce7' : '1px solid #eceef3', borderRadius: '8px', cursor: isSavingOpSetup ? 'not-allowed' : 'pointer', fontWeight: opSetupMessageLanguage === lng ? 700 : 400 }}>
                    <input type="radio" name="opSetupMsgLang" value={lng} checked={opSetupMessageLanguage === lng} onChange={() => setOpSetupMessageLanguage(lng)} disabled={isSavingOpSetup} />
                    {lng === 'ar' ? 'AR - عربي' : 'EN - English'}
                  </label>
                ))}
              </div>
            </div>

            {opSetupError && (
              <p style={{ color: '#ff5a6e', fontSize: '13px', marginBottom: '14px' }}>⚠️ {opSetupError}</p>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowOperationalSetupModal(false)} className="rd-btn rd-btn-ghost" disabled={isSavingOpSetup}>{t('operationalSetup.modal.close')}</button>
              <button onClick={handleSaveOperationalSetup} className="rd-btn rd-btn-primary" disabled={isSavingOpSetup} style={{ opacity: isSavingOpSetup ? 0.6 : 1 }}>
                {isSavingOpSetup ? t('operationalSetup.modal.saving') : t('operationalSetup.modal.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  // ============================================================
  // RENDER: shell wrapping all views
  // ============================================================
  return (
    <div className="rd-root">
      {SidebarJSX}
      <main className="rd-main">
        {TopBarJSX}
        <div className="rd-canvas">
          {/* 🏗️ "Finish setting up" banner — Daf One auto-provisioned
              merchants only, see needsOperationalSetup above. Non-blocking:
              sits above the active view, the merchant can keep using
              everything below it normally. */}
          {needsOperationalSetup && (
            <div
              style={{
                display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between',
                gap: '12px', marginBottom: '20px', padding: '16px 20px', borderRadius: '12px',
                background: 'rgba(245,166,35,0.10)', border: '1px solid rgba(245,166,35,0.35)',
              }}
            >
              <div>
                <div style={{ fontWeight: 700, color: '#b8860b', fontSize: '15px', marginBottom: '2px' }}>
                  🚀 {t('operationalSetup.banner.title')}
                </div>
                <div style={{ color: '#6b7280', fontSize: '13px' }}>{t('operationalSetup.banner.body')}</div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setOpSetupTelegram(String(clientInfo?.brand_owner_telegram_ID || ''));
                  setOpSetupFacebook(String(clientInfo?.facebook_url || ''));
                  setOpSetupInstagram(String(clientInfo?.instegram_url || ''));
                  setOpSetupMessageLanguage(clientInfo?.message_language === 'en' ? 'en' : 'ar');
                  setOpSetupError('');
                  setShowOperationalSetupModal(true);
                }}
                className="rd-btn rd-btn-primary rd-btn-sm"
                style={{ whiteSpace: 'nowrap' }}
              >
                {t('operationalSetup.banner.cta')}
              </button>
            </div>
          )}
          {activeView === 'overview'      && OverviewView}
          {activeView === 'orders' && <><SectionTabs items={[{key:'',ar:'الأوردرات',en:'Orders'},{key:'requests',ar:'طلبات التعديل',en:'Change requests'}]}/>{activeSection==='requests'?<AgentPage key="requests" section="requests" lang={lang}/>:OrdersView}</>}
          {activeView === 'pending'       && PendingView}
          {activeView === 'conversations' && ConversationsView}
          {activeView === 'questions'     && QuestionsView}
          {activeView === 'products' && <AgentPage key="catalog" section="catalog" lang={lang}/>}
          {activeView === 'offers' && <AgentPage key="offers" section="offers" lang={lang}/>}
          {activeView === 'feedback'      && FeedbackView}
          {activeView === 'knowledge' && <><SectionTabs items={[{key:'',ar:'الأسئلة والإجابات',en:'Questions and answers'},{key:'memory',ar:'ذاكرة البراند',en:'Brand memory'}]}/>{activeSection==='memory'?<AgentPage key="learning" section="learning" lang={lang}/>:KnowledgeView}</>}
          {activeView === 'settings' && <><SectionTabs items={[{key:'',ar:'الإعدادات العامة',en:'General settings'},{key:'agent',ar:'إعدادات الايجنت',en:'Agent settings'}]}/>{activeSection==='agent'?<AgentPage key="settings" section="settings" lang={lang}/>:SettingsView}</>}
          {activeView === 'billing'       && BillingView}
        </div>
      </main>
      {ModalsJSX}
    </div>
  );
}

// ==========================================================
// الكومبوننت الأساسية
// 🔥 ==========================================================
// 🔒 Wave 3b security fix: مفيش أي props هوية بتتقرأ من الـURL هنا خالص
// (viewingClientUid/isSuperAdminView/shopifyShop/platform اتشالوا بالكامل —
// كانوا هما بالظبط ثغرة uid-in-URL). البراند الوحيد اللي بيتجاب هو براند
// الجلسة الحالية، مش أي uid تاني.
function ClientDashboard({ user }) {
  const { t, lang } = useI18n();
  const [clientInfo, setClientInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleLogout = useCallback(() => {
    signOut().then(() => {
      navigate('/');
    }).catch((error) => {
      console.error('Logout Error:', error);
    });
  }, [navigate]);

  // 🔒 subscribeMyBrand() بيحل البراند حصريًا من auth.uid() جوّه Postgres
  // (RPC get_my_brand_settings، SECURITY DEFINER) — صفر uid/admin/shop
  // بيتقرأ من الـURL هنا. لو مفيش جلسة/مفيش صف مطابق، البراند بيرجع null
  // ونعمل logout (نفس سلوك الفرع "العادي" القديم — فروع Shopify/super-admin
  // القديمة اتشالت لأنها كانت أساس الثغرة).
  useEffect(() => {
    const unsubscribe = subscribeMyBrand(({ brand, error }) => {
      if (error) {
        console.error("Error fetching client data:", error);
        setError('فشل تحميل بيانات العميل');
        setLoading(false);
        return;
      }

      if (!brand) {
        console.error('Client data not found!');
        setError('بيانات العميل غير موجودة');
        handleLogout();
      } else {
        setClientInfo(brand);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [handleLogout]);


  if (loading) {
    return <div style={loadingScreenStyles.container}>جاري تحميل بيانات البراند...</div>;
  }

  if (error) {
    return (
      <div style={loadingScreenStyles.error}>
        {error}
        <button onClick={handleLogout} style={{...styles.logoutButton, marginTop: '20px'}}>تسجيل الخروج</button>
      </div>
    );
  }

  if (!clientInfo) {
    return <div style={loadingScreenStyles.container}>لا يوجد بيانات...</div>;
  }

  // 🛑 WebhookSetupPage: EasyOrder ONLY (Shopify gets webhook automatically from OAuth)
  // ✅ Wave 3b cleanup (2026-07-05): كانت هنا فجوة موثّقة — `easyorder_shared_secret`
  // سرّ مستبعد عمدًا من whitelist get_my_brand_settings() (daf_confirmation_rls.sql)،
  // فـ `clientInfo.easyorder_shared_secret` كانت تفضل `undefined` دايمًا حتى لو
  // التاجر ضبط السرّ فعليًا (loop لا نهائي لصفحة WebhookSetupPage). اتصلحت بإضافة
  // بوليان وجود-فقط `has_easyorder_shared_secret` (يترجع من الـRPC، أبدًا القيمة
  // الخام) — نفس منطق الشرط القديم بالظبط (نفس السلوك للبراندات القديمة اللي
  // مفيش عندها الحقل خالص → `undefined`/`false` → لسه بيحتاج إعداد).
  const needsWebhookSetup = clientInfo.platform === 'easyorder' && !clientInfo.has_easyorder_shared_secret;

  if (needsWebhookSetup) {
    return (
      <WebhookSetupPage 
        user={user} 
        clientData={clientInfo} 
        setClientData={setClientInfo} 
      />
    );
  }

  return <RealDashboard user={user} clientInfo={clientInfo} handleLogout={handleLogout} />;
}


// === الستايلات (light theme) ===
const styles = {
  dashboardContainer: { padding: '24px', backgroundColor: '#f4f3fb', color: '#1b1f2a', minHeight: '100vh', },
  topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '16px' },
  topBarActions: { display: 'flex', alignItems: 'center', gap: '10px' },
  langButton: { background: '#ffffff', color: '#6b7280', border: '1px solid #eceef3', padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px', transition: 'background 0.15s', },
  logoutButton: { background: '#ffffff', color: '#6b7280', border: '1px solid #eceef3', padding: '8px 14px', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, transition: 'background 0.15s', },
    topInfoContainer: { display: 'flex', alignItems: 'stretch', gap: '16px', margin: '20px 0', },
    infoBox: {
      background: '#ffffff',
      padding: '20px',
      borderRadius: '12px',
      border: '1px solid #eceef3',
      textAlign: 'right',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      flexGrow: 1,
      boxShadow: '0 2px 8px rgba(27,31,42,0.05)',
      transition: 'transform 0.2s ease-out',
    },
    savedBox: { background: 'rgba(0,194,203,0.06)', borderColor: '#00c2cb', color: '#1b1f2a', },
    waStatusInline: { display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '15px 0', fontSize: '18px', },
    statusLight: { width: '16px', height: '16px', borderRadius: '50%', marginLeft: '10px', flexShrink: 0, },
    reconnectButton: { background: '#6c5ce7', color: '#fff', border: 'none', padding: '10px 15px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', marginTop: '15px', transition: 'all 0.2s', },
    deleteWhatsAppButton: { background: '#ff5a6e', color: '#fff', border: 'none', padding: '10px 15px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', transition: 'all 0.2s', },
    card: { background: '#ffffff', padding: '24px', margin: '0', borderRadius: '14px', border: '1px solid #eceef3', boxShadow: '0 2px 8px rgba(27,31,42,0.05)', },
    cardContainer: { display: 'flex', flexWrap: 'wrap', gap: '16px', margin: '20px 0', },
    statCard: {
      flexGrow: 1,
      flexBasis: '200px',
      background: '#ffffff',
      border: '1px solid #eceef3',
      padding: '18px',
      borderRadius: '14px',
      textAlign: 'center',
      boxShadow: '0 2px 8px rgba(27,31,42,0.05)',
      cursor: 'pointer',
      transition: 'all 0.15s ease-in-out',
      color: '#6b7280',
    },
    statNumber: { fontSize: '2.2em', fontWeight: 700, margin: '8px 0 0 0', color: '#1b1f2a', },
    statPercentage: { fontSize: '1em', fontWeight: 600, margin: '4px 0 0 0', color: '#6b7280', },
    activeCardHighlight: { border: '2px solid #6c5ce7', boxShadow: '0 4px 16px rgba(108,92,231,0.15)', transform: 'translateY(-3px)', color: '#6c5ce7', },
    downloadButton: { background: '#21c97a', color: '#fff', padding: '8px 15px', textDecoration: 'none', borderRadius: '8px', fontWeight: 'bold', fontSize: '13px', border: 'none', },
    table: { width: '100%', borderCollapse: 'collapse', marginTop: '0', },
    th: { borderBottom: '1px solid #eceef3', padding: '10px 14px', background: '#f4f3fb', textAlign: 'right', fontSize: '12px', color: '#6b7280', fontWeight: 600, position: 'sticky', top: 0, zIndex: 1, },
    td: { borderBottom: '1px solid #f4f3fb', padding: '10px 14px', textAlign: 'right', fontSize: '13px', color: '#1b1f2a', wordBreak: 'break-word', verticalAlign: 'middle', },
    trOdd: { backgroundColor: 'transparent' },
    trEven: { backgroundColor: '#faf8ff' },
    filterContainer: { display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginBottom: '16px', padding: '12px', border: '1px solid #eceef3', borderRadius: '10px', backgroundColor: '#faf8ff', },
    filterButton: { padding: '6px 14px', border: '1px solid #eceef3', background: '#ffffff', color: '#6b7280', cursor: 'pointer', borderRadius: '100px', fontSize: '12px', fontWeight: 600, transition: 'all 0.15s', },
    activeFilterButton: { background: '#6c5ce7', color: '#fff', borderColor: '#6c5ce7' },
    filterInput: { padding: '7px 10px', border: '1px solid #eceef3', borderRadius: '8px', background: '#ffffff', color: '#1b1f2a', outline: 'none', minWidth: '130px', fontSize: '13px', },
    modalBackdrop: { position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(27,31,42,0.55)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, backdropFilter: 'blur(4px)', },
    modalContent: { background: '#ffffff', padding: '32px', borderRadius: '16px', textAlign: 'center', boxShadow: '0 12px 40px rgba(27,31,42,0.15)', color: '#1b1f2a', border: '1px solid #eceef3', maxHeight: '90vh', overflowY: 'auto', },
    closeModalButton: { background: '#ff5a6e', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', marginTop: '20px', fontSize: '14px', fontWeight: 'bold', transition: 'opacity 0.2s', },
    questionCountBadge: {
      backgroundColor: '#6c5ce7',
      color: '#fff',
      padding: '2px 9px',
      borderRadius: '100px',
      fontSize: '11px',
      fontWeight: 700,
      marginRight: '10px'
    },
    replyButton: {
      background: '#6c5ce7',
      color: '#fff',
      border: 'none',
      padding: '7px 12px',
      borderRadius: '7px',
      cursor: 'pointer',
      fontWeight: 600,
      fontSize: '13px',
      transition: 'opacity 0.15s',
    },
    deleteButton: {
      background: '#fff0f1',
      color: '#ff5a6e',
      border: '1px solid #ff5a6e',
      padding: '7px 12px',
      borderRadius: '7px',
      cursor: 'pointer',
      fontWeight: 600,
      fontSize: '13px',
      transition: 'background 0.15s',
    },
    editButton: {
      background: '#fffbeb',
      color: '#f5a623',
      border: '1px solid #f5a623',
      padding: '7px 12px',
      borderRadius: '7px',
      cursor: 'pointer',
      fontWeight: 600,
      fontSize: '13px',
      transition: 'background 0.15s',
    },
    replyModalContent: {
      textAlign: 'right',
      width: '90%',
      maxWidth: '600px',
    },
    questionBox: {
      background: '#f4f3fb',
      padding: '14px',
      borderRadius: '10px',
      border: '1px solid #eceef3',
      marginBottom: '16px',
      textAlign: 'right',
    },
    replyTextarea: {
      width: '100%',
      padding: '10px',
      borderRadius: '8px',
      border: '1px solid #eceef3',
      background: '#f4f3fb',
      color: '#1b1f2a',
      fontSize: '15px',
      fontFamily: 'inherit',
      boxSizing: 'border-box',
      resize: 'vertical',
      outline: 'none',
    },
    checkboxContainer: {
      display: 'flex',
      alignItems: 'center',
      marginTop: '16px',
      textAlign: 'right',
    },
    checkboxInput: {
      width: '17px',
      height: '17px',
      marginRight: '8px',
      cursor: 'pointer',
      accentColor: '#6c5ce7',
    },
    checkboxLabel: {
      cursor: 'pointer',
      fontSize: '14px',
      color: '#1b1f2a',
    },
    modalActions: {
      display: 'flex',
      justifyContent: 'space-between',
      marginTop: '24px',
      gap: '10px',
    },
    autoCancelToggleContainer: {
      marginTop: '12px',
      paddingTop: '12px',
      borderTop: '1px solid #eceef3',
    },
    autoCancelToggle: {
      width: '100%',
      padding: '9px 14px',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontWeight: 600,
      fontSize: '12px',
      color: '#ffffff',
      transition: 'opacity 0.15s',
    }
};

// (ملحوظة: الستايلات دي كانت متكررة تحت، شيلتها من هنا)
// const chatStyles = { ... };

const loadingScreenStyles = {
    container: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', fontSize: '22px', color: '#6c5ce7', backgroundColor: '#f4f3fb', fontFamily: 'IBM Plex Sans Arabic, sans-serif', },
    error: { display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', fontSize: '20px', color: '#ff5a6e', textAlign: 'center', padding: '20px', backgroundColor: '#f4f3fb', fontFamily: 'IBM Plex Sans Arabic, sans-serif', }
};

export default ClientDashboard;
